<template lang="pug">
.bq-agent(ref="agentRoot")
    .bq-meta
        .bq-section-title
            .bq-title-row
                .bq-title-left
                    .bq-ai-select.bq-ai-select--platform(
                        v-if="!initialGateLoading"
                        @click.stop=""
                    )
                        button.bq-ai-select-btn(
                            type="button"
                            :disabled="sending || typing || uploadingAttachments || initialGateLoading || clearingHistory || savingPlatform"
                            :class="{ open: platformMenuOpen }"
                            @click.prevent="togglePlatformMenu"
                        )
                            span.bq-ai-select-value
                                img.bq-ai-logo(v-if="platformLogo" :src="platformLogo" :alt="platformLabel")
                                span {{ agentTitle }}
                            span.bq-ai-caret ▾
                        select.bq-ai-select-native(
                            v-if="isMobile"
                            :value="selectedAiPlatform"
                            :disabled="savingPlatform"
                            aria-label="AI Platform"
                            @change="onPlatformSelectFromNative"
                        )
                            option(v-for="p in platformOptions" :key="p.id" :value="p.id") {{ p.label }}
                        .bq-ai-options.bq-platform-options(v-if="!isMobile && platformMenuOpen")
                            button.bq-ai-option(
                                v-for="p in platformOptions"
                                :key="p.id"
                                type="button"
                                :class="{ active: selectedAiPlatform === p.id }"
                                @click="onPlatformSelect(p.id)"
                                :data-track="`agent_platform_${p.id}`"
                            )
                                img.bq-ai-logo(v-if="p.logo" :src="p.logo" :alt="p.label")
                                span {{ p.label }}
                    .bq-ai-select.bq-ai-select--inline(
                        v-if="!initialGateLoading"
                        @click.stop=""
                    )
                        button.bq-ai-select-btn(
                            type="button"
                            :disabled="!chatEnabled || fetchingModels || sending || typing || uploadingAttachments || initialGateLoading || clearingHistory || savingModel"
                            :class="{ open: modelMenuOpen }"
                            @click.prevent="toggleModelMenu"
                        )
                            span.bq-ai-select-value
                                span(v-if="!chatEnabled") No Model
                                span(v-else-if="fetchingModels || savingModel") Loading...
                                span(v-else-if="!availableModels.length") No models
                                span(v-else translate="no") {{ effectiveModelLabel }}
                            span.bq-ai-caret ▾
                        select.bq-ai-select-native(
                            v-if="isMobile && availableModels.length"
                            :value="effectiveModel"
                            :disabled="fetchingModels || savingModel"
                            aria-label="AI Model"
                            @change="onModelSelectFromNative"
                        )
                            option(v-for="m in availableModels" :key="m.id" :value="m.id" translate="no") {{ m.display_name || m.id }}
                        .bq-ai-options.bq-model-options(v-if="!isMobile && modelMenuOpen")
                            button.bq-ai-option(
                                v-for="m in availableModels"
                                :key="m.id"
                                type="button"
                                :class="{ active: effectiveModel === m.id }"
                                @click="onModelSelect(m.id)"
                                :data-track="`agent_model_${m.id}`"
                            )
                                span(translate="no") {{ m.display_name || m.id }}
                .bq-title-right
                    span.material-symbols-outlined.bq-icon.bq-icon--danger(
                        translate="no"
                        v-if="chatEnabled && messages.length > 0"
                        :class="{ disabled: sending || typing || uploadingAttachments || initialGateLoading || clearingHistory }"
                        title="Clear chat history"
                        aria-label="Clear chat history"
                        role="button"
                        tabindex="0"
                        @click="onClearHistoryClick"
                        @keydown.enter.prevent="onClearHistoryClick"
                        @keydown.space.prevent="onClearHistoryClick"
                        data-track="agent_clearHistory"
                    ) delete_history

        .bq-chat(
            ref="chatBox"
            :class="{ 'drag-here': dragHere }"
            @dragenter.stop.prevent="onChatDragEnter"
            @dragover.stop.prevent="onChatDragOver"
            @dragleave.stop.prevent="onChatDragLeave"
            @drop.stop.prevent="onChatDrop"
        )
            .bq-drop-overlay(v-if="dragHere")
                .bq-drop-overlay-inner
                    span.material-symbols-outlined(translate="no" aria-hidden="true" style="font-size:32px;") upload
                    span Drop files or folders to attach
            //- Shown from the instant of the drop until the files are listed.
            //- A dropped folder takes a directory walk to become a file list, and
            //- until this was here the chat looked like it had ignored the drop.
            .bq-drop-preparing(v-if="preparingDrop" role="status" aria-live="polite")
                BunnyLoader(:overlay="false" label="Preparing files...")
            .bq-apikey-overlay(v-if="!initialGateLoading && !hasAiPlatform")
                .bq-apikey-overlay-inner
                    p.bq-apikey-help Select an AI platform to start chatting.
                    .bq-platform-overlay-options
                        button.btn.bq-platform-overlay-btn(
                            v-for="p in platformOptions"
                            :key="p.id"
                            type="button"
                            :disabled="savingPlatform"
                            @click="onPlatformSelect(p.id)"
                            :data-track="`agent_platformOverlay_${p.id}`"
                        )
                            img.bq-apikey-logo(v-if="p.logo" :src="p.logo" :alt="p.label" :class="`bq-apikey-logo--${p.id}`")
                            span {{ p.label }}
            .bq-apikey-overlay(v-if="!initialGateLoading && hasAiPlatform && (!hasAiApiKey || apiKeyInvalid)")
                .bq-apikey-overlay-inner
                    img.bq-apikey-logo(v-if="platformLogo" :src="platformLogo" :alt="platformLabel")
                    p.bq-apikey-error(v-if="hasAiApiKey && apiKeyInvalid") Your API key is invalid
                    button.btn(type="button" :disabled="savingApiKey || loadingApiKey" :class="{ 'is-loading-key': loadingApiKey }" @click="openApiKeyModal" data-track="agent_openApiKey")
                        span(v-if="loadingApiKey") {{ apiKeyLoadingDots }}
                        span(v-else) Enter API Key
                    p.bq-apikey-help
                        span.wordset Get your API key:&nbsp;
                        a.bq-link(:href="apiKeyDocsUrl" target="_blank" rel="noopener noreferrer" translate="no") {{ apiKeyDocsUrl }}
            .bq-messages(ref="messagesBox" @scroll.passive="onHistoryScroll" @wheel.passive="onMessagesWheel" @touchstart.passive="onMessagesTouchStart" @touchmove.passive="onMessagesTouchMove")
                .bq-gate-loading(v-if="initialGateLoading")
                    BunnyLoader(
                        :overlay="false"
                        :label="savingPlatform ? 'Switching...' : chatEnabled ? 'Fetching history...' : 'Loading...'"
                    )

                template(v-else)
                    //- The outer element takes NO height in flow (see the CSS): it
                    //- is a zero-height sticky hook, and the inner strip overflows
                    //- it. Mounting and unmounting this must not move the list.
                    .bq-history-loading(v-if="showHistoryLoadingIndicator")
                        span.bq-history-loading-inner {{ historyLoadingLabel }}

                    //- Deferred indexing-stub batch (first-paint split): a thin
                    //- hint where indexing rows will merge once the stubs land.
                    //- Only for a GENUINE first paint — the silent head refresh a
                    //- tab return runs never sets bgHistoryLoading (see the
                    //- bgPending handler), so this cannot flash on every return.
                    .bq-history-loading(v-else-if="bgHistoryLoading && !initialGateLoading")
                        span.bq-history-loading-inner Loading indexing history{{ ".".repeat((historyLoadDotTick % 3) + 1) }}

                    //- ALWAYS, and always first. It is the opening line of the
                    //- conversation, not a placeholder for an empty one: it sits
                    //- above every message the history ever loads, so a long chat
                    //- simply has it far up its scrollback where it belongs.
                    //- It used to be gated on the chat being genuinely empty,
                    //- which meant the one message that explains what this box is
                    //- for vanished the moment someone used it.
                    //-
                    //- It leads with the UPLOAD, not with "ask me anything": a
                    //- project with no indexed files has nothing to answer from,
                    //- so inviting questions first reads as a promise the box
                    //- cannot keep. When uploads are blocked (uploadsFrozenForUser)
                    //- that instruction is a dead end, so those sessions get the
                    //- ask-first line instead. The sentence itself comes from the
                    //- engine (buildChatGreeting), which is also what the model is
                    //- told it opened with: this bubble never enters the history.
                    .bq-message.is-assistant.bq-empty-greeting
                        .bq-bubble
                            | {{ greetingParts.lead }}
                            template(v-if="greetingParts.name") &nbsp;
                                strong(translate="no") {{ greetingParts.name }}
                            | {{ greetingParts.tail }}
                    //- Rows, not raw messages: a file's many background-indexing turns
                    //- collapse into one status row (chatDisplayRows / the engine's
                    //- buildChatDisplayList). Expanding one emits its own turns as
                    //- ordinary message rows right after it, so the bubble markup below
                    //- stays the single source for every message.
                    template(v-for="row in chatDisplayRows" :key="row.key")
                        //- The row's colour, one class per state: yellow working,
                        //- green indexed, red failed, grey not-known-yet (resolving)
                        //- and grey for anything else, cancelled included. is-active
                        //- is the WORKING look, so it must never win over
                        //- is-resolving, where "not confirmed over" is exactly what
                        //- has not been established. is-indexed is the mirror of the
                        //- DONE glyph in indexGroupIcon, and reachable only from
                        //- status 'done' so a failure or a stop cannot come out green.
                        //- The widget writes these same four predicates verbatim.
                        .bq-index-group(
                            v-if="row.kind === 'group'"
                            :data-row-key="row.key"
                            :data-row-pos="indexGroupAnchorId(row.group)"
                            :class="{ 'is-resolving': row.group.resolving, 'is-active': !row.group.resolving && !row.group.finished && row.group.status !== 'cancelled', 'is-indexed': !row.group.resolving && row.group.finished && row.group.status === 'done', 'is-error': row.group.finished && row.group.status === 'error', 'is-open': expandedIndexGroups.has(row.group.key) }"
                        )
                            //- A div, not a button: the file name inside is a real anchor
                            //- (click = mint a fresh temporary URL), and an anchor nested
                            //- in a button is invalid and swallows its own activation.
                            .bq-index-head(
                                role="button"
                                tabindex="0"
                                :aria-expanded="expandedIndexGroups.has(row.group.key) ? 'true' : 'false'"
                                :title="expandedIndexGroups.has(row.group.key) ? 'Hide indexing steps' : 'Show indexing steps'"
                                @click="toggleIndexGroup(row.group.key)"
                                @keydown.enter.prevent="toggleIndexGroup(row.group.key)"
                                @keydown.space.prevent="toggleIndexGroup(row.group.key)"
                            )
                                span.bq-index-icon(v-html="indexGroupIcon(row.group)" translate="no")
                                span.bq-index-label
                                    template(v-for="(part, pi) in parseMsgParts(indexGroupLabel(row.group), { imagePreviews: false })" :key="pi")
                                        //- No .stop here: the plain-text part of the label is
                                        //- the row's main click target. Only the file anchor
                                        //- below swallows the click (it is a download).
                                        span.bq-md(
                                            v-if="part.type === 'html'"
                                            v-html="part.html"
                                            @click="onBubbleLinkClick"
                                        )
                                        //- No href when the file is unavailable: an
                                        //- anchor without one cannot navigate, which
                                        //- is what disables the click.
                                        a.bq-link-button(
                                            v-else-if="part.type === 'link'"
                                            :href="isLinkPartUnavailable(part) ? undefined : part.href"
                                            :title="(part.fullLabel || part.label) + linkStateSuffix(part)"
                                            :target="isLinkPartUnavailable(part) ? undefined : '_blank'"
                                            rel="noopener noreferrer"
                                            :aria-disabled="isLinkPartUnavailable(part) ? 'true' : undefined"
                                            :class="{ 'is-expired': part.expired, 'is-refreshing': isLinkRefreshing(part), 'is-unavailable': isLinkPartUnavailable(part) }"
                                            @click.stop="onMessageLinkClick($event, part)"
                                        ) #[span(translate="no") {{ part.label }}]{{ linkStateSuffix(part) }}
                                        span(v-else translate="no") {{ part.text }}
                                span.bq-index-count(v-if="indexGroupCount(row.group)") {{ indexGroupCount(row.group) }}
                                //- Spinning arrows while this row's history is being
                                //- paged in — separate from the status icon, so a
                                //- green (done) row spins too while detail loads.
                                span.bq-index-fetch(
                                    v-if="indexGroupFetching.has(row.group.key)"
                                    v-html="INDEX_ICON_ACTIVE"
                                    title="Fetching this file's indexing history"
                                    translate="no"
                                )
                                //- Stop indexing. Lives in the head so it is reachable
                                //- without expanding the row; .stop keeps it from
                                //- toggling the row it sits in.
                                //- Offered while there is work left to stop, not
                                //- while a pass happens to be live in THIS client:
                                //- a worker-driven file has none between passes and
                                //- the button used to blink out for the whole gap.
                                //- See indexGroupStoppable.
                                button.bq-index-cancel(
                                    v-if="indexGroupStoppable(row.group) || row.group.cancelling"
                                    type="button"
                                    :class="{ 'is-disabled': row.group.cancelling }"
                                    :disabled="row.group.cancelling"
                                    :aria-label="`Stop indexing ${row.group.name}`"
                                    :title="row.group.cancelling ? 'Stopping...' : 'Stop indexing this file'"
                                    @click.stop="cancelIndexGroup(row.group)"
                                    @keydown.stop="() => {}"
                                ) {{ row.group.cancelling ? 'Stopping...' : 'Stop' }}
                                span.bq-index-chevron ▶
                            .bq-index-note.is-error(v-if="row.group.cancelError") Could not stop this file: {{ row.group.cancelError }}
                            //- "Scroll up to load them" is an instruction, so it must
                            //- not be given while the loading it asks for is already
                            //- in flight: following it does nothing the row is not
                            //- doing, and reads as the row not having noticed.
                            //- While the expand-triggered fetch runs, the note IS the
                            //- progress: dot-trail loader, not an instruction.
                            .bq-index-note(v-if="expandedIndexGroups.has(row.group.key) && indexGroupFetching.has(row.group.key)")
                                span Loading this file's indexing history
                                span.bq-loader
                            .bq-index-note(v-else-if="expandedIndexGroups.has(row.group.key) && row.group.mayHaveOlder")
                                template(v-if="row.group.resolvingReason === 'history' || (row.group.stub && bgHistoryLoading)")
                                    | Earlier passes of this file are further back in the conversation. Loading them now.
                                template(v-else)
                                    | Earlier passes of this file are further back in the conversation. Scroll up to load them.
                            //- An open row must NEVER be a void: when the whole
                            //- history is walked and the passes still are not here
                            //- (cleared conversation, another chat or platform's
                            //- run), say so instead of showing nothing.
                            .bq-index-note(v-else-if="expandedIndexGroups.has(row.group.key) && !row.group.visibleMembers.length")
                                | This file's indexing steps aren't in this chat's history. They may belong to another chat or platform, or the conversation was cleared.
                        //- Still working, or not confirmed otherwise. No data-row-key:
                        //- the scroll anchor picks its target by that attribute, and a
                        //- keyed element with no data-row-pos would be preferred over
                        //- the real rows. aria-hidden because the state is already
                        //- announced on the row head.
                        //- bq-index-pass too, exactly as the widget's tail carries it:
                        //- the run-closing gap rule keys off that class, so without it
                        //- the gap opens ABOVE the loader (breaking the rail) instead
                        //- of after the run.
                        .bq-index-pass.bq-index-tail(v-else-if="row.kind === 'tail'" aria-hidden="true")
                            span.bq-loader
                        .bq-message(
                            v-else
                            :data-msg-index="row.index"
                            :data-row-key="row.key"
                            :class="{ 'is-user': row.msg.role === 'user', 'is-assistant': row.msg.role === 'assistant', 'is-error': !!row.msg.isError, 'is-cancelled': !!row.msg.isCancelled, 'is-pending-older': !!(row.msg.isPendingQueued || row.msg.isPendingOlder), 'is-sending-to-server': !!row.msg._dimSending || !!row.msg._cancelling, 'bq-index-pass': row.inGroup }"
                        )
                            //- Assistant "Thinking" placeholder for in-process (running) requests.
                            //- THE LIVE-STREAM RENDER CONTRACT, mirrored verbatim in the
                            //- widget's buildMessageEl: a streaming bubble is STILL a
                            //- pending bubble (the engine keeps isPending set because every
                            //- queue mechanism finds the turn by it), so the only thing
                            //- streaming changes is which half of this branch it takes. By
                            //- the time _streaming is set the bubble's `content` already
                            //- holds a safe prefix of the answer - liveSafePrefix never
                            //- leaves it ending inside a half-arrived link, fence or url -
                            //- so it renders as ordinary markdown instead of the dot-trail,
                            //- and everything else about the bubble is unchanged.
                            //-
                            //- _streamPending is the OTHER direction, and it is not a
                            //- pending turn at all: the row is terminal, but its answer was
                            //- streamed and nobody ever finalized it, so the text is in the
                            //- chunk store and can be read back
                            //- (ChatSession.recoverStreamedAnswer). Empty content there
                            //- means UNKNOWN, not "answered nothing". Once it has content
                            //- (the merge adopted a local answer onto it) it renders as
                            //- ordinary text and neither branch is taken.
                            //-
                            //- WHICH of the two it takes is streamRecoveryPhase's call, and
                            //- the whole point of asking is that "the answer is elsewhere"
                            //- and "somebody is on their way to get it" are different facts.
                            //- This used to draw the live turn's loader for both, so the
                            //- turns the per-load cap left behind - the third and later on a
                            //- page - and every turn whose read had failed spun for the rest
                            //- of the session with nothing driving them and no way for the
                            //- reader to resolve them. A loader is a promise that something
                            //- is coming, so it is now drawn only where something is; the
                            //- other two phases get the affordance below. Mirrored in the
                            //- widget's buildMessageEl.
                            .bq-bubble(v-if="(row.msg.isPending && !row.msg._streaming) || streamRecoveryPhase(row.msg) === 'active'")
                                span.bq-loader
                            //- 'idle' (nobody has been sent for it) and 'failed' (somebody
                            //- went and could not read it) differ ONLY in wording,
                            //- deliberately: both leave the answer where it is, both are
                            //- fixed by asking again, and the words come from the engine so
                            //- the two clients cannot describe the same state differently.
                            //- Nothing here claims the answer is lost - the row is
                            //- unfinalized, which is exactly why the chunks are still there.
                            .bq-bubble.is-stream-recover(
                                v-else-if="streamRecoveryPhase(row.msg)"
                                :class="{ 'is-stream-failed': streamRecoveryPhase(row.msg) === 'failed' }"
                            )
                                span.bq-stream-recover-note {{ streamRecoveryLabels(streamRecoveryPhase(row.msg)).note }}
                                //- The engine flips the bubble to 'active' synchronously, so
                                //- the click's own feedback is the loader replacing this
                                //- button. No local disabled flag: a second click while the
                                //- read is in flight is already refused by the engine, and a
                                //- flag here would be one more piece of state to leave stuck.
                                button.bq-stream-recover-btn(
                                    type="button"
                                    @click.stop="recoverStreamedAnswer(row.msg._serverItemId)"
                                ) {{ streamRecoveryLabels(streamRecoveryPhase(row.msg)).action }}
                            .bq-bubble(v-else)
                                button.bq-cancel-queue-btn(
                                    v-if="row.msg.role === 'user' && row.msg.isPendingQueued"
                                    type="button"
                                    :class="{ 'is-disabled': !row.msg._serverItemId || row.msg.isSendingToServer || row.msg._cancelling }"
                                    @click.stop="cancelQueuedMessage(row.msg, row.index)"
                                    aria-label="Cancel queued message"
                                    title="Cancel queued message"
                                ) ×
                                template(v-for="(part, pi) in parseMsgParts(row.msg.content)" :key="pi")
                                    div.bq-md(
                                        v-if="part.type === 'html'"
                                        translate="no"
                                        v-html="part.html"
                                        @click="onBubbleLinkClick"
                                    )
                                    a.bq-file-download(
                                        v-else-if="part.type === 'file'" translate="no"
                                        :href="part.href"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    )
                                        //- The glyph is its own element so chat.css
                                        //- can make it an atomic inline: the hover
                                        //- underline stops before it. Same markup the
                                        //- widget's fileToAnchorHtml emits.
                                        span.bq-link-glyph(translate="no") &#x2197;
                                        | {{ part.filename }}
                                    //- Unavailable: ✕ instead of ↗, and no href /
                                    //- target / download, so the chip cannot
                                    //- navigate. Mirrors the engine's
                                    //- renderInlineLinkHtml, which emits the same
                                    //- chip on the v-html path.
                                    a.bq-link-button(
                                        v-else-if="part.type === 'link'"
                                        :href="isLinkPartUnavailable(part) ? undefined : part.href"
                                        :title="(part.fullLabel || part.label) + linkStateSuffix(part)"
                                        :target="isLinkPartUnavailable(part) ? undefined : '_blank'"
                                        rel="noopener noreferrer"
                                        :download="isLinkPartUnavailable(part) ? undefined : (part.fullLabel || part.label)"
                                        :aria-disabled="isLinkPartUnavailable(part) ? 'true' : undefined"
                                        :class="{ 'is-expired': part.expired, 'is-refreshing': isLinkRefreshing(part), 'is-unavailable': isLinkPartUnavailable(part) }"
                                        @click="onMessageLinkClick($event, part)"
                                    )
                                        //- Glyph as its own atomic-inline element (see
                                        //- .bq-link-glyph in chat.css); label and the
                                        //- state suffix stay adjacent, no whitespace
                                        //- between them, exactly as before.
                                        span.bq-link-glyph(translate="no") {{ linkGlyph(part) }}
                                        span(translate="no") {{ part.label }}
                                        | {{ linkStateSuffix(part) }}
                                    span(v-else) {{ part.text }}
                                //- Order matters: a staged turn carries isPendingQueued
                                //- the whole way through, so both attachment phases
                                //- have to be tested before it.
                                span.bq-pending-note(v-if="row.msg.isUploadingAttachments") (Uploading files...)
                                span.bq-pending-note(v-else-if="row.msg.isAwaitingIndexing") (Indexing files...)
                                span.bq-pending-note(v-else-if="row.msg.isPendingQueued") (In queue)
                                span.bq-cancel-error(v-if="row.msg.isCancelled") (cancelled)
                                span.bq-cancel-error(v-if="row.msg._cancelError") {{ row.msg._cancelError }}
                                time.bq-msg-time(v-if="bubbleTime(row.msg)") {{ bubbleTime(row.msg) }}
                    //- Local "you are typing" indicator: while the composer holds
                    //- text, the list ends with a user-side bubble running the same
                    //- dot-trail as the assistant's Thinking placeholder. Cosmetic
                    //- only; it never becomes a message and never enters history.
                    .bq-message.is-user.bq-user-drafting(v-if="isDrafting" aria-hidden="true")
                        .bq-bubble
                            span.bq-loader

            form.bq-input-row(@submit.prevent="sendMessage")
                .bq-attachment-warning(
                    v-if="attachmentCapNotice"
                    role="alert"
                )
                    span.material-symbols-outlined.bq-attachment-warning-icon(translate="no") priority_high
                    span
                        | You can attach up to {{ MAX_ATTACHMENT_FILE_COUNT }} files per message. {{ attachmentCapNotice }} file{{ attachmentCapNotice === 1 ? ' was' : 's were' }} not added. Use the&nbsp;
                        router-link.bq-link(:to="`/my-projects/${currentService.id}/dbfile`") [Files]
                        | &nbsp;page for bulk uploads.
                .bq-attachment-warning(
                    v-if="attachmentCountWarning"
                    role="alert"
                )
                    span.material-symbols-outlined.bq-attachment-warning-icon(translate="no") priority_high
                    span
                        | {{ attachmentCountWarning }}&nbsp;
                        br
                        | Go to&nbsp;
                        router-link.bq-link(:to="`/my-projects/${currentService.id}/dbfile`") [Files]
                        | &nbsp;page to upload more files.
                .bq-attachment-warning(
                    v-if="attachments.length && attachmentsBudgetWarning"
                    role="alert"
                )
                    span.material-symbols-outlined.bq-attachment-warning-icon(translate="no") priority_high
                    span
                        | Attachments are ~{{ formatTokenCount(attachmentsTokenEstimate) }} tokens, which may exceed the ~{{ formatTokenCount(currentInputTokenBudget) }}-token per-request limit.
                        br
                        | Go to&nbsp;
                        router-link.bq-link(:to="`/my-projects/${currentService.id}/dbfile`") [Files]
                        | &nbsp;page to upload more files.
                .bq-attachments(v-if="attachments.length" ref="attachmentsBox")
                    .bq-attachment(
                        v-for="a in visibleAttachments"
                        :key="a.id"
                        :title="attachmentTooltip(a)"
                        :class="{ 'is-uploading': a.status === 'uploading', 'is-preparing': a.status === 'uploading' && a.progress == null, 'is-finalizing': a.status === 'uploading' && (a.progress ?? 0) >= 100, 'is-done': a.status === 'done', 'is-error': a.status === 'error', 'is-index-error': a.status === 'indexError', 'is-clickable': a.status === 'done' && a.kind === 'file' && !!a.uploadedUrl }"
                        :style="a.status === 'uploading' && a.progress != null ? { '--att-progress': a.progress + '%' } : {}"
                        @click="onAttachmentClick(a)"
                    )
                        span.bq-attachment-arrow(v-if="a.status === 'done' && a.kind === 'file'" aria-hidden="true") &#8599;
                        span.material-symbols-outlined.bq-attachment-icon(v-else translate="no" aria-hidden="true" :class="{ 'is-filled': a.kind === 'folder' }") {{ a.kind === 'folder' ? 'folder' : 'description' }}
                        span.bq-attachment-name(translate="no") {{ a.name }}
                        span.bq-attachment-meta(v-if="a.status === 'error'") &nbsp;(Failed)
                        span.bq-attachment-meta(v-else-if="a.status === 'indexError'") &nbsp;(Error)
                        span.bq-attachment-meta(v-else-if="a.kind === 'folder'") &nbsp;({{ a.files.length }})
                        //- No byte-progress event yet (get-signed-url in flight): there
                        //- is no percentage to show, so say so rather than print "0%".
                        span.bq-attachment-meta(v-else-if="a.status === 'uploading' && a.progress == null") &nbsp;Preparing
                        //- Bytes done but the post-upload work (signed url, src::
                        //- record, indexing dispatch) hasn't settled: a chip stuck
                        //- at "100%" reads as hung, so say what's happening.
                        span.bq-attachment-meta(v-else-if="a.status === 'uploading' && (a.progress ?? 0) >= 100") &nbsp;Finalizing
                        span.bq-attachment-meta(v-else-if="a.status === 'uploading' && a.progress != null") &nbsp;{{ a.progress }}%
                        //- Removable right up until its bytes start moving — including
                        //- while a batch it belongs to is uploading, which is how a
                        //- file queued behind a big one gets pulled back out.
                        button.bq-attachment-remove(
                            v-if="a.status !== 'done' && a.status !== 'uploading'"
                            type="button"
                            aria-label="Remove attachment"
                            title="Remove"
                            @click.stop="removeAttachment(a.id)"
                            data-track="agent_attachment_remove"
                        ) &times;
                    .bq-attachment.bq-attachment-more(v-if="hiddenAttachmentsCount > 0" :title="hiddenAttachmentsTooltip")
                        span.bq-attachment-name ...({{ hiddenAttachmentsCount }}) more
                        button.bq-attachment-remove(
                            type="button"
                            aria-label="Remove hidden attachments"
                            :title="`Remove these ${hiddenAttachmentsCount}`"
                            @click.stop="removeHiddenAttachments"
                            data-track="agent_attachment_remove_hidden"
                        ) &times;
                    button.bq-attachment-remove-all(
                        v-if="hiddenAttachmentsCount === 0 && composerAttachments.length >= 2"
                        type="button"
                        title="Remove all attachments"
                        @click.stop="clearAttachments"
                        data-track="agent_attachment_remove_all"
                    ) Remove all &times;
                .bq-input-wrap
                    button.bq-attach-btn(
                        v-if="!uploadsFrozenForUser"
                        type="button"
                        aria-label="Attach files"
                        title="Attach files"
                        :disabled="!chatEnabled || initialGateLoading"
                        @click="openAttachPicker"
                        data-track="agent_attach"
                    )
                        svg(width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true")
                            path(d="M21.44 11.05l-9.19 9.19a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 1 1 5.66 5.66l-9.2 9.19a2 2 0 1 1-2.83-2.83l8.49-8.48")
                    input.bq-attach-input(
                        v-if="!uploadsFrozenForUser"
                        ref="attachInput"
                        type="file"
                        multiple
                        @change="onAttachFromInput"
                    )
                    textarea.bq-input(
                        ref="inputBox"
                        :class="{ 'bq-input--noattach': uploadsFrozenForUser }"
                        v-model="prompt"
                        rows="1"
                        :placeholder="'Ask anything about: ' + currentService?.service?.name"
                        :disabled="!chatEnabled || initialGateLoading"
                        @keydown.enter.exact="handlePromptEnter"
                        @compositionstart="isPromptComposing = true"
                        @compositionend="handlePromptCompositionEnd"
                        @input="autoGrowInput"
                    )
                button.btn(type="submit" :disabled="!canSend" data-track="agent_send") Send

    //- modal :: clear chat history
    Modal(:open="showClearHistoryModal", @close="() => { if (!clearingHistory) showClearHistoryModal = false }")
        .bq-modal-delete
            .bq-modal-delete-header
                span.material-symbols-outlined.bq-modal-delete-icon(translate="no") delete
                span Clear chat history
            .bq-modal-delete-body
                p Hide all previous messages from this {{ platformLabel }} chat?
                p.bq-modal-delete-warn New messages will appear normally. Existing history will be hidden from view and from the AI's context, but the records remain on the server.
            .bq-modal-delete-footer
                template(v-if="clearingHistory")
                    span.bq-loader Clearing
                template(v-else)
                    button.bq-modal-delete-btn.bq-modal-delete-btn--cancel(
                        @click="showClearHistoryModal = false"
                    ) Cancel
                    button.bq-modal-delete-btn.bq-modal-delete-btn--delete(
                        @click="confirmClearHistory"
                        data-track="agent_clearHistoryModal_clear"
                    ) Clear

    //- modal :: stop indexing a file (asked by the collapsed row's Stop button)
    Modal(:open="!!stopIndexTarget", @close="closeStopIndexModal")
        .bq-modal-delete
            .bq-modal-delete-header
                span.material-symbols-outlined.bq-modal-delete-icon(translate="no") block
                span Stop indexing
            .bq-modal-delete-body
                p Stop indexing “#[span(translate="no") {{ stopIndexTarget?.name }}]”?
                p.bq-modal-delete-warn Whatever has been indexed so far stays searchable, and the pass already running finishes on the server. The remaining passes are dropped, not paused, so the file stays partly indexed until you reindex it.
            .bq-modal-delete-footer
                button.bq-modal-delete-btn.bq-modal-delete-btn--cancel(
                    @click="closeStopIndexModal"
                ) Keep indexing
                button.bq-modal-delete-btn.bq-modal-delete-btn--delete(
                    @click="confirmStopIndexGroup"
                    data-track="agent_stopIndexModal_stop"
                ) Stop indexing

    //- modal :: file already exists — non-dismissible, user must choose
    Modal(:open="overwriteModal.open", @close="() => {}")
        .bq-modal-delete
            .bq-modal-delete-header
                span.material-symbols-outlined.bq-modal-delete-icon(translate="no") file_copy
                span File already exists
            .bq-modal-delete-body
                p A file named “#[span(translate="no") {{ overwriteModal.filename }}]” already exists.
                p.bq-modal-delete-warn Skip it, keep the existing file and just reindex it, or overwrite it completely?
                label.bq-overwrite-applyall
                    input(type="checkbox" v-model="overwriteModal.applyToAll")
                    span Apply to all remaining files
            .bq-modal-delete-footer
                button.bq-modal-delete-btn.bq-modal-delete-btn--cancel(@click="chooseOverwrite('skip')") Skip
                button.bq-modal-delete-btn.bq-modal-delete-btn--cancel(@click="chooseOverwrite('reindex')") Reindex
                button.bq-modal-delete-btn.bq-modal-delete-btn--delete(@click="chooseOverwrite('overwrite')") Overwrite

    //- Who may read a file once it is indexed. Shown per file only when the
    //- project has "Ask on every upload" on; otherwise the default applies.
    Modal(:open="accessModal.open", @close="() => {}")
        .bq-modal-delete
            .bq-modal-delete-header
                span.material-symbols-outlined.bq-modal-delete-icon(translate="no") lock
                span Who can read this file?
            .bq-modal-delete-body
                p Choose who can ask questions about “#[span(translate="no") {{ accessModal.filename }}]” once it is indexed.
                .bq-access-options
                    label.bq-access-option(v-for="o in uploadAccessOptions" :key="o.value")
                        input(type="radio" name="bq-chat-upload-access" :value="o.value" v-model="accessModal.choice")
                        span.bq-access-option-label {{ o.label }}
                        span.bq-access-option-hint {{ o.hint }}
                label.bq-overwrite-applyall
                    input(type="checkbox" v-model="accessModal.applyToAll")
                    span Apply to all remaining files
            .bq-modal-delete-footer
                button.bq-modal-delete-btn.bq-modal-delete-btn--delete(@click="chooseAccessGroup(accessModal.choice)") Upload

    //- modal :: upload error report — files that failed to upload/index, grouped by error
    Modal(:open="uploadErrorReport.length > 0", @close="uploadErrorReport = []")
        .bq-modal-delete
            .bq-modal-delete-header
                span.material-symbols-outlined.bq-modal-delete-icon(translate="no") error
                span {{ uploadErrorReportTitle }}
            .bq-modal-delete-body
                p.bq-modal-delete-warn These files were not added to your message. They stay in the attachment row so you can remove or retry them.
                .bq-upload-error-list
                    .bq-upload-error-group(v-for="(g, i) in uploadErrorReport" :key="i")
                        p.bq-upload-error-heading {{ g.code ? g.code + ' — ' + g.message : g.message }}
                        ul.bq-upload-error-files
                            li(v-for="(f, j) in g.files" :key="j" translate="no") {{ f }}
            .bq-modal-delete-footer
                button.bq-modal-delete-btn.bq-modal-delete-btn--cancel(@click="uploadErrorReport = []") Close

    //- modal :: enter AI api key
    Modal(:open="apiKeyModalOpen" @close="() => { if (!savingApiKey) apiKeyModalOpen = false }")
        .bq-modal-wrap
            .bq-modal-close(@click="apiKeyModalOpen = false" :class="{ disabled: savingApiKey }") &times;
            form.bq-modal-step(@submit.prevent="saveApiKey")
                h2 API Key
                p.bq-modal-desc Enter your {{ platformLabel }} API key.
                textarea.bq-modal-textarea(
                    ref="apiKeyInputEl"
                    :disabled="savingApiKey || null"
                    placeholder="sk-..."
                    :value="apiKeyInput"
                    rows="3"
                    @input="e => apiKeyInput = e.target.value"
                )
                button.btn(
                    type="submit"
                    :class="{ 'bq-btn--loading': savingApiKey }"
                    :disabled="savingApiKey || !apiKeyInput.trim()"
                    data-track="agent_apiKeyModal_save"
                )
                    span.bq-loader(v-if="savingApiKey")
                    template(v-else) Save
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, onUpdated, reactive, ref, toRef, watch, type Ref } from "vue";
import { SkapiError } from "skapi-js";
import { skapi } from "@/main";
import mime from "mime";
import { marked } from "marked";
import { aiApiKeyCache, aiApiKeyInflight, aiChatHistoryCache, aiModelCache, agentHistoryRefreshTrigger, agentHistoryRefreshLastSeen, dbFileRefreshTrigger, recordsRefreshTrigger, currentService, pendingAgentRequests, pullServices, serviceClientSecrets, saveAiApiKeyPrivately, loadAiApiKeyPrivately } from "@/views/project-list";
import { hostDomain, aiClientSecretGrp } from "@/code/service";
import { normalizeStoragePath } from "@/code/utils";
import { ensureMcpGrantFresh, user } from "@/code/user";
import { removeFullScreenLoader } from "@/components/jsComponents";
import BunnyLoader from "@/components/bunnyLoader.vue";
import Modal from "@/components/modal.vue";
import chatgptLogo from "@/assets/img/logo/ChatGPT-Logo.png";
import claudeLogo from "@/assets/img/logo/claude-ai-symbol.svg";
import {
    // Inline image previews: the chip/preview markup is shared with the widget so
    // the two clients cannot drift, and the url mint is cached outside the parse.
    renderInlineLinkHtml,
    IMAGE_PREVIEWS_PER_MESSAGE,
    hydrateImagePreviews,
    clearImagePreviewCache,
    DEFAULT_CLAUDE_MODEL,
    DEFAULT_OPENAI_MODEL,
    extractClaudeText,
    extractOpenAIText,
    getChatHistory,
    getSplitChatHistory,
    buildHistoryItemFullId,
    registerModelContextWindows,
    notifyAgentSaveAttachment,
    deleteFileIndexRecord,
    upsertIndexRunRecord,
    ensureFileIndexRecord,
    readFileIndexAccessGroup,
    composeUserMessage,
    groupAttachmentFailures,
    BG_INDEXING_QUEUE_SUFFIX,
    isBgIndexingQueue,
    POLL_INTERVAL,
    MAX_CONCURRENT_BG_POLLS,
    bgTaskQueue,
    agentViewMounted,
    chatSession,
    setChatHost,
    buildChatDisplayList,
    // Recorded then stripped from a bg response's text, in this fork exactly as the
    // engine's own mapper does it — see mapHistoryListToMessages below.
    INDEXING_COMPLETE_MARKER,
    EMPTY_INDEXING_REPLY,
    createHistoryFiller,
    HISTORY_FILL_SLACK_PX,
    isHttpUrlLike,
    repairUrlWhitespace,
    classifyInlineLink,
    normalizeTrailingInlineToken,
    formatChatTimestamp,
    formatDuration,
    // Per-format UTF-8 declaration for downloadable files, single-sourced in
    // engine/download_encoding.ts and mirrored in skapi-mcp.
    prepareDownloadText,
    extOf,
    EXT_CONTENT_TYPES,
    EXPIRED_LINK_REFRESH_EXPIRES_SECONDS,
    PREVIEW_URL_EXPIRES_SECONDS,
    PREVIEW_BROWSER_CACHE_SECONDS,
    LINK_REFRESH_WINDOW_MS,
    // Every key a file's chips can be marked unavailable under, so a clear has
    // the same reach as the mark (a failed CLICK marks the href key too).
    linkUnavailableKeysForPath,
    // Link/path primitives, single-sourced in engine/links.ts. The three that
    // need a projectId are aliased and wrapped below so call sites stay unchanged.
    createInlineLinkRegex,
    safeDecodeURIComponent,
    encodePathSegments,
    normalizeAttachmentPathCandidate,
    getExpiredAttachmentVisiblePath,
    buildDisplayExpiredAttachmentHref,
    truncateLabelForDisplay,
    // A file whose url could not be minted: shared keys + glyphs so this chip
    // reads the same here and in the widget.
    isLinkUnavailable,
    linkUnavailableKeyForPath,
    linkUnavailableKeyForHref,
    INLINE_LINK_GLYPH,
    INLINE_LINK_UNAVAILABLE_GLYPH,
    INLINE_LINK_UNAVAILABLE_SUFFIX,
    EXPIRED_ATTACHMENT_URL_HOST,
    EXPIRED_ATTACHMENT_URL_ORIGIN,
    extractRemotePathFromAttachmentHref as engineExtractRemotePathFromAttachmentHref,
    isServiceDbAttachmentHref as engineIsServiceDbAttachmentHref,
    sanitizeAttachmentLinksForHistory as engineSanitizeAttachmentLinksForHistory,
    type IndexingGroup,
    type IndexingFileRef,
    type ChatHost,
    type ClaudeMessage,
    type AttachmentFailureGroup,
    type PinnedDispatchContext,
    parseAiAgentValue as engineParseAiAgentValue,
    setProjectContextWindow,
    getInputTokenBudget,
    // Narrow detector for "the PROVIDER rejected this project's API key", as
    // opposed to isAuthExpiredError (our own session token) or any transport
    // failure. Only this may raise the "Your API key is invalid" overlay.
    isProviderApiKeyError,
    // Holding the reader's place while the list mutates underneath them. Shared
    // with the widget so the two cannot drift on the one behaviour a reader
    // notices most.
    createScrollAnchor,
    // The element a preview's own transitions actually resize (the anchor, not the
    // <img>). Shared so both this and the engine's layout hook measure one node.
    previewLayoutBox,
    // Which locally-pushed bubbles survive a first-page refetch. Shared for the
    // same reason: a drift between the two clients shows up as a duplicated turn.
    shouldRescueInFlightMessage,
    // "Can a streamed turn whose answer never reached its row be read back?" -
    // streaming on AND the chunk reader injected. The mapper fork below gates its
    // `_streamPending` mark on exactly this, never on liveStreaming alone: a host
    // with no reader would then get a permanently empty bubble where it used to get
    // none, which is strictly worse than the bug.
    streamRecoveryEnabled,
    // "Is anything actually fetching this turn's answer, and if not, what do we
    // offer the reader instead of a spinner that never resolves?" Shared with the
    // widget rather than decided here, because a client that answers it on its own
    // answers it differently, and the symptom is a bubble that spins forever.
    streamRecoveryPhase,
    streamRecoveryLabels,
    // The provider error nested one level inside a FAILED csr-poll status envelope.
    // A STREAMED failure cannot report early (the chunks that arrived have to ship
    // in the same response), so it comes back as { id, status: 'failed', ..., error }
    // and every predicate in this fork used to look at the top level - which is how
    // a wrong API key on a streamed turn rendered as "No text response received from
    // AI provider", the one message that tells the user nothing. One unwrap at the
    // head of each reader makes a streamed error and a buffered error take the same
    // path, exactly as the engine's own errors.ts does it.
    csrEnvelopeError,
    // The ENGINE's spelling of a chat's identity key, and now the ONLY one this file
    // uses: `projectId#platform#userId`. This fork used to stamp its own bubbles with
    // a shorter `projectId#platform`, so every ownership guard answered "no" for half
    // of a mixed list (see engineLoadKey in fetchHistoryPage for the three failures
    // that caused). Anything handed back INTO the engine is compared against
    // getHistoryCacheKey(), which is this.
    chatCacheKey,
} from "@/code/ai_agent";
import { buildChatSystemPrompt, buildChatGreeting } from "@/code/ai_prompts";
import { convertBytesToReadableStorage } from "@/code/service-spec";
import { useOverwritePrompt } from "@/views/service/useOverwritePrompt";
import { useAccessGroupPrompt } from "@/views/service/useAccessGroupPrompt";
import { UPLOAD_ACCESS_OPTIONS, projectUploadAccessGroup, projectAsksUploadAccess, primeProjectSettings, readyProjectSettings, projectChatGreeting, type UploadAccessGroup } from "@/code/access_group";
import { chatIndexVerdicts, sweepIndexMarkers, invalidateDoneMarkerSweep, noteRunRecordStatus, type IndexRunRecordInfo } from "@/views/service/file";
import { loadModelCatalogue, reportProviderError, reportProviderSuccess, apiKeyRejected, invalidateApiKeyVerdict } from "@/code/ai_models";

type ChatMessage = ClaudeMessage & {
    isError?: boolean;
    isPending?: boolean;
    // The single oldest pending request — the one currently being processed
    // by the upstream provider. Renders yellow with "Processing previous
    // request..." on the assistant placeholder and "(In process)" labels.
    isPendingInProcess?: boolean;
    // Pendings that are queued behind the in-process one. Renders yellow with
    // a "(In queue)" label below the user message bubble. No assistant
    // placeholder is emitted for queued items to keep the chat compact.
    isPendingQueued?: boolean;
    // PROTOCOL flag: true from the moment the user sends a queued message until
    // the server acknowledges receipt (CSR call returns). It is the token the
    // engine's ack matches on, so nothing may clear it early — it is NOT what
    // draws the dim (see _dimSending).
    isSendingToServer?: boolean;
    // PRESENTATIONAL flag: render this bubble dimmed because the turn has not been
    // handed over yet. Split from isSendingToServer so an ATTACHMENT turn can go
    // solid the instant its files finish indexing, while its own request is still
    // un-acked for a moment longer.
    _dimSending?: boolean;
    // Stable server-side identifier (PollingResult.id) for messages that
    // originated from getChatHistory. The id stays the same across the
    // pending→resolved lifecycle, so we can detect when a previously-pending
    // entry has just resolved and scroll its bubble into view (instead of
    // resetting the user's scroll position to the bottom on every poll).
    _serverItemId?: string;
    // True while a cancel request is in-flight. Dims the bubble like isSendingToServer.
    _cancelling?: boolean;
    // Non-empty when a cancel attempt returned removed:false; shown in red below the bubble.
    _cancelError?: string;
    // True after a successful cancel — bubble stays visible but styled as cancelled.
    isCancelled?: boolean;
    // Back-compat: kept so cached histories from older builds don't break
    // when re-hydrated. Treated as `isPendingInProcess` for rendering.
    isPendingOlder?: boolean;
    // True for messages that originated from the background-indexing queue
    // (notifyAgentSaveAttachment). These are shown in the chat for visibility
    // but must not block the user from sending new chat messages.
    isBackgroundTask?: boolean;
    // The file a background-indexing REQUEST bubble belongs to. Stamped on both
    // the live bubble and the one rebuilt from history so the display layer can
    // collapse a file's many passes into one row without reverse-parsing the
    // formatted label. See the engine's IndexingFileRef / buildChatDisplayList.
    _indexFile?: IndexingFileRef;
    // Set on a background-indexing RESPONSE whose raw answer carried the
    // INDEXING_COMPLETE marker, recorded before the marker is stripped for display.
    // Meaningful only for a client-driven chain; see the engine's ChatMessage.
    _indexComplete?: boolean;
    // True when the chat message was submitted on the bg queue (userId-bg)
    // so it runs server-side after any pending indexing tasks. Needed so
    // cancelQueuedMessage targets the correct Qpass queue.
    _useBgQueue?: boolean;
    // Mapped from a COMPACT listing stub (split history fetch): the content is
    // the server-extracted head, not the full reply. Expanding the row fetches
    // the real body per item and clears this.
    _compact?: boolean;
    // Local id of a turn STAGED at Send time while its attachments upload —
    // the bubble exists before the server request does. dispatchComposedMessage
    // swaps it for the real turn (see the engine's stageOutgoingMessage).
    _stageId?: string;
    // Staged-turn phase 1: its files are still uploading. "(Uploading files...)".
    isUploadingAttachments?: boolean;
    // Staged-turn phase 2: the files are up and the turn is waiting for the whole
    // background-indexing chain behind them. "(Indexing files...)", still dimmed.
    // Cleared with _dimSending when the queue drains, which is the moment the turn
    // genuinely becomes "(In queue)".
    isAwaitingIndexing?: boolean;
    // PROTOCOL + PRESENTATIONAL: this bubble is being painted from a LIVE stream.
    // It sits ALONGSIDE isPending, never instead of it (every queue mechanism in
    // the engine finds the turn by isPending), and all it adds is "content is
    // already worth rendering, so draw the text instead of the spinner". Cleared
    // at settle, BEFORE the authoritative answer replaces the live text; the
    // painted content survives that clear because it is what the typewriter
    // resumes from. Written by the engine only: see the engine's ChatMessage.
    _streaming?: boolean;
    // This turn's row is TERMINAL but carries no stored answer, because the answer
    // was streamed and nobody ever finalized it: the bytes are in the chunk store,
    // not on the row. The bubble's `content` is therefore UNKNOWN, not empty, and
    // that distinction is the whole of two fixes at once - an unknown answer never
    // overwrites a known one (so a refetch landing between a streamed row resolving
    // and its finalize storing the body no longer erases the answer off the screen),
    // and an unknown one left over is resolved by reading the chunks back (so a turn
    // that settled while no poll was attached is no longer gone forever). For the
    // template it is a rendering hint and nothing more: empty content plus this flag
    // means "being fetched", so draw the loader. See the engine's ChatMessage.
    _streamPending?: boolean;
    // IS ANYTHING ACTUALLY DRIVING THIS BUBBLE RIGHT NOW? The second half of
    // _streamPending: that flag says the answer is elsewhere, this one says whether
    // somebody is on their way to fetch it. 'active' (a chunk read is in flight or
    // queued) is the ONLY value that may draw a spinner; 'failed' and absent both
    // mean nothing is coming, so the reader is owed a way to ask. Written by the
    // engine only, never persisted - it describes this session's fetching, not the
    // turn. Read it through streamRecoveryPhase(msg), never directly. See the
    // engine's ChatMessage.
    _streamRecovery?: "active" | "failed";
    // Ephemeral client-side identity assigned just before typewriting begins.
    // Lets typewriteIntoIndex re-find the correct bubble by ID each tick,
    // surviving index shifts from concurrent insertions (e.g. bg Thinking
    // bubbles promoted mid-typewrite by promoteNextBgQueuedToRunning).
    _localId?: string;
    // Epoch ms shown as small text under the bubble: a user bubble's request time
    // (`created`), an assistant bubble's response time (`updated`). Absent while a
    // turn is pending. See the engine's ChatMessage._ts.
    _ts?: number;
    // Epoch ms of when the WORKER BEGAN executing this request, on an indexing
    // pass's reply bubble only. `_ts - _tsStart` is the duration shown in
    // brackets after the timestamp. It is the execution start, not the enqueue
    // time: a pass can sit in the queue for minutes and that wait is not the
    // call. Absent on every other bubble, and absent on a pass that began and
    // ended between two poll ticks (no running envelope was ever seen). See the
    // engine's ChatMessage._tsStart.
    _tsStart?: number;
    // History cache key (`projectId#platform`) this bubble was created under, so a
    // project switch can't misattribute an in-flight turn. Mirrors the engine's
    // ChatMessage._ownerKey; it was already read in half a dozen places here
    // without ever being declared.
    _ownerKey?: string;
};
type AiPlatform = "claude" | "openai" | "none";

const parseAiAgentValue = (value: string | null | undefined) => {
    const raw = (value || "").trim();
    const [platform, model] = raw.includes("#") ? raw.split("#") : [raw, ""];
    const normalizedPlatform: AiPlatform =
        platform === "claude" || platform === "openai" ? platform : "none";

    return {
        raw,
        platform: normalizedPlatform,
        model: model || "",
        hasPlatform: normalizedPlatform !== "none",
    };
};

const initialAiAgentState = parseAiAgentValue(currentService.value?.service?.ai_agent);

// Push the project's context-window override (third segment of `ai_agent`, set
// in project settings) into the engine, which reads it in getContextWindow via
// projectId. Clearing the setting drops the project back to
// DEFAULT_CONTEXT_WINDOW, clamped to the selected model's ceiling.
const applyProjectContextWindow = () => {
    const projectId = currentService.value?.service?.service;
    if (!projectId) return;
    const { contextWindow } = engineParseAiAgentValue(
        currentService.value?.service?.ai_agent
    );
    setProjectContextWindow(projectId, contextWindow);
};
applyProjectContextWindow();

const prompt = ref("");
const isPromptComposing = ref(false);
// True while the composer holds a draft: the message list shows a user-side
// "typing" bubble (dot-trail), mirroring the assistant's Thinking placeholder.
const isDrafting = computed(() => prompt.value.trim().length > 0);
// The indicator adds a row at the very bottom of the list; keep a bottom-pinned
// reader pinned when it pops in (and reclaim the space when it leaves).
watch(isDrafting, () => {
    void nextTick().then(() => scrollToBottomIfSticky(false));
});
// Chat state is owned by the shared engine ChatSession (chatSession.state is a
// Vue reactive() — see ai_agent.ts). toRef preserves the existing `.value`
// read/write interface every computed/template/handler already uses, so the
// engine's in-place mutations render and the view's writes flow to the session.
const sending = toRef(chatSession.state, "sending");
const typing = toRef(chatSession.state, "typing");

type AttachmentUploadStatus = "pending" | "uploading" | "done" | "error" | "indexError";

// _batchId: stamped when the chip is handed to a send. Batched chips keep
// rendering (progress, status) but leave the composer's accounting — count cap,
// token budget, canSend, Remove all — which from that moment describes the NEXT
// message. Unstamped chips are the composer's; see composerAttachments.
type AgentAttachment =
    | {
          id: string;
          kind: "file";
          name: string;
          file: File;
          status?: AttachmentUploadStatus;
          // 0..100 once bytes are moving; UNSET while preparing (see
          // uploadSingleAttachment) so the chip can show a barber pole instead.
          progress?: number;
          errorMessage?: string;
          errorCode?: string;
          errorDetail?: string;
          uploadedUrl?: string;
          _batchId?: string;
      }
    | {
          id: string;
          kind: "folder";
          name: string;
          files: { file: File; path: string }[];
          status?: AttachmentUploadStatus;
          // 0..100 aggregate once bytes are moving; UNSET while preparing.
          progress?: number;
          errorMessage?: string;
          errorCode?: string;
          errorDetail?: string;
          uploadedUrls?: { path: string; url: string }[];
          _batchId?: string;
      };

const { overwriteModal, resetOverwriteBatch, promptOverwrite, chooseOverwrite } = useOverwritePrompt("chat:" + (currentService.value?.id || ""));
const { accessModal, resetAccessGroupBatch, promptAccessGroup, chooseAccessGroup } = useAccessGroupPrompt("chat:" + (currentService.value?.id || ""));
const uploadAccessOptions = UPLOAD_ACCESS_OPTIONS;

/**
 * Who may read what is indexed out of this file: the project default, or a
 * per-file answer when the project asked to be prompted. Same resolver the db
 * files page uses, keyed to the chat surface so the two never share a modal or
 * an "apply to all" answer.
 *
 * AWAITS THE SETTINGS FIRST, and that await is the whole reason this is async.
 * The setting lives in a record now (see @/code/access_group), and the sync
 * accessors answer with the default until it has been fetched: reading them
 * straight would index the first file after a page load at 'authorized' on a
 * project set to "Only me", and would never open the question on a project set
 * to "Ask every time". Cheap in practice, because the mount primed it.
 *
 * TAKES THE SERVICE ID rather than reading currentService, because the caller
 * pins its project at Send time: an upload started here can finish minutes after
 * the user has wandered into another project, and the group written must be the
 * one that project chose.
 */
const resolveUploadAccessGroup = async (service: string, filename: string): Promise<UploadAccessGroup> => {
    await readyProjectSettings(service);
    if (!projectAsksUploadAccess(service)) {
        return projectUploadAccessGroup(service);
    }
    // Under 'ask' the project names no group, so the modal opens on the same
    // value an unset project would have used.
    return promptAccessGroup(filename, projectUploadAccessGroup(service));
};

// Start the settings fetch as soon as the chat has a project, and again whenever
// it changes: this component does NOT remount on a project switch (see
// AttachmentSendJob.pinnedService), so a mount-only prime would leave every
// project the user walked to reading from an empty cache. Fire and forget, and
// deduped per project by the store, so switching back and forth costs one fetch
// each. Nothing waits on it; the two places whose answer is written down
// (resolveUploadAccessGroup, sendMessage) await it themselves.
// The project's own opening line, or "" for the built-in sentence. Mirrored into
// a ref because the engine's settings store is a plain Map: it is shared with
// the widget and must not depend on Vue. The watcher below is its only writer.
//
// Declared BEFORE that watcher on purpose: the watch runs `immediate`, so its
// callback executes while this module is still evaluating, and a `const` below
// it would be in the temporal dead zone -- a ReferenceError on every chat open.
const customGreeting = ref("");

watch(
    () => currentService.value?.id || "",
    (id) => {
        // Cleared first: this ref outlives the project it was read for, and a
        // stale custom line is worse than the built-in one.
        customGreeting.value = "";
        if (!id) return;
        primeProjectSettings(id);
        // The greeting is the exception to "nothing waits on it": unlike the
        // upload group, it is on screen before anything is sent. readyProjectSettings
        // joins the fetch primed above rather than starting a second one.
        readyProjectSettings(id).then(() => {
            if ((currentService.value?.id || "") === id) customGreeting.value = projectChatGreeting(id);
        });
    },
    { immediate: true }
);

const attachments = toRef(chatSession.state, "attachments") as unknown as Ref<AgentAttachment[]>;

// Files that failed to upload or index during the latest send, pre-grouped by
// error (code + description) for a single report dialog shown after the send.
const uploadErrorReport = ref<AttachmentFailureGroup[]>([]);
const uploadErrorReportTitle = computed(() => {
    const n = uploadErrorReport.value.reduce((acc, g) => acc + g.files.length, 0);
    return n === 1 ? "1 file could not be added" : `${n} files could not be added`;
});

const dragHere = ref(false);
// Between the drop and the attachment chips existing. Drives the scrim that
// covers the chat, which is what tells the user the drop landed, and what stops
// them dropping the same folder a second time while the first is still walking.
const preparingDrop = ref(false);
let dragDepth = 0;
const uploadingAttachments = toRef(chatSession.state, "uploadingAttachments");

const MAX_CHATBOX_FILE_COUNT = 20;
// Hard ceiling on attachable files per chat message (folders count as their file
// count). Unlike MAX_CHATBOX_FILE_COUNT (an advisory, text-gated warning) this is
// enforced in appendAttachments so a 10k-file drop/select can't freeze the tab.
const MAX_ATTACHMENT_FILE_COUNT = 20;
// True while the "attach up to N per message" notice is shown (a mass add was
// truncated to the ceiling above). While shown, Send and the input are disabled
// until the user removes attachments (any removal clears it) or Remove all.
// How many files the most recent add left out because of the cap (0 = none).
// Purely informational; it does NOT block the composer.
const attachmentCapNotice = ref(0);

const countFilesInAttachments = (atts: AgentAttachment[]): number => {
    let n = 0;
    for (const a of atts) {
        n += a.kind === 'folder' ? a.files.length : 1;
    }
    return n;
};

// Chips that belong to the message being composed RIGHT NOW — everything not
// already handed to a send. Chips of an in-flight batch stay in the row (that
// is where their progress renders) but they belong to a message that is already
// on its way, so every composer-side rule reads this list instead of the raw
// one: the per-message file cap, the token budget, Send, and Remove all.
const composerAttachments = computed(() =>
    attachments.value.filter((a) => !a._batchId)
);

// #6: file count is unlimited with no chat message (files are indexed one-by-one);
// once there's a message, the cap applies (the message bundles all URLs into one
// prompt). The warning recomputes reactively as prompt/attachments change.
const attachmentCountWarning = computed(() => {
    if (!prompt.value.trim()) return "";
    const count = countFilesInAttachments(composerAttachments.value);
    if (count <= MAX_CHATBOX_FILE_COUNT) return "";
    return `You've attached ${count} files. Up to ${MAX_CHATBOX_FILE_COUNT} per message is recommended.`;
});

// True when a user-initiated chat request is still pending (not yet resolved).
// Background-indexing tasks (isBackgroundTask) are excluded so they don't
// block the chat input — they run in a separate parallel queue.
const hasPendingMessage = computed(() =>
    messages.value.some((m) => m.isPending && !m.isBackgroundTask)
);

const canSend = computed(() => {
    if (!chatEnabled.value || initialGateLoading.value) return false;
    // NOT gated on uploadingAttachments: a send whose files are still uploading
    // has already left the composer (its chips are batched, its bubble is
    // staged), so the user goes right on typing the next message.
    if (prompt.value.trim()) {
        // Sending a chat message bundles all the attachment URLs into one
        // prompt, so the file-count cap (#6) and token budget apply here.
        if (countFilesInAttachments(composerAttachments.value) > MAX_CHATBOX_FILE_COUNT) return false;
        if (attachmentsExceedBudget.value) return false;
        return true;
    }
    // Attachment-only send: files are indexed one-by-one (each its own request),
    // so the aggregate per-request budget doesn't apply.
    if (composerAttachments.value.length === 0) return false;
    // Enable while there's anything to upload OR retry — i.e. at least one chip
    // that isn't already "done". After a run completes, clearSuccessfulAttachments
    // leaves only failed chips (error/indexError) and hands them back to the
    // composer; keeping Send enabled lets the user click it to retry them
    // (the batch re-processes every non-"done" chip).
    return composerAttachments.value.some((a) => a.status !== "done");
});

// Chip overflow handling: when the attachments wrap row would exceed
// 30% of the chat container's height, hide the overflowing chips and
// surface a "...(n) more" pseudo-chip in their place.
const ATTACHMENTS_MAX_HEIGHT_RATIO = 0.3;
const chatBox = ref<HTMLElement | null>(null);
const attachmentsBox = ref<HTMLElement | null>(null);
// Number.MAX_SAFE_INTEGER means "no truncation" (show all).
const visibleAttachmentCount = ref(Number.MAX_SAFE_INTEGER);
// Never mount more than this many attachment chips as DOM nodes; the "...(n) more"
// pill absorbs the rest. Bounds the v-for and the overflow-shrink loop to O(cap).
const VISIBLE_CHIP_CAP = 30;
let attachmentsResizeObserver: ResizeObserver | null = null;
let attachmentsRecomputeFrame = 0;

const visibleAttachments = computed(() =>
    sortedAttachments.value.slice(0, Math.min(visibleAttachmentCount.value, VISIBLE_CHIP_CAP))
);

const hiddenAttachments = computed(() =>
    sortedAttachments.value.slice(visibleAttachments.value.length)
);

const hiddenAttachmentsCount = computed(() => hiddenAttachments.value.length);

const hiddenAttachmentsTooltip = computed(() => {
    const names = hiddenAttachments.value.slice(0, 50).map((a) => (a.kind === "folder" ? `${a.name}/` : a.name));
    if (hiddenAttachments.value.length > names.length) {
        names.push(`...and ${hiddenAttachments.value.length - names.length} more`);
    }
    return names.join("\n");
});

// Display order: active chips first, terminal chips last, so the "...(n) more"
// truncation tail falls on finished items instead of hiding what's actively
// happening. The order is:
//   uploading  →  queued (pending)  →  failed  →  completed
// A freshly added attachment has no status yet — it's queued to upload — so a
// missing status is treated as "pending". Within a group insertion order is
// preserved (newest-first only within the in-flight uploading group).
const ATTACHMENT_STATUS_PRIORITY: Record<string, number> = {
    uploading: 0,
    pending: 1,
    error: 2,
    indexError: 2,
    done: 3,
};
const attachmentStatusPriority = (status?: AttachmentUploadStatus): number =>
    ATTACHMENT_STATUS_PRIORITY[status ?? "pending"] ?? 99;

const sortedAttachments = computed(() => {
    const list = attachments.value.map((a, i) => ({ a, i }));
    list.sort((x, y) => {
        const px = attachmentStatusPriority(x.a.status);
        const py = attachmentStatusPriority(y.a.status);
        if (px !== py) return px - py;
        // Newest-first within the in-flight uploading group and the failed
        // group, so the chip that most recently started — or most recently
        // failed — sits at the front of its group. Queued and completed groups
        // keep insertion order.
        if (px === 0 || px === 2) return y.i - x.i;
        return x.i - y.i;
    });
    return list.map((e) => e.a);
});

const newAttachmentId = () => `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Stable identity for an attachment so repeat drops of the same file/folder
// do not stack duplicate chips. Files use name+size+lastModified (a stronger
// signal than name alone, which is the same heuristic browsers expose for
// duplicate detection on `<input type="file">`). Folders use name + file
// count + total size of their contents.
const attachmentKey = (a: AgentAttachment): string => {
    if (a.kind === "file") {
        return `f|${a.name}|${a.file.size}|${a.file.lastModified}`;
    }
    let total = 0;
    for (const entry of a.files) total += entry.file.size;
    return `d|${a.name}|${a.files.length}|${total}`;
};

const attachmentTooltip = (a: AgentAttachment) => {
    if (a.status === "error") return "File upload has failed";
    if (a.status === "indexError") return "File indexing failed";
    if (a.kind === "folder") {
        return `${a.name}/ — ${a.files.length} file${a.files.length === 1 ? "" : "s"}`;
    }
    return `${a.name} (${a.file.size} bytes)`;
};

// Per-attachment abort handles for in-flight uploads. Populated by
// uploadSingleAttachment via the progress callback's `abort` function.
const attachmentAborters = new Map<string, () => void>();
// Track ids the user explicitly cancelled so the upload loop can swallow the
// resulting abort error and continue with the next attachment instead of
// failing the whole batch.
const cancelledAttachmentIds = new Set<string>();

const removeAttachment = (id: string) => {
    const target = attachments.value.find((a) => a.id === id);
    if (target && target.status === "uploading") {
        cancelledAttachmentIds.add(id);
        const ab = attachmentAborters.get(id);
        if (ab) {
            try { ab(); } catch { /* ignore */ }
        }
    }
    attachmentAborters.delete(id);
    attachments.value = attachments.value.filter((a) => a.id !== id);
    // Removing attachments makes room again, so lift the cap notice (and the
    // Send/input disable it drives) rather than leaving the user stranded.
    attachmentCapNotice.value = 0;
};

// "Remove all" is a composer affordance: it drops what the user has staged for
// the NEXT message and leaves in-flight batches alone (those files belong to a
// message that is already sent — their own chip × cancels them individually).
const clearAttachments = () => {
    attachments.value = attachments.value.filter((a) => !!a._batchId);
    attachmentCapNotice.value = 0;
};

// Called when a batch finishes. Its successfully-handled chips go away; its
// failed ones (red upload-fail / yellow index-fail) are handed BACK to the
// composer — un-batched — so the next Send retries them. Chips belonging to
// other batches, and to the message being composed, are untouched.
const clearSuccessfulAttachments = (batchId: string) => {
    attachments.value = attachments.value.filter((a) => {
        if (a._batchId !== batchId) return true;
        if (a.status !== "error" && a.status !== "indexError") return false;
        delete a._batchId;
        return true;
    });
    attachmentCapNotice.value = 0;
};

// #10: the × on the "...(x) more" pill removes the currently-hidden (overflow)
// attachments only.
const removeHiddenAttachments = () => {
    const ids = new Set(hiddenAttachments.value.map((a) => a.id));
    if (!ids.size) return;
    ids.forEach((id) => {
        const ab = attachmentAborters.get(id);
        if (ab) { try { ab(); } catch { /* ignore */ } }
        attachmentAborters.delete(id);
    });
    attachments.value = attachments.value.filter((a) => !ids.has(a.id));
    attachmentCapNotice.value = 0;
};

// Open a finished file attachment in a new tab when the chip itself is
// clicked. Folders and in-flight/errored attachments are not clickable.
const onAttachmentClick = (a: AgentAttachment) => {
    if (a.status !== "done" || a.kind !== "file" || !a.uploadedUrl) return;
    window.open(a.uploadedUrl, "_blank", "noopener,noreferrer");
};

// Upload an attachment (single file or folder) to the service's db storage
// at the bucket root and return the resulting list of {filename, url}.
// Mutates `att` to track progress/status so the chip can render the bar.
const ATTACHMENT_URL_EXPIRES_SECONDS = 60 * 10; // 10m

const getAttachmentTemporaryUrlOptions = (_platform: AiPlatform) => {
    // Both Claude and OpenAI fetch the file from the URL we hand them, so a
    // temporary CDN URL works for both. generate_redirect_url is intentionally
    // left false because the redirect endpoint requires the caller's session.
    return {
        generate_temporary_cdn_url: true,
    };
};

const resolveAttachmentMemberUrl = async (
    storagePath: string,
    displayPath: string,
    platform: AiPlatform = selectedAiPlatform.value
) => {
    // storagePath is the exact db key the file was uploaded under (filename or
    // folder/filename). Pass it through verbatim. Do NOT slice/trim - earlier
    // versions did `slice(-2)` which corrupted deeper folder paths and, when
    // re-resolving a link extracted from a CDN URL, accidentally used the
    // service's content hash as if it were a folder.
    const plainPath = normalizeAttachmentPathCandidate(storagePath) || storagePath;
    const tu = await currentService.value.getTemporaryUrl({
        request: "get-db",
        path: plainPath,
        expires: ATTACHMENT_URL_EXPIRES_SECONDS,
        ...getAttachmentTemporaryUrlOptions(platform),
    });

    return {
        name: displayPath,
        url: typeof tu === "string" ? tu : tu.url,
    };
};

// The link handed to the model is the CDN one minted at upload time, and that
// is deliberate: it answers HEAD as well as GET. A short-lived S3 presign does
// not — a SigV4 query presign is bound to the single method it was signed for —
// and a provider that probes a file url with HEAD before downloading it gets a
// 403. See the WORKER_URL_REMINT_ENABLED note in the engine's composeUserMessage.
// Freshness is covered by the drain gate instead: the turn is dispatched once
// the indexing queue is empty, not left sitting in it.

// Read an existing db file's byte size by HEAD-ing its temporary URL. The
// chatbox keeps no local file listing (unlike dbfile.vue), so this is how we
// learn the size of a file we're about to overwrite — to apply the exact
// storage delta (newSize - oldSize). Returns null if it can't be determined
// (Content-Length is a CORS-safelisted header, so it's readable cross-origin;
// CloudFront signed URLs sign the path, not the method, so HEAD is allowed).
// On null the caller falls back to an optimistic add + authoritative refresh().
const fetchExistingDbFileSize = async (storagePath: string): Promise<number | null> => {
    try {
        const { url } = await resolveAttachmentMemberUrl(storagePath, storagePath);
        const res = await fetch(url, { method: "HEAD" });
        if (!res.ok) return null;
        const len = res.headers.get("content-length");
        const n = len != null ? Number(len) : NaN;
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
};

// Record the original error code/message on a failed attachment so the
// post-send error-report dialog can group failures by (code, description).
// Keeps the first error seen (folder members may fail more than once).
const recordAttachmentError = (att: AgentAttachment, err: any) => {
    if (att.errorCode || att.errorDetail) return;
    att.errorCode = (err && (err.code || err?.body?.code)) || "";
    att.errorDetail =
        (err && (err.message || err?.body?.message)) ||
        (typeof err === "string" ? err : "");
};

// Distil a raw per-file upload error from uploadHostFiles (an S3 XML error body,
// a JSON object, or a 'Network error'/'Timeout' string) into { code, message }.
// Without this, a failed upload throws a bare "Upload failed" and the real reason
// (EntityTooLarge, AccessDenied, a signature/policy rejection, a 5xx) is lost, so
// a file that "won't upload no matter what" is impossible to diagnose.
const summarizeUploadError = (err: any): { code: string; message: string } => {
    if (!err) return { code: "", message: "" };
    if (typeof err === "string") {
        const code = /<Code>([^<]+)<\/Code>/.exec(err)?.[1] || "";
        const msg = /<Message>([^<]+)<\/Message>/.exec(err)?.[1] || "";
        if (code || msg) return { code, message: msg || code };
        return { code: "", message: err.length > 300 ? err.slice(0, 300) : err };
    }
    if (err instanceof SkapiError) return { code: err.code || "", message: err.message || "" };
    const code = err.code || err?.body?.code || "";
    const message = err.message || err?.body?.message || "";
    if (code || message) return { code, message };
    try {
        return { code: "", message: JSON.stringify(err).slice(0, 300) };
    } catch {
        return { code: "", message: String(err) };
    }
};

const uploadSingleAttachment = async (
    att: AgentAttachment,
    // The turn these files were attached to, carried onto every indexing task so
    // its collapsed row is injected directly ABOVE that turn's bubble (see the
    // engine's BgTaskEntry.stageId). Undefined for an attachment-only send.
    stageId?: string,
    // The project + chat this upload belongs to, captured at Send time. This
    // function runs long after the user moved on, so reading the LIVE project
    // here is how one project's files ended up in another project's storage and
    // index (see AttachmentSendJob.pinnedService).
    pinnedService?: any,
    pinnedIdentity?: { projectId?: string; platform?: string; model?: string },
): Promise<{ name: string; url: string }[]> => {
    const svc = pinnedService || currentService.value;
    // Collect the attachment members first. Each member is then uploaded
    // individually so `EXISTS` can fall back to a temporary URL instead of
    // aborting the whole attachment upload.
    const members: { file: File; relPath: string; storagePath: string }[] = [];

    // Compose at CAPTURE. storagePath is the single string that becomes BOTH the upload key
    // (passed as the FormData filename below, which is what service.ts reads) and the "src::"
    // record id. Composing it here keeps those two identical by construction; composing either
    // one later produces a record pointing at an object that is not there.
    if (att.kind === "file") {
        members.push({
            file: att.file,
            relPath: att.name,
            storagePath: normalizeStoragePath(att.name),
        });
    } else {
        for (const entry of att.files) {
            const relPath = entry.path; // already includes folder prefix
            members.push({
                file: entry.file,
                relPath,
                storagePath: normalizeStoragePath(relPath),
            });
        }
    }

    const totalCount = members.length;
    if (totalCount === 0) return [];

    // Read BEFORE the status is reset below: a chip in "indexError" is one whose
    // bytes are already stored and only whose indexing failed (see the member loop).
    const isIndexRetry = att.status === "indexError";

    att.status = "uploading";
    // Left UNSET (not 0) on purpose: every member opens with a get-signed-url
    // round trip that reports no progress, so a chip starting at 0 rendered an
    // empty motionless bar for that whole phase and read as a stalled upload.
    // Unset is the "preparing" signal (barber pole); a number means bytes are
    // actually moving, which is the only time a real bar is shown.
    att.progress = undefined;
    delete att.errorMessage;
    delete att.errorCode; // clear any prior failure (retry)
    delete att.errorDetail;

    const urls: { name: string; url: string }[] = [];
    // Track whether any member uploaded OK but FAILED to queue an index request
    // (distinct yellow "Error" state vs a red upload "Failed").
    let anyIndexFailed = false;
    // Set when a member overwrote an existing file — triggers an authoritative
    // storage refresh() at the end to reconcile the optimistic over-count.
    let memberOverwritten = false;
    // Count members the user chose to Skip (duplicate files left untouched), so
    // an all-skipped attachment isn't mistaken for an upload failure below.
    let skippedCount = 0;
    for (let index = 0; index < members.length; index++) {
        const member = members[index];
        const form = new FormData();
        const baseName = member.storagePath.split("/").pop() || member.file.name;
        const contentTypeMapping: Record<string, string> = {};

        form.append("files[]", member.file, member.storagePath);

        if (!member.file.type) {
            const ct = mime.getType(baseName);
            if (ct) {
                contentTypeMapping[baseName] = ct;
            }
        }

        // hadExists: user kept the existing file ("Reindex") — skip the
        // re-upload, reuse + reindex it. overwritten: user replaced it.
        // skippedMember: user chose "Skip" — leave it untouched entirely.
        let hadExists = false;
        let overwritten = false;
        let skippedMember = false;
        // Size of the file an overwrite replaced (null if it couldn't be read),
        // so storage usage can change by the exact delta (newSize - oldSize).
        let overwrittenOldSize: number | null = null;

        // One upload attempt for this member. check_existence makes the server
        // reject an existing file (EXISTS) before the bytes upload.
        const doMemberUpload = async (checkExistence: boolean) => {
            const result = (await svc.uploadHostFiles(form, {
                contentTypeMapping,
                target: "db",
                ...(checkExistence ? { check_existence: true } : {}),
                progress: (p: any) => {
                    // Capture the abort handle on every callback so cancel
                    // works at any point during the upload. Cleared in finally.
                    if (typeof p.abort === "function") {
                        attachmentAborters.set(att.id, p.abort);
                    }
                    if (p.status !== "progress") return;

                    const currentPct = Math.max(
                        0,
                        Math.min(100, Math.floor(p.progress || 0))
                    );
                    const overall = Math.floor(
                        ((index + currentPct / 100) / totalCount) * 100
                    );
                    att.progress = Math.max(0, Math.min(100, overall));
                },
            })) as { completed: File[]; failed: File[]; failedErrors?: any[] };

            if (result.failed?.length) {
                // Surface the REAL reason (S3 code/message, network, timeout)
                // instead of a bare "Upload failed" so the failure is diagnosable.
                const { code, message } = summarizeUploadError(
                    result.failedErrors?.[0]
                );
                const e: any = new Error(
                    `Upload failed for ${member.relPath}` +
                        (message ? `: ${message}` : "")
                );
                if (code) e.code = code;
                throw e;
            }
        };

        // An INDEX-failure retry: these bytes are already in storage.
        // clearSuccessfulAttachments hands a chip whose upload succeeded but whose
        // index request failed back to the composer, and the next Send picks it up
        // here. Re-uploading it asks the user to resolve a "file already exists"
        // clash with themselves — which is what "it looks like it uploaded the same
        // file twice" was — burns the bytes a second time, and adds a second first
        // pass for a file that may still be indexing. Retry only the part that
        // failed, via the existing "the file is already there, reindex it" path.
        if (isIndexRetry) hadExists = true;
        try {
            try {
                if (!isIndexRetry) await doMemberUpload(true);
            } catch (err: any) {
                // If the user cancelled this attachment, surface that immediately
                // so the outer loop can skip to the next one.
                if (cancelledAttachmentIds.has(att.id)) {
                    throw err;
                }
                const code = err instanceof SkapiError ? err.code : err?.code;
                if (code !== "EXISTS") {
                    throw err;
                }
                // File already exists — ask how to resolve it.
                const choice = await promptOverwrite(member.file.name);
                if (choice === "overwrite") {
                    // Read the existing file's size BEFORE replacing it, so the
                    // storage delta is exact (newSize - oldSize).
                    overwrittenOldSize = await fetchExistingDbFileSize(member.storagePath);
                    await doMemberUpload(false); // replace the existing file
                    overwritten = true;
                } else if (choice === "skip") {
                    skippedMember = true;        // leave it untouched; no upload/index
                } else {
                    hadExists = true;            // keep it; Reindex
                }
            }
        } finally {
            attachmentAborters.delete(att.id);
        }

        if (skippedMember) {
            // User chose "Skip" for this duplicate — nothing uploaded, no
            // reindex, no index request, no storage change. Count it and
            // advance progress, then move on to the next member.
            skippedCount++;
            att.progress = Math.max(
                att.progress || 0,
                Math.floor(((index + 1) / totalCount) * 100)
            );
            continue;
        }

        if (cancelledAttachmentIds.has(att.id)) {
            // Cancelled mid-batch (folder member). Stop processing more
            // members and let the outer loop handle the cancel.
            throw new Error("Upload cancelled");
        }

        // Storage usage. New file → +size. Overwrite → the exact delta
        // (newSize - oldSize) when we could read the old size; otherwise add
        // optimistically and reconcile to the authoritative total via the
        // post-batch refresh(). "Reindex" keeps the existing file → no
        // change.
        if (overwritten) {
            if (overwrittenOldSize != null) {
                svc?.adjustFileStorageUsage?.(member.file.size - overwrittenOldSize);
            } else {
                memberOverwritten = true;
                svc?.adjustFileStorageUsage?.(member.file.size);
            }
        } else if (!hadExists) {
            svc?.adjustFileStorageUsage?.(member.file.size);
        }

        let resolvedUrl: string | undefined;
        try {
            const resolved = await resolveAttachmentMemberUrl(
                member.storagePath,
                member.relPath
            );
            urls.push(resolved);
            resolvedUrl = resolved.url;
        } catch (err) {
            console.error("getTemporaryUrl failed for", member.storagePath, err);
        }

        // Notify the AI agent to index the uploaded file. EXISTS files (already
        // in storage) are re-indexed too — the bubble shows "Reindexing: …"
        // (hadExists) instead of "Indexing: …". Push to bgTaskQueue (same as
        // dbfile.vue) so drainBgTaskQueue injects the Thinking bubble via the
        // poll it already holds, without triggering agentHistoryRefreshTrigger
        // which would fire fetchHistoryPage mid-send and wipe the local bubble.
        if (resolvedUrl) {
            // Whether anything actually reached the queue for this member, so the
            // drain below runs only when it has work.
            let queuedAnyIndex = false;
            try {
                // Platform/model from the PINNED identity when this upload was
                // queued behind a send (the chat it belongs to), falling back to
                // the project's saved setting for a files-page style upload.
                const raw = (svc?.service?.ai_agent || "").trim();
                if (raw || pinnedIdentity?.platform) {
                    const [platformRaw, modelRaw] = raw.includes("#") ? raw.split("#") : [raw, ""];
                    const pinnedPlatform = pinnedIdentity?.platform;
                    const resolvedPlatformRaw = (pinnedPlatform === "claude" || pinnedPlatform === "openai")
                        ? pinnedPlatform : platformRaw;
                    const platform = (resolvedPlatformRaw === "claude" || resolvedPlatformRaw === "openai")
                        ? (resolvedPlatformRaw as "claude" | "openai")
                        : null;
                    if (platform) {
                        const projectId = svc.id;
                        // Already being indexed: a second run opens a SECOND collapsed
                        // row for the same file and re-reads the whole document. Checked
                        // BEFORE the delete-then-repost below, which would otherwise wipe
                        // the records the LIVE run has already saved and then not
                        // re-index them. Reached by the index-failure retry, whose
                        // request the server can have queued even though the client saw
                        // it fail.
                        // CLAIM, not ask: the claim is recorded before the request goes
                        // out, so a second dispatch four seconds later sees it even
                        // though no ack has landed and the server's status index has
                        // not caught up. Asking could only ever see a dispatch that had
                        // already completed.
                        const alreadyIndexing = !(await chatSession.claimIndexRun(member.storagePath));
                        if (alreadyIndexing) {
                            console.log("[agent] skipping a duplicate index request for", member.storagePath);
                        }
                        // Delete-then-repost: when this file already existed
                        // (user chose Reindex, or Overwrite replaced the bytes),
                        // clear the stale "src::<storagePath>" index record first
                        // — the skapi backend cascades the delete to its
                        // reference-linked children — so re-indexing REPLACES the
                        // record instead of colliding/duplicating. Best-effort.
                        // BEFORE the delete: an existing file keeps the group it
                        // already had. Re-indexing or overwriting is not a
                        // decision about who may see the file, and re-resolving
                        // would apply a changed project default (or prompt again)
                        // and could declassify a file marked private. The delete
                        // destroys the record the group is stored on.
                        const existingGroup = (hadExists || overwritten)
                            ? await readFileIndexAccessGroup(projectId, member.storagePath)
                            : null;
                        if ((hadExists || overwritten) && !alreadyIndexing) {
                            await deleteFileIndexRecord(projectId, member.storagePath);
                        }
                        // Create the file record BEFORE any pass is enqueued. Every pass
                        // references "src::<path>", and a pass cannot know whether an
                        // earlier one made it, so the backend was rejecting whole windows
                        // with NOT_EXISTS on reference.unique_id. Must run AFTER the delete
                        // above, which would otherwise wipe what we just created.
                        // ONE decision, used twice: the src:: record's own access
                        // group and the group the indexing prompt tells the agent
                        // to write its records at. Resolving it twice could give
                        // two different answers (the project can be set to ask per
                        // file), and a file whose rows sit in a different group
                        // than its record never comes back whole.
                        const accessGroup = existingGroup
                            || await resolveUploadAccessGroup(projectId, member.file?.name || member.storagePath);
                        await ensureFileIndexRecord(projectId, member.storagePath, {
                            name: member.file?.name,
                            mime: member.file?.type,
                            size: member.file?.size,
                        }, accessGroup);
                        // Index requests this member got onto the queue. Only their
                        // presence decides whether the drain below is worth running.
                        const queuedIndexIds: string[] = [];
                        try {
                            // trackIndexDispatch, mirroring the engine's own upload path:
                            // a chat waiting on awaitIndexingDrained must not be sent in
                            // the window between this call and the queue accepting it —
                            // the pass is not on the server's queue yet, so no look can
                            // see it, and the gap reads as the file being finished.
                            const indexResult: any = alreadyIndexing ? null : await chatSession.trackIndexDispatch(notifyAgentSaveAttachment({
                                platform,
                                model: (pinnedIdentity?.model as string) || modelRaw || undefined,
                                service: projectId,
                                publicProjectId: svc?.service?.projectId || undefined,
                                owner: svc.owner,
                                // The queue this pass is enqueued on, and it MUST match the
                                // one the chat turn goes out on (chatHost.getIdentity().userId)
                                // — the backend only serialises requests that share a queue
                                // name. Omitted, this defaulted to the service id, so the
                                // files were indexed on "<projectId>-bg" while the question
                                // ran on "<userId>-bg": two queues, running side by side, and
                                // the answer arrived before anything had read the file.
                                userId: (skapi as any).user?.user_id || projectId,
                                serviceName: svc?.service?.name,
                                serviceDescription: svc?.service?.description,
                                attachment: {
                                    name: member.file.name,
                                    storagePath: member.storagePath,
                                    mime: member.file.type || mime.getType(member.file.name) || undefined,
                                    size: member.file.size,
                                    url: resolvedUrl,
                                    accessGroup,
                                },
                            }));
                            // Push to bgTaskQueue so drainBgTaskQueue injects
                            // the bubble with its own poll — no fetchHistoryPage needed.
                            if (indexResult?.id && typeof indexResult.id === 'string') {
                                bgTaskQueue.push({
                                    projectId,
                                    platform,
                                    id: indexResult.id,
                                    filename: member.file.name,
                                    storagePath: member.storagePath,
                                    isReindex: hadExists,
                                    mime: member.file.type || mime.getType(member.file.name) || undefined,
                                    size: member.file.size,
                                    status: indexResult.status === 'running' ? 'running' : 'pending',
                                    poll: indexResult.poll ?? undefined,
                                    // Puts this file's row directly above the chat
                                    // turn it came with, from the moment it appears.
                                    stageId,
                                });
                                queuedIndexIds.push(indexResult.id);
                            }
                        } catch (err) {
                            console.error("notifyAgentSaveAttachment failed", err);
                            // Nothing reached the queue, so give the slot back or this
                            // client's own claim would refuse the retry. See claimIndexRun.
                            chatSession.releaseIndexRun(member.storagePath);
                            anyIndexFailed = true;
                            recordAttachmentError(att, err);
                        }
                        queuedAnyIndex = queuedAnyIndex || queuedIndexIds.length > 0;
                    }
                }
            } catch (err) {
                console.error("notifyAgentSaveAttachment failed", err);
                anyIndexFailed = true;
                recordAttachmentError(att, err);
            }
            // Drain HERE, as the engine's own upload path does, not only from the
            // bgTaskQueue watcher: that watcher bails while a history page is loading,
            // and the older-page branch never drains afterwards. An ack landing during
            // a scroll-up left the entry undrained — no row, and no poll attached (the
            // poll is attached by the drain) — until something else happened to drain,
            // by which time the staged bubble was gone and the row appended BELOW the
            // message instead of above it.
            //
            // Outside BOTH try blocks on purpose: their catches mean "the indexing
            // request failed" and paint the chip yellow, so a throw from anything the
            // drain reaches (notify, a scroll, a poll) would report a request the
            // server accepted as a failed one.
            if (queuedAnyIndex) drainBgTaskQueue();
        }

        att.progress = Math.max(
            att.progress || 0,
            Math.floor(((index + 1) / totalCount) * 100)
        );
    }

    if (!urls.length) {
        if (skippedCount === totalCount) {
            // Every member was a duplicate the user chose to Skip — not a
            // failure. Drop the chip; it contributes nothing to the message.
            const i = attachments.value.findIndex((a) => a.id === att.id);
            if (i !== -1) attachments.value.splice(i, 1);
            return [];
        }
        throw new Error(`Upload failed for ${att.name}`);
    }

    att.progress = 100;
    // Uploaded, but if any member's index request failed → yellow "Error".
    att.status = anyIndexFailed ? "indexError" : "done";
    if (anyIndexFailed) att.errorMessage = "File indexing failed";
    if (att.kind === "file") {
        att.uploadedUrl = urls[0]?.url;
    } else {
        att.uploadedUrls = urls.map((u) => ({ path: u.name, url: u.url }));
    }
    // An overwrite optimistically added newSize; pull the authoritative usage to
    // reconcile the old bytes the server reclaimed (the chatbox has no local
    // file listing to compute the exact delta).
    if (memberOverwritten) {
        void svc?.refresh?.();
    }
    return urls;
};

// Uploads the chips of ONE batch (stamped with batchId at Send time) and
// returns their urls. Scoped to the batch because the composer stays live: by
// the time this runs, attachments.value can already hold chips picked for the
// next message — uploading those here would attach them to the wrong turn, and
// collecting an earlier batch's finished urls would re-attach files the user
// already sent.
const uploadPendingAttachments = async (
    batchId: string,
    // See uploadSingleAttachment: anchors this batch's indexing rows to its turn.
    stageId?: string,
    // The project this batch belongs to, captured at Send time (see
    // AttachmentSendJob.pinnedService). Falls back to the live one for the
    // callers that upload synchronously with the user's click.
    pinnedService?: any,
    pinnedIdentity?: { projectId?: string; platform?: string; model?: string },
): Promise<{ name: string; url: string }[]> => {
    // Fresh batch — clear any "apply to all" choice from a previous upload.
    resetOverwriteBatch();
    resetAccessGroupBatch();
    // The files land in THIS project's storage; capture it up front so the
    // post-upload refresh signal below still targets that project even if the
    // user navigated elsewhere while the uploads were running.
    const svc = pinnedService || currentService.value;
    const uploadTargetServiceId = svc?.id;
    const batch = attachments.value.filter((a) => a._batchId === batchId);
    const all = batch.filter((a) => a.status !== "done");
    if (all.length === 0) {
        // already-uploaded attachments contribute their cached URLs
        const cached: { name: string; url: string }[] = [];
        for (const a of batch) {
            if (a.kind === "file" && a.uploadedUrl) {
                cached.push({ name: a.name, url: a.uploadedUrl });
            } else if (a.kind === "folder" && a.uploadedUrls) {
                cached.push(
                    ...a.uploadedUrls.map((u) => ({ name: u.path, url: u.url }))
                );
            }
        }
        return cached;
    }

    uploadingAttachments.value = true;
    const collected: { name: string; url: string }[] = [];

    try {
        // Snapshot so removals (e.g. user cancelling the in-flight upload)
        // don't disrupt iteration of the live reactive array.
        const queue = [...batch];
        for (const att of queue) {
            // The attachment may have been removed (cancelled) before we
            // reached it.
            if (!attachments.value.some((a) => a.id === att.id)) continue;
            if (att.status === "done") {
                if (att.kind === "file" && att.uploadedUrl) {
                    collected.push({ name: att.name, url: att.uploadedUrl });
                } else if (att.kind === "folder" && att.uploadedUrls) {
                    collected.push(
                        ...att.uploadedUrls.map((u) => ({ name: u.path, url: u.url }))
                    );
                }
                continue;
            }
            try {
                const urls = await uploadSingleAttachment(att, stageId, svc, pinnedIdentity);
                collected.push(...urls);
            } catch (err: any) {
                if (cancelledAttachmentIds.has(att.id)) {
                    // User cancelled this one; skip to the next attachment.
                    cancelledAttachmentIds.delete(att.id);
                    attachmentAborters.delete(att.id);
                    continue;
                }
                // Genuine upload failure → red "Failed". Do NOT throw: keep
                // uploading the rest; the failed chip persists for the user.
                att.status = "error";
                att.errorMessage = "File upload has failed";
                recordAttachmentError(att, err);
            }
        }
    } finally {
        uploadingAttachments.value = false;
    }

    // The chatbox just stored files in db storage → tell the db file manager
    // (dbfile.vue) to refresh its listing. Only when at least one file actually
    // landed (collected has its URL) — not when every upload failed, which would
    // be a wasted refresh.
    if (uploadTargetServiceId && collected.length > 0) {
        dbFileRefreshTrigger[uploadTargetServiceId] =
            (dbFileRefreshTrigger[uploadTargetServiceId] || 0) + 1;
    }

    return collected;
};

// Iteratively reduce the number of visible chips until the wrap container's
// scrollHeight no longer exceeds 30% of the chat container's height. The
// "...(n) more" chip renders as soon as we hide one, so each tick stabilizes
// to the largest count that fits *with* the overflow indicator present.
const recomputeAttachmentOverflow = async () => {
    const total = attachments.value.length;
    if (!total) {
        visibleAttachmentCount.value = Number.MAX_SAFE_INTEGER;
        return;
    }

    // Start from the render cap (not `total`): visibleAttachments never mounts
    // more than VISIBLE_CHIP_CAP chips, so measuring/shrinking from a higher
    // count would spin the loop uselessly over invisible items. This bounds the
    // loop to O(cap) awaits instead of O(n).
    let count = Math.min(total, VISIBLE_CHIP_CAP);
    visibleAttachmentCount.value = count;
    await nextTick();

    const chat = chatBox.value;
    const box = attachmentsBox.value;
    if (!chat || !box) return;

    const maxHeight = chat.clientHeight * ATTACHMENTS_MAX_HEIGHT_RATIO;
    if (maxHeight <= 0) return;

    while (count > 0 && box.scrollHeight > maxHeight) {
        count--;
        visibleAttachmentCount.value = count;
        await nextTick();
    }
};

const scheduleAttachmentRecompute = () => {
    cancelAnimationFrame(attachmentsRecomputeFrame);
    attachmentsRecomputeFrame = requestAnimationFrame(() => {
        void recomputeAttachmentOverflow();
    });
};

watch(
    () => attachments.value.length,
    () => {
        scheduleAttachmentRecompute();
    }
);

// Recursively reads a FileSystemEntry (file or directory) into a flat list of
// files with their relative paths. Paths are prefixed with the provided prefix
// (used for nested directory traversal).
const readEntry = (entry: any, prefix = ""): Promise<{ file: File; path: string }[]> => {
    return new Promise((resolve) => {
        if (!entry) {
            resolve([]);
            return;
        }

        if (entry.isFile) {
            entry.file(
                (file: File) => {
                    resolve([{ file, path: prefix + file.name }]);
                },
                () => resolve([])
            );
            return;
        }

        if (entry.isDirectory) {
            const reader = entry.createReader();
            const all: { file: File; path: string }[] = [];
            const readBatch = () => {
                reader.readEntries(async (entries: any[]) => {
                    if (!entries.length) {
                        resolve(all);
                        return;
                    }
                    const groups = await Promise.all(
                        entries.map((e) => readEntry(e, prefix + entry.name + "/"))
                    );
                    groups.forEach((g) => all.push(...g));
                    // readEntries only returns a chunk; keep reading until empty.
                    readBatch();
                }, () => resolve(all));
            };
            readBatch();
            return;
        }

        resolve([]);
    });
};

const onChatDragEnter = (e: DragEvent) => {
    if (!chatEnabled.value) return;
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
    dragDepth++;
    dragHere.value = true;
};

// Hide the attach affordances (clip button + drag-drop) when the service DB is
// frozen for a non-admin user (access_group 99 == admin/master). Mirrors the
// widget (index.js uploadsFrozenForUser) and the backend get_signed_url gate
// (freeze_database && not is_admin). The backend gate was is_master until
// 03c831c1 widened it to any admin, so keep this on access_group 99, not owner.
const uploadsFrozenForUser = computed(() => {
    if (!currentService.value?.service?.freeze_database) return false;
    const ag = typeof user.access_group === "number" ? user.access_group : 0;
    return ag < 99;
});

const onChatDragOver = (e: DragEvent) => {
    if (!chatEnabled.value || uploadsFrozenForUser.value) return;
    if (!e.dataTransfer) return;
    if (!Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.dataTransfer.dropEffect = "copy";
    dragHere.value = true;
};

const onChatDragLeave = (_e: DragEvent) => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) {
        dragHere.value = false;
    }
};

const onChatDrop = async (e: DragEvent) => {
    dragDepth = 0;
    dragHere.value = false;
    if (!chatEnabled.value || uploadsFrozenForUser.value) return;
    if (!e.dataTransfer) return;
    // A second drop while the first is still being read would queue another
    // directory walk behind the one already making the page feel slow.
    if (preparingDrop.value) return;

    // BEFORE the first await, so the scrim is on screen for the whole walk
    // rather than appearing once there is nothing left to wait for.
    preparingDrop.value = true;
    try {
        await collectChatDrop(e);
    } finally {
        preparingDrop.value = false;
    }
};

/** Turn a drop into attachments. Split out only so onChatDrop can wrap it. */
const collectChatDrop = async (e: DragEvent) => {
    if (!e.dataTransfer) return;

    const items = e.dataTransfer.items;
    const added: AgentAttachment[] = [];

    if (items && items.length) {
        const entries: any[] = [];
        for (let i = 0; i < items.length; i++) {
            const it = items[i];
            if (it.kind !== "file") continue;
            const entry = (it as any).webkitGetAsEntry ? (it as any).webkitGetAsEntry() : null;
            entries.push(entry || it.getAsFile());
        }

        for (const entry of entries) {
            if (!entry) continue;
            if (entry instanceof File) {
                added.push({
                    id: newAttachmentId(),
                    kind: "file",
                    name: entry.name,
                    file: entry,
                });
                continue;
            }

            if (entry.isFile) {
                const files = await readEntry(entry);
                if (files[0]) {
                    added.push({
                        id: newAttachmentId(),
                        kind: "file",
                        name: files[0].file.name,
                        file: files[0].file,
                    });
                }
            } else if (entry.isDirectory) {
                const files = await readEntry(entry);
                added.push({
                    id: newAttachmentId(),
                    kind: "folder",
                    name: entry.name,
                    files,
                });
            }
        }
    } else if (e.dataTransfer.files && e.dataTransfer.files.length) {
        for (const f of Array.from(e.dataTransfer.files)) {
            added.push({ id: newAttachmentId(), kind: "file", name: f.name, file: f });
        }
    }

    if (added.length) {
        appendAttachments(added);
    }
};

const attachInput = ref<HTMLInputElement | null>(null);

const appendAttachments = (added: AgentAttachment[]) => {
    if (!added.length) return;
    const existingKeys = new Set(attachments.value.map(attachmentKey));
    const seenInBatch = new Set<string>();
    const deduped = added.filter((a) => {
        const k = attachmentKey(a);
        if (existingKeys.has(k) || seenInBatch.has(k)) return false;
        seenInBatch.add(k);
        return true;
    });
    if (!deduped.length) return;

    // Hard cap total file count at MAX_ATTACHMENT_FILE_COUNT (folders count as
    // their file count). Excess is dropped and the boundary folder truncated so a
    // mass drop/select can't balloon the set past the ceiling and freeze the tab.
    // (The advisory 20-file limit still applies to sends via attachmentCountWarning.)
    let remaining = MAX_ATTACHMENT_FILE_COUNT - countFilesInAttachments(composerAttachments.value);
    let dropped = 0;
    const accepted: AgentAttachment[] = [];
    for (const a of deduped) {
        let count = a.kind === "folder" ? a.files.length : 1;
        if (remaining <= 0) { dropped += count; continue; }
        if (a.kind === "folder" && count > remaining) {
            dropped += count - remaining;
            a.files = a.files.slice(0, remaining);
            count = remaining;
        }
        accepted.push(a);
        remaining -= count;
    }
    if (accepted.length) attachments.value = attachments.value.concat(accepted);
    attachmentCapNotice.value = dropped;
};

const openAttachPicker = () => {
    // Attaching stays open during an upload: those files join the NEXT message.
    if (!chatEnabled.value) {
        return;
    }
    attachInput.value?.click();
};

const onAttachFromInput = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    if (files.length) {
        const added: AgentAttachment[] = files.map((f) => ({
            id: newAttachmentId(),
            kind: "file",
            name: f.name,
            file: f,
        }));
        appendAttachments(added);
    }
    // reset so selecting the same file again still fires `change`
    input.value = "";
};
// Drives the animated dots in [generating file.ext...] placeholders.
// Ticks from 0..2 while any typewriter is active.
const dotTick = ref(0);
let dotTickTimer: ReturnType<typeof setInterval> | null = null;
const historyLoadDotTick = ref(0);
let historyLoadDotTickTimer: ReturnType<typeof setInterval> | null = null;
// chatSession.state.typingAbort / chatSession.state.gateRefreshToken / chatSession.state.historyRequestToken / chatSession.cancelledServerIds /
// chatSession.historyItemPolls now live on the shared engine (chatSession.state.* and
// chatSession.*) — see the global sed-repoint in the cutover.
const initialGateLoading = ref(initialAiAgentState.hasPlatform);
const selectedAiPlatform = ref<AiPlatform>(initialAiAgentState.platform);
const selectedAiModel = ref(initialAiAgentState.model);
const hasAiPlatform = ref(initialAiAgentState.hasPlatform);
const hasAiApiKey = ref(false);
const messages = toRef(chatSession.state, "messages") as unknown as Ref<ChatMessage[]>;
const agentRoot = ref<HTMLElement | null>(null);
const messagesBox = ref<HTMLElement | null>(null);
const inputBox = ref<HTMLTextAreaElement | null>(null);
const loadingHistory = toRef(chatSession.state, "loadingHistory");
const loadingOlderHistory = toRef(chatSession.state, "loadingOlderHistory");
const historyEndOfList = toRef(chatSession.state, "historyEndOfList");
const bgHistoryLoading = toRef(chatSession.state, "bgHistoryLoading");
// Ownership sequence for the flag above — see the deferred-batch handler.
let bgHistoryBatchSeq = 0;
const historyStartKeyHistory = toRef(chatSession.state, "historyStartKeyHistory");
// Which files the server still has indexing work for, and whether that has been
// answered yet for this chat. Both live on the engine's reactive state, so a row
// stops (or starts) claiming to be finished as soon as the answer changes.
const liveIndexKeys = toRef(chatSession.state, "liveIndexKeys");
const liveIndexChecked = toRef(chatSession.state, "liveIndexChecked");
// The passes that were on a row when the user stopped it. On the engine's reactive
// state for the same reason: a stop with nothing left to cancel changes no message,
// so this is the only thing that can make the row re-render as stopped.
const stoppedIndexIds = toRef(chatSession.state, "stoppedIndexIds");

// Durable done:: markers for this project (one shared prefix sweep, see
// file.ts). Rows carrying one settle green with NO queue round trip; only
// marker-less runs (pre-marker history, dead chains) still wait on the queue
// inference. Refreshed on gate load and whenever the live-key set drains —
// the moment a run ends is the moment a fresh marker may exist.
const doneMarkerKeys = ref<Record<string, boolean>>({});
// Durable run:: records from the same sweep: one per file, carrying status.
// Fed to buildChatDisplayList as runStubs so an indexing row paints (with its
// recorded verdict) BEFORE any bg history arrives, and for runs older than the
// paging cap that never load at all. A real group always suppresses its stub.
const runStubRecords = ref<Record<string, IndexRunRecordInfo>>({});
// Which project the two refs above describe (cleared on switch in refreshGate).
let markerRefsSvc = "";
// Re-sweep cadence while a 'working' run:: stub is on screen. Matches the
// sweep TTL so each tick fetches at most one fresh sweep — and BOUNDED: a dead
// record nothing will ever flip must not keep a forced sweep running for the
// life of the tab. The round counter resets whenever the stub set changes.
const STUB_RECHECK_MS = 30_000;
const STUB_RECHECK_MAX_ROUNDS = 5;
let stubRecheckTimer: ReturnType<typeof setTimeout> | null = null;
let stubRecheckSig = "";
let stubRecheckRounds = 0;
// False until the current project's marker sweep has answered once (success or
// failure): the greeting holds on it, or it flashes over stub rows that are a
// beat away from existing on a cold project load.
const markerSweepSettled = ref(false);
// Paths whose 'working' run:: record this visit already flipped to done off
// deterministic row evidence (mint-on-observe) — once per path per project.
const mintObservedPaths = new Set<string>();
const refreshDoneMarkers = (invalidate = false) => {
    const svc = currentService.value?.id;
    if (!svc) return;
    if (invalidate) invalidateDoneMarkerSweep(svc);
    void sweepIndexMarkers(svc).then((sweep) => {
        if (currentService.value?.id !== svc) return;
        markerRefsSvc = svc;
        markerSweepSettled.value = true;
        const next: Record<string, boolean> = sweep.partial ? { ...doneMarkerKeys.value } : {};
        for (const p of sweep.done) next[p] = true;
        doneMarkerKeys.value = next;
        // Stub rows are per-CHAT, and each admin has their own chat: another
        // user's run must not splice a row into this conversation (their runs
        // still feed the FILES page, which reads the sweep directly). Records
        // without an owner (pre-owner sweeps) are kept — better a stray row
        // than a hidden run of your own.
        const myId = (skapi as any).user?.user_id || "";
        // Copy each record: the sweep cache owns these objects and patches them
        // in place (noteRunRecordStatus), which would mutate what is rendered
        // with no reactive notification.
        const stubs: Record<string, IndexRunRecordInfo> = sweep.partial
            ? { ...runStubRecords.value }   // truncated: absence proves nothing
            : {};
        sweep.runs.forEach((info, path) => {
            if (info.owner && myId && info.owner !== myId) return;
            stubs[path] = { ...info };
        });
        runStubRecords.value = stubs;
    }).catch(() => {
        // A failed sweep still SETTLES the greeting hold — better an early
        // greeting than one that can never appear.
        if (currentService.value?.id === svc) markerSweepSettled.value = true;
    });
};
watch(liveIndexKeys, (nv, ov) => {
    const now = nv ? Object.keys(nv).length : 0;
    const before = ov ? Object.keys(ov).length : 0;
    if (before > 0 && now < before) {
        // A run just ended: this is the moment a fresh verdict exists, so the
        // recheck budget starts over rather than staying spent from the live
        // phase.
        stubRecheckRounds = 0;
        refreshDoneMarkers(true);
    }
});

let viewportHeightFrame = 0;
let agentUnmounted = false;

// Point the engine session's history-cache + in-flight-request map at the
// project-list SHARED stores (they survive component unmount and are read by
// other views), preserving cross-mount + cross-component behavior. The session
// is a module singleton; re-pointing on each setup is idempotent.
chatSession.aiChatHistoryCache = aiChatHistoryCache as any;
chatSession.pendingAgentRequests = pendingAgentRequests as any;

const chatEnabled = computed(() => {
    return hasAiPlatform.value && hasAiApiKey.value;
});

const showInitialLoading = computed(() => {
    return initialGateLoading.value && hasAiPlatform.value;
});

const showHistoryLoadingIndicator = computed(() => {
    return loadingOlderHistory.value && !initialGateLoading.value && messages.value.length > 0;
});

const historyLoadingLabel = computed(() => {
    return `Fetching history${".".repeat((historyLoadDotTick.value % 3) + 1)}`;
});

const platformLabel = computed(() => {
    if (selectedAiPlatform.value === "claude") {
        return "Claude";
    }

    if (selectedAiPlatform.value === "openai") {
        return "ChatGPT";
    }

    return "AI";
});

const platformLogo = computed(() => {
    if (selectedAiPlatform.value === "claude") return claudeLogo;
    if (selectedAiPlatform.value === "openai") return chatgptLogo;
    return "";
});

const agentTitle = computed(() => {
    if (!hasAiPlatform.value) return "No AI platform";
    return `${platformLabel.value} Agent`;
});

const projectName = computed(() => {
    return currentService.value?.service?.name || "";
});

// The opening line, from the engine so the widget cannot say something else.
// The template draws the three pieces (the name needs its own translate="no"
// element); buildSystemPrompt sends the assembled `.text` to the model, which
// never sees the bubble itself.
const greetingParts = computed(() =>
    buildChatGreeting({
        projectName: projectName.value,
        canUpload: !uploadsFrozenForUser.value,
        // Set from the settings page. Replaces the sentence outright, so the
        // template's three-piece render collapses to one plain block.
        custom: customGreeting.value,
    }));

const selectedAiModelDisplay = computed(() => {
    if (selectedAiModel.value) {
        return selectedAiModel.value;
    }

    if (selectedAiPlatform.value === "claude") {
        return DEFAULT_CLAUDE_MODEL;
    }

    if (selectedAiPlatform.value === "openai") {
        return DEFAULT_OPENAI_MODEL;
    }

    return "";
});

// Available models for the currently-selected platform. Sourced from
// `aiModelCache` (shared with project settings) so we don't refetch.
type AiModel = { id: string; display_name?: string };
const availableModels = ref<AiModel[]>([]);
const fetchingModels = ref(false);
const savingModel = ref(false);
const modelMenuOpen = ref(false);
const isMobile = ref(false);
// Set to true when fetching the model list fails despite an API key being
// configured — the only realistic cause is an invalid/revoked key. Cleared on
// platform change and on successful save/refetch.
const apiKeyInvalid = ref(false);
let modelFetchToken = 0;

const syncMobile = () => {
    isMobile.value = window.matchMedia("(max-width: 600px)").matches;
    if (isMobile.value) modelMenuOpen.value = false;
};

const toggleModelMenu = () => {
    if (isMobile.value) return;
    modelMenuOpen.value = !modelMenuOpen.value;
};

const closeModelMenu = () => {
    modelMenuOpen.value = false;
};

// Resolve the model id we should treat as currently selected: the saved one,
// or the platform default if the saved one is empty/missing from the list.
const effectiveModel = computed(() => {
    const list = availableModels.value;
    if (!list.length) return selectedAiModel.value || "";

    const saved = selectedAiModel.value;
    if (saved && list.some((m) => m.id === saved)) return saved;

    const platformDefault =
        selectedAiPlatform.value === "claude"
            ? DEFAULT_CLAUDE_MODEL
            : selectedAiPlatform.value === "openai"
                ? DEFAULT_OPENAI_MODEL
                : "";

    if (platformDefault && list.some((m) => m.id === platformDefault)) {
        return platformDefault;
    }

    return list[0]?.id || "";
});

const effectiveModelLabel = computed(() => {
    const id = effectiveModel.value;
    if (!id) return "Select model";
    const match = availableModels.value.find((m) => m.id === id);
    return match?.display_name || match?.id || id;
});

// The picker reads the STATIC catalogue (see @/code/ai_models): the provider's
// listing call cost seconds through the queue+proxy on every chat visit and
// every platform switch, to fill a dropdown that changes a few times a year.
//
// That call also doubled as the invalid-key probe, so the probe is now its own
// thing: fired in the BACKGROUND (never blocking the picker), at most once per
// project+platform per page session, and only when a key is actually
// configured — a missing key already has its own overlay.
const loadAvailableModels = async () => {
    const platform = selectedAiPlatform.value;
    if (platform !== "claude" && platform !== "openai") {
        availableModels.value = [];
        return;
    }

    const token = ++modelFetchToken;
    const cached = aiModelCache[platform];
    if (cached && cached.length) {
        availableModels.value = cached;
    } else {
        fetchingModels.value = true;
        try {
            const models = (await loadModelCatalogue(platform)) as AiModel[];
            if (token !== modelFetchToken || selectedAiPlatform.value !== platform) return;
            aiModelCache[platform] = models;
            availableModels.value = models;
            // Anthropic's catalogue carries max_input_tokens per model, which is
            // the authoritative context window for the send budget.
            registerModelContextWindows(models as any);
        } finally {
            if (token === modelFetchToken) fetchingModels.value = false;
        }
    }

    // Nothing is probed here. The catalogue is a static file and says nothing
    // about the key, and the old probe (a provider model-listing call) reported
    // every transport failure as "your API key is invalid". The verdict now
    // comes from real chat traffic: see syncApiKeyVerdict.
    syncApiKeyVerdict();
};

// Mirror the stored verdict (set only by a provider answer that NAMED the key)
// into the overlay flag.
const syncApiKeyVerdict = () => {
    const platform = selectedAiPlatform.value;
    const projectId = currentService.value?.id;
    if (!projectId || (platform !== "claude" && platform !== "openai") || !hasAiApiKey.value) {
        apiKeyInvalid.value = false;
        return;
    }
    apiKeyInvalid.value = apiKeyRejected(projectId, platform);
};

watch(
    () => [chatEnabled.value, selectedAiPlatform.value],
    () => {
        modelMenuOpen.value = false;
        if (chatEnabled.value) void loadAvailableModels();
    },
    { immediate: true }
);

const onModelSelect = async (modelId: string) => {
    modelMenuOpen.value = false;
    const platform = selectedAiPlatform.value;
    if (platform !== "claude" && platform !== "openai") return;
    if (!modelId || modelId === selectedAiModel.value) return;

    const previous = selectedAiModel.value;
    selectedAiModel.value = modelId;
    savingModel.value = true;
    try {
        await currentService.value.setServiceOption({
            ai_agent: `${platform}#${modelId}`,
        });
    } catch (err) {
        // revert on failure
        selectedAiModel.value = previous;
        console.error("Failed to update AI model", err);
    } finally {
        savingModel.value = false;
    }
};

const onModelSelectFromNative = (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    if (target) void onModelSelect(target.value);
};

// AI platform selector. Mirrors the model selector pattern: writes the new
// platform to the service config (clearing the model so the platform's
// default is used until the user picks one), then relies on the existing
// `currentService.service.ai_agent` watcher to call refreshGate(), which
// re-pulls the API key state and reloads the model list for the new platform.
type PlatformOption = { id: "claude" | "openai"; label: string; logo: string };
// ChatGPT leads the list so it reads as the house default; Claude follows.
const platformOptions: PlatformOption[] = [
    { id: "openai", label: "ChatGPT", logo: chatgptLogo },
    { id: "claude", label: "Claude", logo: claudeLogo },
];
const platformMenuOpen = ref(false);
const savingPlatform = ref(false);

const togglePlatformMenu = () => {
    if (isMobile.value) return;
    platformMenuOpen.value = !platformMenuOpen.value;
    if (platformMenuOpen.value) modelMenuOpen.value = false;
};

const closePlatformMenu = () => {
    platformMenuOpen.value = false;
};

const onPlatformSelect = async (platform: "claude" | "openai") => {
    platformMenuOpen.value = false;
    if (!platform) return;
    if (platform === selectedAiPlatform.value) return;

    const previousPlatform = selectedAiPlatform.value;
    const previousModel = selectedAiModel.value;
    selectedAiPlatform.value = platform;
    selectedAiModel.value = "";
    apiKeyInvalid.value = false;
    savingPlatform.value = true;
    // Show the hopping-rabbit chatbox overlay (same one used during the
    // initial gate load) while the platform swap + gate refresh runs.
    initialGateLoading.value = true;
    try {
        await currentService.value.setServiceOption({
            ai_agent: platform,
        });
        
        savingPlatform.value = false;

        // Force the gate to re-resolve immediately so the overlay stays up
        // until API key + model list are ready for the new platform.
        await refreshGate();
    } catch (err) {
        // revert on failure
        selectedAiPlatform.value = previousPlatform;
        selectedAiModel.value = previousModel;
        initialGateLoading.value = false;
        console.error("Failed to update AI platform", err);
    } finally {
        savingPlatform.value = false;
    }
};

const onPlatformSelectFromNative = (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    const value = target?.value;
    if (value === "claude" || value === "openai") void onPlatformSelect(value);
};

// API key entry overlay + modal. Mirrors service.vue's saveAiApiKey flow:
// registerClientSecret → setServiceOption(ai_agent: <platform>) → refresh
// the local API-key cache and re-run refreshGate so the chat unlocks.
const apiKeyModalOpen = ref(false);
const apiKeyInput = ref("");
const apiKeyInputEl = ref<HTMLTextAreaElement | null>(null);
const savingApiKey = ref(false);
const loadingApiKey = ref(false);
const apiKeyLoadingDots = ref(".");
let apiKeyLoadingDotsTimer: ReturnType<typeof setInterval> | null = null;
const startApiKeyDots = () => {
    if (apiKeyLoadingDotsTimer) return;
    let n = 0;
    apiKeyLoadingDots.value = ".";
    apiKeyLoadingDotsTimer = setInterval(() => {
        n = (n + 1) % 3;
        apiKeyLoadingDots.value = ".".repeat(n + 1);
    }, 350);
};
const stopApiKeyDots = () => {
    if (apiKeyLoadingDotsTimer) {
        clearInterval(apiKeyLoadingDotsTimer);
        apiKeyLoadingDotsTimer = null;
    }
};

const apiKeyDocsUrl = computed(() => {
    if (selectedAiPlatform.value === "claude") return "https://platform.claude.com";
    if (selectedAiPlatform.value === "openai") return "https://platform.openai.com/";
    return "";
});

const openApiKeyModal = async () => {
    if (!hasAiPlatform.value || savingApiKey.value || loadingApiKey.value) return;
    const platform = selectedAiPlatform.value;
    const projectId = currentService.value?.id;
    const cachedKey =
        (platform === "claude" || platform === "openai") && projectId
            ? aiApiKeyCache[projectId]?.[platform] ||
              serviceClientSecrets[projectId]?.list?.[platform]?.client_secret ||
              ""
            : "";
    let prefill = cachedKey || "";
    // If we don't have a current key for this service+platform, await the
    // privately-stored last key before opening the dialog so the input is
    // already populated when the user sees it.
    if (!prefill && (platform === "claude" || platform === "openai")) {
        loadingApiKey.value = true;
        startApiKeyDots();
        try {
            prefill = (await loadAiApiKeyPrivately(platform)) || "";
        } finally {
            loadingApiKey.value = false;
            stopApiKeyDots();
        }
    }
    apiKeyInput.value = prefill;
    apiKeyModalOpen.value = true;
    nextTick(() => {
        apiKeyInputEl.value?.focus();
    });
};

const saveApiKey = async () => {
    const platform = selectedAiPlatform.value;
    if (platform !== "claude" && platform !== "openai") return;
    const key = apiKeyInput.value.trim();
    if (!key) return;

    savingApiKey.value = true;
    apiKeyInvalid.value = false;
    try {
        const projectId = currentService.value.id;
        const hadExistingKey = !!(
            aiApiKeyCache[projectId]?.[platform] ||
            serviceClientSecrets[projectId]?.list?.[platform]?.client_secret
        );
        await currentService.value.registerClientSecret({
            id: platform,
            key,
            // Tracks "Allow anonymous users"; see aiClientSecretGrp.
            grp: aiClientSecretGrp(currentService.value),
            req: hadExistingKey ? "update" : "create",
        });
        await currentService.value.setServiceOption({ ai_agent: platform });

        aiApiKeyCache[projectId] = {
            ...(aiApiKeyCache[projectId] || {}),
            [platform]: key,
        };

        // Also persist the key as a private record so we can recover the
        // last value on a fresh session without going through the
        // client-secret store.
        void saveAiApiKeyPrivately(projectId, platform, key);

        const pager = serviceClientSecrets[projectId];
        if (pager) {
            const mapped = {
                name: platform,
                client_secret: key,
                locked: true,
            };
            if (pager.list?.[platform]) {
                await pager.editItem(mapped);
            } else {
                await pager.insertItems([mapped]);
            }
        }

        apiKeyModalOpen.value = false;
        apiKeyInput.value = "";

        // The model list is static and key-independent, so it needs no reset —
        // but the KEY VERDICT this project+platform is holding describes the
        // OLD key and must be dropped, or a previously-invalid key would keep
        // the "invalid" overlay up over a good one.
        const svcForKey = currentService.value?.id;
        if (svcForKey) invalidateApiKeyVerdict(svcForKey, platform);
        apiKeyInvalid.value = false;

        // Re-run the gate so chatEnabled flips on and history loads.
        initialGateLoading.value = true;
        await refreshGate();
        // Re-probe with the new key. refreshGate's watcher only fires
        // loadAvailableModels when chatEnabled flips; if the key was
        // previously invalid (chatEnabled was already true) the watcher is a
        // no-op, so kick it off explicitly.
        void loadAvailableModels();
    } catch (err) {
        console.error("Failed to save API key", err);
    } finally {
        savingApiKey.value = false;
    }
};

const estimateTextTokens = (text: string) => {
    // Approximation: ~4 chars per token for English-like text.
    return Math.ceil((text || "").length / 3);
};

const estimateMessageTokens = (msg: { role: "user" | "assistant"; content: string }) => {
    // Includes lightweight per-message framing overhead.
    return estimateTextTokens(msg.content) + estimateTextTokens(msg.role) + 6;
};

// Cap how many prior messages we ever send back. The Messages API is stateless
// so every turn re-bills the full history as input tokens; bounding this hard
// keeps us under Anthropic's per-minute input limit regardless of content size.
const MAX_HISTORY_MESSAGES = 20;

// Hard cap on tokens spent on prior history (everything except the latest user
// turn). The latest message always goes through in full so a freshly-attached
// file or pasted context isn't truncated. Sized to comfortably hold ~2 medium
// attachments worth of prior context without re-billing huge bodies forever.
const HISTORY_TOKEN_BUDGET = 8000;

// --- Attachment token-budget estimation ----------------------------------
// We can't know exactly how many tokens an attachment will cost without
// actually fetching and tokenizing what `web_fetch` returns, so apply a
// per-file-type heuristic:
//   - Text/code/markup files: ~3 bytes per token (matches estimateTextTokens).
//   - PDFs: ~5 bytes per token of *extracted* text (PDFs compress structure,
//     extracted text is usually much smaller than the file).
//   - Images: a flat per-image cost approximating Claude's normal-resolution
//     image-token charge (file size doesn't matter; pixel count does, and
//     we can't read pixel dimensions cheaply from a File).
//   - Other binaries / unknown: not counted (web_fetch likely won't return
//     useful tokens for them).
const ESTIMATED_BYTES_PER_TOKEN = 3;
const ESTIMATED_PDF_BYTES_PER_TOKEN = 5000;
const ESTIMATED_IMAGE_TOKENS = 800;

// No eml here: an .eml is a server-parsed container like .docx (its bytes are mostly
// base64 attachment blobs that go to __MEDIA__ or are never inlined), and counting
// them at 3 bytes/token estimated a 380 KB mail with one photo at 127k tokens and
// blocked Send. Mirrored byte-for-byte in the widget (bunnyquery/src/index.js).
const TEXTLIKE_EXTENSION_RE =
    /\.(txt|md|markdown|rst|csv|tsv|json|jsonl|ndjson|ya?ml|xml|html?|css|less|scss|sass|js|mjs|cjs|ts|tsx|jsx|vue|svelte|astro|py|rb|go|rs|java|kt|swift|c|h|hpp|cpp|cc|cs|php|sh|bash|zsh|ps1|sql|log|conf|cfg|ini|toml|env|gitignore|dockerfile|makefile|lock)$/i;
const PDF_EXTENSION_RE = /\.pdf$/i;
const IMAGE_EXTENSION_RE = /\.(png|jpe?g|gif|webp|bmp|tiff?|heic|heif|avif|svg)$/i;

const estimateFileTokenCost = (file: File): number => {
    const name = file.name || "";
    const size = file.size || 0;
    const type = (file.type || "").toLowerCase();

    if (TEXTLIKE_EXTENSION_RE.test(name) || type.startsWith("text/") || type.includes("json") || type.includes("xml")) {
        return Math.ceil(size / ESTIMATED_BYTES_PER_TOKEN);
    }
    if (PDF_EXTENSION_RE.test(name) || type === "application/pdf") {
        return Math.ceil(size / ESTIMATED_PDF_BYTES_PER_TOKEN);
    }
    if (IMAGE_EXTENSION_RE.test(name) || type.startsWith("image/")) {
        return ESTIMATED_IMAGE_TOKENS;
    }
    // Unknown / opaque binary: don't count toward the budget. The model
    // probably can't read it via web_fetch anyway.
    return 0;
};

const estimateAttachmentTokenCost = (a: AgentAttachment): number => {
    if (a.kind === "file") return estimateFileTokenCost(a.file);
    let total = 0;
    for (const entry of a.files) total += estimateFileTokenCost(entry.file);
    return total;
};

const attachmentsTokenEstimate = computed(() => {
    let total = 0;
    // Composer chips only: the budget is per REQUEST, and an already-batched
    // chip is part of a request that has been sent.
    for (const a of composerAttachments.value) {
        total += estimateAttachmentTokenCost(a);
    }
    return total;
});

// Per-request input-token budget for the current platform/model. This used to
// be a local reimplementation over a local model table, and it drifted: the
// table never saw the per-project override or the windows reported by
// /v1/models, and its cap never scaled, so a project set to 1M warned at 28,000
// while the engine would have allowed roughly five times that. It now calls the
// same engine helper `buildBoundedChatMessages` uses, so the two cannot disagree.
const currentInputTokenBudget = computed(() => {
    const platform = selectedAiPlatform.value;
    if (platform !== "claude" && platform !== "openai") return 0;
    return getInputTokenBudget(
        platform,
        selectedAiModel.value,
        currentService.value?.service?.service,
    );
});

const attachmentsExceedBudget = computed(() => {
    const budget = currentInputTokenBudget.value;
    if (!budget) return false;
    return attachmentsTokenEstimate.value > budget;
});

const formatTokenCount = (tokens: number): string => {
    if (tokens >= 1000) {
        const k = tokens / 1000;
        return `${k >= 10 ? Math.round(k) : k.toFixed(1)}k`;
    }
    return String(tokens);
};

const attachmentsBudgetWarning = computed(() => {
    // The budget only matters when files are sent together with a chat message
    // (which bundles all their URLs into one prompt). Attachment-only sends
    // index files one-by-one, so don't warn — and clear the warning — when the
    // chat input is empty. Recomputes reactively as `prompt` changes.
    if (!prompt.value.trim()) return "";
    if (!attachmentsExceedBudget.value) return "";
    const est = formatTokenCount(attachmentsTokenEstimate.value);
    const cap = formatTokenCount(currentInputTokenBudget.value);
    return `Attachments are ~${est} tokens, which may exceed the ~${cap}-token per-request limit. Go to "Files" page to upload more files.`;
});

// Replace fenced file blocks from *older* assistant messages with a short
// reference line. A generated CSV/JSON in history can easily be thousands of
// tokens that get re-billed on every subsequent request even though the model
// rarely needs the full body again.
const stripFileBlocksFromHistory = (content: string): string => {
    if (!content) return content;
    return content.replace(
        /```([^\n`]+?\.[^\s.`]+)\n[\s\S]*?```/g,
        "[file previously attached: $1]",
    );
};

// Link/path primitives come from the SHARED engine (bunnyquery/engine ->
// engine/links.ts), never from a copy kept here. This file used to carry its own
// port of all twelve of them, and the cost was exact: every link bug had to be
// found and fixed twice, and the two copies were only ever as correct as the last
// person to remember the second one. The catastrophic-backtracking freeze and the
// whitespace-in-a-url dead chip were both fixed twice for that reason.
//
// The engine takes projectId as a PARAMETER (it is consumer-agnostic); the three
// wrappers below supply it from the live service so every call site here reads
// exactly as it did before.
const currentServiceId = () => currentService.value?.id || "";
const extractRemotePathFromAttachmentHref = (href: string): string | null =>
    engineExtractRemotePathFromAttachmentHref(href, currentServiceId());
const isServiceDbAttachmentHref = (href: string): boolean =>
    engineIsServiceDbAttachmentHref(href, currentServiceId());
const sanitizeAttachmentLinksForHistory = (content: string, forAssistant = false): string =>
    engineSanitizeAttachmentLinksForHistory(content, currentServiceId(), forAssistant);

const isExpiredAttachmentHref = (href: string) => {
    try {
        return new URL(href).hostname === EXPIRED_ATTACHMENT_URL_HOST;
    } catch {
        return false;
    }
};


// Cosmetic label for a background file-indexing bubble in the chat. Purely for
// display — the agent receives the full message built in @/code/ai_prompts, not
// this string. Both the live (in-progress) bubble and the one rebuilt from
// history route through here so they stay identical. Tweak the format here to
// change how indexing bubbles look (e.g. separator, which fields to show).
const formatIndexingLabel = (
    name: string,
    mime?: string,
    size?: number,
    storagePath?: string,
    reindex = false,
    // A CONTINUE pass of a large file. A big spreadsheet/PDF produces many passes, and
    // rendering the identical "Indexing: <file>" header for each reads like the same
    // task repeating. A continuation gets a compact, visibly different label so the run
    // reads as progress, not duplication.
    continued = false,
): string => {
    if (continued) {
        const nameLabel = storagePath ? `[${name}](${storagePath})` : name;
        return `Indexing (continuing) ${nameLabel}`;
    }
    // When the file's storage path is known, render the name as a *bare*
    // storage-path markdown link — the same `[filename](path/to/file)` form the
    // AI agent is told to emit (chat_system_prompt.ts). parseMsgParts routes a
    // bare-path link through buildLinkPartFromGroups' g4/g5 branch, which marks
    // it `expired` so a click fetches a fresh temporary URL. A full
    // `https://_expired_.url/…` href must NOT be used here: it hits the http-URL
    // branch instead, renders as a plain external link, and never refreshes.
    // Falls back to an unlinked name when the path is unknown.
    const nameLabel = storagePath
        ? `[${name}](${storagePath})`
        : name;
    const parts = [nameLabel];
    if (mime) parts.push(mime);
    if (typeof size === "number" && size > 0) parts.push(convertBytesToReadableStorage(size));
    // "Reindexing:" for files that already existed in storage (re-uploaded in
    // chat, or sent through dbfile.vue's explicit Reindex action); "Indexing:"
    // for first-time uploads.
    return `${reindex ? "Reindexing" : "Indexing"}: ${parts.join(" · ")}`;
};

// buildBoundedChatMessages (token-budget windowing) now lives in the shared
// engine; the send path reaches it inside chatSession.dispatchComposedMessage.
// (The composer's own budget guard calls the same engine getInputTokenBudget.)

const scrollToBottom = async (_smooth = false) => {
    // No smooth branch any more, in either scroller. A glide fires a scroll event
    // per frame at positions that are NOT the bottom, and onHistoryScroll clears
    // stickToBottom on each of them — so anything merging inside the ~130ms
    // animation strands the reader off the bottom permanently, aiming at a target
    // that was already stale when the glide began.
    //
    // A hidden tab does not run rAF, so the two-frame wait below would land one to
    // two frames after it comes back. Write now instead.
    if (typeof document !== "undefined" && document.hidden) {
        stickToBottom = true;
        chatScrollAnchor.pinBottom();
        return;
    }
    await nextTick();
    // Wait two animation frames so layout settles (CSS var height changes,
    // image loads, on-screen keyboard) before we compute scrollHeight. On
    // mobile the --agent-available-height CSS var is applied asynchronously
    // via rAF, so a single nextTick is not enough - the first scrollHeight
    // read can be stale and leave us short of the real bottom.
    await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
    if (messagesBox.value) {
        stickToBottom = true;
        chatScrollAnchor.pinBottom();
    }
};

// Whether the messages view is currently pinned to the bottom. Toggled by
// scrollToBottom (true) and by the user scrolling away (false). Used to
// re-pin after viewport height changes so a late --agent-available-height
// update doesn't leave the view short of the bottom on mobile.
let stickToBottom = true;

// Only scrolls if the user is already at the bottom. Used by automated
// resolutions (the streaming typewriter, bg-task polls, history polling) so
// they don't yank the user away when they've scrolled up to read old messages.
// Unlike scrollToBottom, this does NOT force-pin stickToBottom: it re-checks
// after the DOM settles and bails if the user scrolled up mid-tick, so a
// streamed response can't repeatedly drag them back down.
const scrollToBottomIfSticky = async (_smooth = false) => {
    if (!stickToBottom) return;
    // See scrollToBottom: never smooth, and never rAF-deferred while hidden.
    if (typeof document !== "undefined" && document.hidden) { chatScrollAnchor.pinBottom(); return; }
    await nextTick();
    if (!stickToBottom || !messagesBox.value) return;
    chatScrollAnchor.pinBottom();
};

// The ONE place that decides where the reader sits after a list refresh.
//
// Every refresh has two moments that change heights: the surface page landing, and
// the deferred background-indexing batch merging on top of it a network round trip
// later (getSplitChatHistory's headRefresh split). Both have to end with the reader
// where they were, and neither may leave a wrong position on screen in between —
// which is why this is called at the end of each, rather than once at the end.
//
// Pinned to the bottom means the BOTTOM, and specifically the bottom AFTER the
// batch has merged: re-pinning only on the surface page left the reader stranded
// above the newest turn by the height of everything the batch brought in.
// Anywhere else means the exact line they were on, which the anchor holds.
// Called at BOTH moments a first-page refresh changes heights: the surface page
// landing, and the deferred indexing batch merging a round trip later. It decides
// from the box AS IT IS at that moment and nothing else — a reader following the
// bottom gets the bottom that exists now (which after the batch is the real one),
// and any other reader gets the line they are on. There is deliberately no
// "returning" state here: an instruction recorded earlier and carried out at one
// of these settles is exactly how the chat threw people around.
const settleScrollAfterRefresh = () => {
    anchorWroteSinceScroll = true;
    if (stickToBottom) void scrollToBottomIfSticky(false);
    else chatScrollAnchor.hold();
};

const autoGrowInput = () => {
    if (!inputBox.value) {
        return;
    }
    // The composer growing SHRINKS the message box (it is flex:1, min-height:0),
    // which pushes a pinned reader off the bottom with no scroll event at all — so
    // stickToBottom stays true while the view no longer matches it, and the next
    // return honours that stale flag and jumps them. Re-pin in the same task as the
    // height write, and when the box gets TALLER re-run the fill: a box that stops
    // overflowing takes the scroll-to-top pager trigger away with it.
    const prevInputHeight = inputBox.value.offsetHeight;

    const minHeight = parseFloat(window.getComputedStyle(inputBox.value).minHeight) || 0;
    inputBox.value.style.height = "auto";
    inputBox.value.style.height = `${Math.max(inputBox.value.scrollHeight, minHeight)}px`;
    const nextInputHeight = inputBox.value.offsetHeight;
    // Deliberately NOT gated on the height having changed: the height:"auto" probe
    // above forces a layout at the COLLAPSED height, which makes the message box
    // taller, drops its maxScrollTop and clamps a pinned reader down — on every
    // keystroke, including the ones whose net height is unchanged.
    if (stickToBottom) chatScrollAnchor.pinBottom();
    // A TALLER box can stop overflowing, which takes the scroll-to-top pager trigger
    // away with it. Not an `else`: a pinned reader is exactly who ends up there,
    // having just cleared a long draft.
    if (nextInputHeight < prevInputHeight) void ensureHistoryFillsViewport();
};

const handlePromptCompositionEnd = async () => {
    isPromptComposing.value = false;
    await nextTick();
    autoGrowInput();
};

const handlePromptEnter = async (event: KeyboardEvent) => {
    if (event.isComposing || isPromptComposing.value || event.keyCode === 229) {
        return;
    }

    event.preventDefault();
    await nextTick();
    await sendMessage();
};

// Kept byte-compatible with the engine's getErrorMessage (bunnyquery/src/engine/errors.ts).
// Both are reachable for the SAME row inside this component: the mount path
// (loadChatHistory -> fetchHistoryPage) uses this fork, while resumePolling routes
// through the engine's loadHistory. When the two disagreed, switching browser tabs
// away and back silently rewrote an already-rendered error bubble's text. Branch
// order and the fallback string must match the engine, not just the behaviour.
const STATUS_MESSAGE: { [code: string]: string } = {
    "408": "The AI provider timed out before it started.",
    "409": "The AI provider rejected the request as conflicting.",
    "413": "The request was too large for the AI provider.",
    "429": "The AI provider is rate limiting requests right now.",
    "500": "The AI provider hit an internal error.",
    "502": "The AI provider is temporarily unreachable.",
    "503": "The AI provider is temporarily unavailable.",
    "504": "The AI provider timed out.",
};

const isTransientStatus = (status: number): boolean =>
    status === 408 || status === 425 || status === 429 || status >= 500;

const getErrorMessage = (err: any): string => {
    // One unwrap, first, exactly as the engine's errors.ts does it. A STREAMED
    // failure is the poller's status envelope with the provider payload nested
    // inside ({ id, status: 'failed', ..., error: { status_code, body } }), because
    // the chunks that arrived before the stream died have to ship in the same
    // response and so the poller cannot return the error early. Every branch below
    // reads the top level, where `error.message` is undefined and `status` is the
    // string 'failed' rather than a number, so without this a wrong API key read as
    // "not an error, and no answer either" and rendered as "No text response
    // received from AI provider".
    const envErr = csrEnvelopeError(err);
    if (envErr !== undefined) err = envErr;
    if (!err) return "Something went wrong.";
    if (typeof err === "string") return err;
    if (err.error && err.error.message) return err.error.message;
    if (err.body && err.body.error && err.body.error.message) return err.body.error.message;
    if (err.body && typeof err.body.message === "string") return err.body.message;
    if (err.message) return err.message;

    // A gateway failure in front of the model API answers with an HTML page, which
    // lands in `body` as a raw STRING, so every branch above misses it. Report the
    // status code instead of an unexplained 'Something went wrong.'.
    const status = typeof err.status_code === "number" ? err.status_code
        : typeof err.status === "number" ? err.status : 0;
    if (status) {
        const text = STATUS_MESSAGE[String(status)]
            || (status >= 500 ? "The AI provider returned a server error."
                : "The AI provider rejected the request.");
        return text + " (error " + status + ")"
            + (isTransientStatus(status) ? " This is usually temporary, please try again." : "");
    }
    return "Something went wrong.";
};

// True for any AI-provider response that should be surfaced as a red error
// bubble. Handles:
//   - bare shape `{type:'error', error:{...}}`
//   - proxy-wrapped shape `{body:{type:'error', error:{...}}, status_code:4xx}`
//   - AWS-style error JSON like `{message: "An error occurred (ValidationException) ..."}`
//     which the proxy returns as the response body when the upstream call fails
//     before any provider response can be produced. Such a body has no Claude
//     `content` array and no OpenAI `output` / `output_text` / `choices`.
const isErrorResponseBody = (response: any): boolean => {
    // Same one-level unwrap as getErrorMessage (engine mirror). A FAILED streamed
    // row is an error however empty its payload turned out to be: the status is the
    // fact, which is why the guard below returns `envErr !== undefined` rather than
    // false when the unwrapped payload is not an object.
    const envErr = csrEnvelopeError(response);
    if (envErr !== undefined) response = envErr;
    if (!response || typeof response !== "object") return envErr !== undefined;
    if (typeof response.status_code === "number" && response.status_code >= 400) return true;
    if (response.type === "error") return true;
    if (response.error && (response.error.message || response.error.type)) return true;
    const body = response.body;
    if (body && typeof body === "object") {
        if (body.type === "error") return true;
        if (body.error && (body.error.message || body.error.type)) return true;
    }
    // AWS/Lambda-style `{message: "..."}` with no recognizable success payload.
    if (typeof response.message === "string" && response.message.length) {
        const hasClaudeBody = Array.isArray(response.content);
        const hasOpenAIBody =
            typeof response.output_text === "string" ||
            Array.isArray(response.output) ||
            Array.isArray(response.choices);
        if (!hasClaudeBody && !hasOpenAIBody) return true;
    }
    return false;
};

// Detect MCP / upstream "your access token is no longer valid" failures so we
// can transparently force-refresh the skapi session and retry once. The
// `$ACCESS_TOKEN` placeholder in clientSecretRequest is substituted with the
// caller's skapi access token, which the MCP server validates via skapi-js's
// `loginWithToken`. When a queued request is processed after the captured
// token has expired (long poll, retry, etc.) the MCP call surfaces here as
// "Token has expired" / 401 / invalid_token.
const isAuthExpiredError = (input: any): boolean => {
    // A streamed turn whose bearer expired fails exactly like a buffered one, one
    // level deeper. Without this unwrap the refresh-and-resend gate never fires on
    // the streaming path, so an expiry that costs a buffered turn nothing costs a
    // streamed turn the whole answer. (Engine mirror.)
    const envErr = csrEnvelopeError(input);
    if (envErr !== undefined) input = envErr;
    if (!input) return false;
    const blobs: string[] = [];
    const push = (v: any) => { if (typeof v === "string" && v) blobs.push(v); };
    if (typeof input === "string") push(input);
    else {
        push(input.message);
        push(input.code);
        if (input.error) {
            push(input.error.message);
            push(input.error.code);
            push(input.error.type);
        }
        if (input.body) {
            push(input.body.message);
            if (input.body.error) {
                push(input.body.error.message);
                push(input.body.error.code);
                push(input.body.error.type);
            }
        }
        if (typeof input.status === "number" && input.status === 401) return true;
        if (typeof input.status_code === "number" && input.status_code === 401) return true;
    }
    const hay = blobs.join(" | ").toLowerCase();
    if (!hay) return false;
    return (
        hay.includes("token has expired") ||
        hay.includes("token is expired") ||
        hay.includes("expired_token") ||
        hay.includes("invalid_token") ||
        hay.includes("unauthorized") ||
        hay.includes("not authorized") ||
        (hay.includes("invalid_request") && hay.includes("token"))
    );
};

// A request that failed because the REQUEST ITSELF is malformed — an unknown or
// rejected parameter on a 400/422 — re-fails identically if resent unchanged; no
// session refresh fixes it. Detecting it lets the auth-retry gate refuse to
// auto-resend (otherwise a 400 whose param name merely contains "token", e.g.
// `max_output_tokens`, trips the `invalid_request + token` branch above and loops
// refresh->resend forever). Mirrors the shared engine's isNonRetryableRequestError.
const isNonRetryableRequestError = (input: any): boolean => {
    // Same one-level unwrap as the readers above: a streamed turn's failure is
    // wrapped in its poll envelope, and a retry gate that cannot see the payload
    // would re-send a request the provider has already rejected as malformed.
    // (Engine mirror.)
    const envErr = csrEnvelopeError(input);
    if (envErr !== undefined) input = envErr;
    if (!input || typeof input !== "object") return false;
    const status =
        typeof input.status_code === "number" ? input.status_code
        : typeof input.status === "number" ? input.status : undefined;
    let param: any = undefined;
    const blobs: string[] = [];
    const sources = [input.error, input.body && input.body.error, input.body, input];
    for (const e of sources) {
        if (!e) continue;
        if (typeof e === "string") { blobs.push(e); continue; }
        if (typeof e !== "object") continue;
        if (param === undefined && e.param != null) param = e.param;
        if (typeof e.code === "string") blobs.push(e.code);
        if (typeof e.type === "string") blobs.push(e.type);
        if (typeof e.message === "string") blobs.push(e.message);
    }
    const hay = blobs.join(" | ").toLowerCase();
    if (
        hay.includes("unknown_parameter") || hay.includes("unknown parameter") ||
        hay.includes("unsupported_parameter") || hay.includes("unsupported parameter")
    ) {
        return true;
    }
    const isClientReqStatus = status === 400 || status === 422;
    if (isClientReqStatus && param != null && param !== "") return true;
    if (
        isClientReqStatus && hay.includes("invalid_request") &&
        (hay.includes("parameter") || hay.includes("param"))
    ) {
        return true;
    }
    return false;
};

// Force the skapi session to refresh its access/id tokens, so the next
// `clientSecretRequest` (which substitutes `$ACCESS_TOKEN` from the live
// session) carries a fresh JWT. `getProfile({ refreshToken: true })` is the
// public skapi-js entrypoint that performs the Cognito refresh.
const refreshSkapiSession = async (): Promise<boolean> => {
    try {
        await (skapi as any).getProfile({ refreshToken: true });
        // Mirror the bunnyquery widget: an MCP 401 (a stale/cleaned server-side
        // session) is NOT fixed by a fresh skapi JWT alone, so silently
        // re-establish the MCP grant too (refresh_token flow, no redirect — the
        // engine resends right after this resolves).
        await ensureMcpGrantFresh();
        return true;
    } catch (err) {
        console.warn("[agent] refreshSkapiSession failed", err);
        return false;
    }
};

const debugAgentLoad = (
    stage: string,
    details: Record<string, unknown> = {}
) => {
    return; // comment out to enable debug logs
    console.debug("[agent]", stage, details);
};

const clearChatState = () => {
    messages.value = [];
    // The reader's place belongs to the conversation that is going away. Keeping
    // it would let a row key that happens to repeat in the next chat pull the
    // fresh list to an offset measured in the old one.
    chatScrollAnchor.forget();
    // Minted preview urls are keyed by project, and an identity-blind cache is
    // how one project's content has reached another project's chat before. One
    // component instance serves every project (a switch re-runs refreshGate, it
    // does not remount), so this has to be dropped here.
    clearImagePreviewCache(currentServiceId());
    // Same reason, and the same blast radius: a "this file has no url" mark is
    // keyed by a project-relative storage path, so keeping it across a switch
    // would grey out project B's chip for project A's dead file.
    unavailableLinkMap.value = {};
    pendingUnavailableKeys = [];   // a queued mark must not land in the next project's map
    historyEndOfList.value = false;
    historyStartKeyHistory.value = [];
    // Expansion is per-chat. A run key is a project-RELATIVE storage path
    // ("data.csv#0"), and one component instance serves every project (switching
    // projects re-runs refreshGate, it does not remount), so keeping the Set
    // across a switch rendered project B's same-named row pre-expanded. It also
    // grew without bound for the life of the page.
    expandedIndexGroups.value = new Set();
    // Stop each poll before dropping it. Clearing the map alone leaves the underlying
    // intervals running with nothing tracking them — they keep hitting the server and,
    // because polls are serialised one-at-a-time per queue, block everything behind them.
    chatSession.historyItemPolls.forEach((handle) => {
        try { handle?.stop?.(); } catch { /* best effort */ }
    });
    chatSession.historyItemPolls.clear();
};

// "Clear chat history" is a soft delete: we persist a `clearedAt` timestamp
// per (service, platform) in localStorage and filter any history entries with
// `updated <= clearedAt` out of fetched results. The server still has the
// records (skapi has no public delete API for clientSecretRequest history),
// but the UI and the model never see them again.
const CLEAR_HISTORY_STORAGE_PREFIX = "agent.clearedAt";

const showClearHistoryModal = ref(false);
const clearingHistory = ref(false);
// Bumped whenever the clear horizon moves — getClearedAt() reads localStorage,
// which Vue cannot track, so this is what re-runs the stub filter.
const clearHorizonTick = ref(0);

const getClearHistoryStorageKey = () => {
    const id = currentService.value?.id || "";
    if (!id || selectedAiPlatform.value === "none") return "";
    return `${CLEAR_HISTORY_STORAGE_PREFIX}:${id}#${selectedAiPlatform.value}`;
};

const getClearedAt = (): number => {
    const key = getClearHistoryStorageKey();
    if (!key) return 0;
    try {
        const raw = window.localStorage.getItem(key);
        const value = raw ? Number(raw) : 0;
        return Number.isFinite(value) && value > 0 ? value : 0;
    } catch {
        return 0;
    }
};

const setClearedAt = (timestamp: number) => {
    const key = getClearHistoryStorageKey();
    if (!key) return;
    try {
        window.localStorage.setItem(key, String(timestamp));
    } catch {
        /* localStorage may be unavailable (Safari private mode); ignore */
    }
};

// PollingResult.updated is a millisecond timestamp on each history entry. We
// only keep entries strictly newer than the horizon so a clear performed at
// time T also drops messages saved at T (within the same ms).
const filterListByClearHorizon = (list: any[]): any[] => {
    const clearedAt = getClearedAt();
    if (!clearedAt) return list;
    return list.filter((item) => {
        const updated = Number(item?.updated);
        return Number.isFinite(updated) && updated > clearedAt;
    });
};

const onClearHistoryClick = () => {
    if (!chatEnabled.value || sending.value || typing.value || uploadingAttachments.value) {
        return;
    }
    if (!messages.value.length) return;
    showClearHistoryModal.value = true;
};

const confirmClearHistory = async () => {
    if (clearingHistory.value) return;
    clearingHistory.value = true;
    try {
        // Use the most recent visible message timestamp if we can derive one,
        // otherwise just `Date.now()`. Date.now() is fine because new messages
        // sent *after* this point will have a later `updated` value.
        setClearedAt(Date.now());
        // localStorage is not reactive: bump the tick so chatDisplayRows
        // re-filters the run:: stubs against the new horizon immediately.
        clearHorizonTick.value++;

        // Wipe any in-memory cache so navigating away and back doesn't restore
        // the cleared messages from the per-tab cache.
        const cacheKey = getHistoryCacheKey();
        if (cacheKey) {
            delete aiChatHistoryCache[cacheKey];
        }

        messages.value = [];
        historyStartKeyHistory.value = [];
        // No older pages can ever pass the horizon, so we're at end-of-list.
        historyEndOfList.value = true;

        showClearHistoryModal.value = false;
    } finally {
        clearingHistory.value = false;
    }
};

// Delegated to the shared engine (keyed by projectId#platform). See chatHost.
const getHistoryCacheKey = () => chatSession.getHistoryCacheKey();

const normalizeTextContent = (content: any): string => {
    if (typeof content === "string") {
        return content;
    }

    if (Array.isArray(content)) {
        return content
            .map((part: any) => {
                if (typeof part === "string") {
                    return part;
                }

                if (part?.type === "text" || part?.type === "input_text" || part?.type === "output_text") {
                    return part?.text || "";
                }

                return "";
            })
            .join("\n")
            .trim();
    }

    return "";
};

const extractLastUserTextFromRequest = (requestBody: any) => {
    const messageArray = Array.isArray(requestBody?.messages)
        ? requestBody.messages
        : Array.isArray(requestBody?.input)
            ? requestBody.input
            : [];

    for (let i = messageArray.length - 1; i >= 0; i--) {
        const item = messageArray[i];
        if (item?.role === "user") {
            const text = normalizeTextContent(item?.content);
            if (text) {
                return text;
            }
        }
    }

    return "";
};

// Items tagged with _isBgTask come from the background-indexing queue.
// Items tagged with _isOnBgQueue are regular chats submitted on the bg queue
// (deferred after indexing) — they display as normal chat but cancel on the bg queue.
type TaggedHistoryItem = any & { _isBgTask?: boolean; _isOnBgQueue?: boolean };

const mapHistoryListToMessages = (list: TaggedHistoryItem[], platform: "claude" | "openai", ownerKey?: string) => {
    const mapped: ChatMessage[] = [];
    const runningItemIds: string[] = [];
    const streamPendingItemIds: string[] = [];
    const extractAssistantText = platform === "openai" ? extractOpenAIText : extractClaudeText;
    // See the `isStreamPending` block below. Read ONCE per call rather than per
    // item: it is a config lookup, and the answer cannot change mid-list.
    const canRecoverStreams = streamRecoveryEnabled();
    const filtered = filterListByClearHorizon(list);

    for (const item of [...filtered].reverse()) {
        // skapi.clientSecretRequestHistory remaps the raw entry into a
        // PollingResult: { request_body, response_body, error, status,
        // status_code, updated, expires }.
        const requestBody = item?.request_body;
        // "running" = worker has claimed the item and is calling the upstream provider.
        // "pending" = item is queued behind a running one.
        const isInProcess = item?.status === "running";
        const isQueued = item?.status === "pending";
        const isCancelledItem = item?.status === "cancelled";
        const isPending = isInProcess || isQueued;
        const isFailed = item?.status === "failed";
        const response = isFailed
            ? (item?.error ?? item?.response_body)
            : (item?.response_body ?? item?.error);
        // Compact bg-queue stubs (split fetch): label/head/marker arrive as
        // dedicated fields, bodies never left the server. Mirrors the engine
        // mapper's compact branch exactly.
        const isCompact = !!item?.compact;
        const userText = isCompact
            ? (typeof item.request_text === "string" ? item.request_text : "")
            : extractLastUserTextFromRequest(requestBody);
        let assistantText = isPending
            ? ""
            : (isCompact
                ? (typeof item.response_text === "string" ? item.response_text : "").trim()
                : extractAssistantText(response)?.trim() || "");
        const isErrorResponse = !isPending && (isFailed || (!isCompact && isErrorResponseBody(response)));
        // AN ANSWER THAT IS UNKNOWN, NOT EMPTY. Engine mirror (history.ts) - the
        // predicate and the branch below must stay identical to it, because BOTH
        // mappers run against the same rows inside this component (this fork on the
        // mount path, the engine's through resumePolling), and a disagreement means
        // one path renders the answer and the other erases it.
        //
        // A STREAMED turn stores nothing on its row: the relay appends the
        // destination's bytes to the chunk table and the row settles with a status
        // and no body, and only csr-finalize ever copies an answer onto the row. So a
        // row that is terminal, carries no body and carries no error is not a turn
        // that answered nothing: it is a turn nobody finalized. That happens for one
        // ordinary reason - the row settled while no poll was attached (the tab was
        // closed, a mobile browser discarded it, the device slept and the interval
        // stopped), so the client that would have finalized it was not there.
        //
        // Read as "empty" (which is what the `assistantText` guard further down did),
        // such a row produces NO assistant bubble at all and the answer is simply
        // gone from the conversation, with every byte of it still sitting in the
        // chunk table. So it is marked instead, and two things act on the mark: the
        // merge (an unknown answer never overwrites a known one - see
        // chatSession.adoptLocalAnswers) and the recovery (an unknown answer is
        // resolved by reading the chunks back - chatSession.scheduleStreamRecovery).
        //
        // Deliberately narrow, so nothing that is genuinely an empty turn is caught:
        //   - only when this host streams AND can read chunks back (see
        //     streamRecoveryEnabled); a host that does neither behaves exactly as it
        //     does today, and one that cannot read them back would only trade a
        //     missing bubble for a permanently empty one;
        //   - never on the background queue. chatStreamWiring turns streaming OFF for
        //     every bg-queue turn, so a bg row with no body really did answer nothing;
        //   - never on a compact stub, whose body was withheld by the server rather
        //     than never stored;
        //   - 'resolved' only. A FAILED row already renders its error, which is the
        //     authoritative account of the turn. Its chunks are deliberately kept but
        //     not rendered.
        const isStreamPending = canRecoverStreams && !isCompact && !isPending && !isCancelledItem
            && !isErrorResponse && !item._isBgTask && !item._isOnBgQueue
            && item?.status === "resolved"
            && (item?.response_body == null) && (item?.error == null)
            && !assistantText;
        // Record the completion marker, then STRIP it — both, and in that order.
        // Mirrors the engine's history.ts and its live resolution path: the display
        // layer needs it as a structured fact, and leaving it in the text rendered
        // the literal token inside the bubble on every history load.
        //
        // Gated on _isBgTask: only an INDEXING pass has a protocol token to hide. An
        // ordinary reply that merely mentions it keeps its own words.
        const reportedComplete = !!item._isBgTask && !isErrorResponse && (isCompact
            ? item.response_complete_marker === true
            : (!!assistantText && assistantText.indexOf(INDEXING_COMPLETE_MARKER) !== -1));
        if (reportedComplete) {
            assistantText = assistantText.split(INDEXING_COMPLETE_MARKER).join("").trim();
        }
        const serverItemId = typeof item?.id === "string" && item.id
            ? item.id
            : undefined;
        // A USER bubble shows the request time (`created`); an ASSISTANT bubble the
        // response time (`updated`). Mirrors the engine mapper. Fall back to the
        // other when one is missing (older records only had `updated`).
        const createdTs = Number(item?.created);
        const updatedTs = Number(item?.updated);
        const userTs = isFinite(createdTs) && createdTs > 0 ? createdTs : (isFinite(updatedTs) && updatedTs > 0 ? updatedTs : undefined);
        const replyTs = isFinite(updatedTs) && updatedTs > 0 ? updatedTs : (isFinite(createdTs) && createdTs > 0 ? createdTs : undefined);
        // When the WORKER began executing this pass, not when it was enqueued. The
        // SDK normalises it to ms (the worker stamps `att` in seconds). Absent on a
        // row that never ran and on rows written before the SDK projected it; in both
        // cases the pass simply shows no duration, which is the honest answer.
        //
        // THIS MAPPER IS A FORK of the engine's mapHistoryListToMessages, and it is
        // the one that runs when you ENTER a chat. The engine's runs on a tab return
        // (resumePolling). So omitting `_tsStart` here did not hide the duration, it
        // DELAYED it: nothing on entry, then it appeared the moment you looked away
        // and came back. Keep the two mappers in step -- tests/pass-duration-live.cjs
        // asserts both branches below.
        const executedTs = Number((item as any)?.executed);
        // Structured file ref for the display layer's per-file collapsing, kept
        // alongside the formatted label so grouping never has to parse it back.
        // Declared out here, not inside the `if (userText)` block, because the reply
        // branches below need it to tell an indexing pass from an ordinary turn.
        let indexFile: ChatMessage["_indexFile"];

        if (userText) {
            // Bg-task user messages contain the full AI indexing prompt; extract
            // the file metadata so the bubble matches the synthetic drainBgTaskQueue label.
            let displayContent: string;
            if (item._isBgTask) {
                const nameMatch = userText.match(/^- name: (.+)$/m);
                if (nameMatch) {
                    const mimeMatch = userText.match(/^- mime type: (.+)$/m);
                    const sizeMatch = userText.match(/^- size \(bytes\): (\d+)$/m);
                    const pathMatch = userText.match(/^- storage path: (.+)$/m);
                    // A CONTINUE pass ("CONTINUE indexing ...") gets the compact
                    // continuation label; a first pass ("A new file ...") gets the full one.
                    const isContinuePass = userText.startsWith('CONTINUE indexing');
                    displayContent = formatIndexingLabel(
                        nameMatch[1].trim(),
                        mimeMatch ? mimeMatch[1].trim() : undefined,
                        sizeMatch ? Number(sizeMatch[1]) : undefined,
                        pathMatch ? pathMatch[1].trim() : undefined,
                        false,
                        isContinuePass,
                    );
                    indexFile = {
                        name: nameMatch[1].trim(),
                        path: pathMatch ? pathMatch[1].trim() : undefined,
                        mime: mimeMatch ? mimeMatch[1].trim() : undefined,
                        size: sizeMatch ? Number(sizeMatch[1]) : undefined,
                        continued: isContinuePass,
                    };
                } else {
                    displayContent = userText;
                }
            } else {
                displayContent = sanitizeAttachmentLinksForHistory(userText);
            }
            const userMsg: ChatMessage = {
                role: "user",
                content: displayContent,
            };
            if (isInProcess) userMsg.isPendingInProcess = true;
            if (isQueued) userMsg.isPendingQueued = true;
            if (isCancelledItem) userMsg.isCancelled = true;
            if (isCompact) userMsg._compact = true;
            if ((item as any)._fromBgChain) (userMsg as any)._fromBgChain = true;
            if (item._isBgTask) userMsg.isBackgroundTask = true;
            if (indexFile) userMsg._indexFile = indexFile;
            if (item._isOnBgQueue) userMsg._useBgQueue = true;
            if (serverItemId !== undefined) userMsg._serverItemId = serverItemId;
            if (userTs !== undefined) userMsg._ts = userTs;
            mapped.push(userMsg);
        }

        if (isCancelledItem) {
            // No assistant message for cancelled items — user bubble already shows as cancelled.
        } else if (isInProcess) {
            const placeholder: ChatMessage = {
                role: "assistant",
                content: "",
                isPending: true,
                isPendingInProcess: true,
            };
            // _ts matters even on a placeholder: merges/display must be able
            // to place it without its user bubble (mirror of the engine).
            if (userTs !== undefined) (placeholder as any)._ts = userTs;
            if ((item as any)._fromBgChain) (placeholder as any)._fromBgChain = true;
            if (item._isBgTask) placeholder.isBackgroundTask = true;
            if (serverItemId !== undefined) {
                placeholder._serverItemId = serverItemId;
                runningItemIds.push(serverItemId);
            }
            mapped.push(placeholder);
        } else if (isQueued) {
            // No assistant placeholder for queued items — the user-message
            // bubble alone (with "(In queue)" small label) communicates state.
        } else if (isErrorResponse) {
            const errMsg: ChatMessage = {
                role: "assistant",
                content: getErrorMessage(response),
                isError: true,
            };
            if ((item as any)._fromBgChain) (errMsg as any)._fromBgChain = true;
            if (item._isBgTask) errMsg.isBackgroundTask = true;
            if (serverItemId !== undefined) errMsg._serverItemId = serverItemId;
            if (replyTs !== undefined) errMsg._ts = replyTs;
            // A pass that FAILED still ran for a while, and how long it ran before it
            // failed is exactly what you want to know. Same rule as the ok branch.
            if (indexFile && isFinite(executedTs) && executedTs > 0) errMsg._tsStart = executedTs;
            mapped.push(errMsg);
        } else if (isStreamPending) {
            // The bubble stands for the turn so the merge has something to key on and
            // the recovery has somewhere to write. Its content is UNKNOWN: empty here
            // is the absence of an answer on the row, not the absence of an answer.
            const sp: ChatMessage = { role: "assistant", content: "", _streamPending: true };
            if (serverItemId !== undefined) { sp._serverItemId = serverItemId; streamPendingItemIds.push(serverItemId); }
            if (replyTs !== undefined) sp._ts = replyTs;
            mapped.push(sp);
        // `|| reportedComplete`: a pass whose ENTIRE answer was the completion token
        // strips down to an empty string, and the plain `assistantText` guard then
        // emitted no bubble at all — while the live path emitted one. The run read as
        // finished live and unfinished after a reload, so the row's loader came back.
        } else if (assistantText || reportedComplete) {
            // Safe db-only sanitize (forAssistant) so a volatile db url the model
            // emitted renders as a re-mintable `_expired_.url` link, not a dead one.
            const okMsg: ChatMessage = { role: "assistant", content: sanitizeAttachmentLinksForHistory(assistantText, true) || EMPTY_INDEXING_REPLY };
            if ((item as any)._fromBgChain) (okMsg as any)._fromBgChain = true;
            if (item._isBgTask) okMsg.isBackgroundTask = true;
            if (isCompact) okMsg._compact = true;
            if (serverItemId !== undefined) okMsg._serverItemId = serverItemId;
            if (replyTs !== undefined) okMsg._ts = replyTs;
            if (reportedComplete) okMsg._indexComplete = true;
            // Only for an indexing pass: `_tsStart`'s presence is what the view reads
            // to decide whether to draw a duration at all, so stamping an ordinary
            // turn would put a bracketed time on every chat reply.
            if (indexFile && isFinite(executedTs) && executedTs > 0) okMsg._tsStart = executedTs;
            mapped.push(okMsg);
        }
    }

    // Stamp the CHAT every bubble belongs to (engine mirror). Every
    // cross-project guard reads `_ownerKey` and treats undefined as "unknown,
    // keep it" — and this fork never set it, so SERVER-history bubbles passed
    // every one of those filters unchallenged, which is how one project's
    // transcript survived into another and got persisted under its key.
    // The key is passed in (the FETCH's own project+platform), never read live:
    // a switch racing the response would otherwise stamp the new chat's identity
    // onto the old chat's bubbles, which is the leak this exists to stop.
    if (ownerKey) {
        for (const m of mapped) (m as any)._ownerKey = ownerKey;
    }
    // `streamPendingItemIds` is additive and reporting-only, kept so this fork and
    // the engine's mapper return the same shape and can be diffed. The recovery is
    // driven off the BUBBLES, not off this list, because the merge runs between the
    // two and can adopt a local answer onto one of them.
    return { messages: mapped, runningItemIds, streamPendingItemIds };
};

// Delegated to the shared engine (writes the same aiChatHistoryCache store,
// re-pointed onto chatSession in setup). See chatHost.
const updateHistoryCache = () => chatSession.updateHistoryCache();

// Resolves a running/pending history item (replaces the placeholder bubble +
// triggers the typewriter). Delegated to the shared engine; the trailing
// cacheKey arg from agent.vue's callers is ignored (the engine resolves by
// _serverItemId against the current cache). See chatHost.
const handleHistoryItemResolution = (
    itemId: string,
    response: any,
    platform: "claude" | "openai",
    _cacheKey?: string,
    executedAt?: number,
) => {
    // Every LIVE provider answer passes through here, which makes it the one
    // place that can honestly judge the project's API key. Only an answer the
    // provider tagged as a key problem sets the verdict (isProviderApiKeyError
    // matches `authentication_error` / `invalid_api_key` / "incorrect api key",
    // never a bare 401 — our own MCP bearer expiring is a 401 too and is
    // refreshed automatically). A timeout, a 429 or a 502 leaves it alone, which
    // is exactly what the old model-listing probe got wrong.
    //
    // History REBUILDS deliberately do not feed this: an error sitting in old
    // history may predate a key the user has already fixed.
    const projectId = currentService.value?.id;
    if (projectId && (platform === "claude" || platform === "openai")) {
        const changed = isProviderApiKeyError(response)
            ? reportProviderError(projectId, platform, true)
            : (response && !isErrorResponseBody(response) ? reportProviderSuccess(projectId, platform) : false);
        if (changed) syncApiKeyVerdict();
    }
    return chatSession.handleHistoryItemResolution(itemId, response, platform, executedAt);
};

// When a running bg-task resolves, promote the next queued bg-task to running.
// Delegated to the shared engine. See chatHost.
const promoteNextBgQueuedToRunning = () => chatSession.promoteNextBgQueuedToRunning();

// Inject Thinking... bubbles for bg-indexing tasks that have been registered
// in bgTaskQueue but are not yet visible in messages. Called:
//   1. After fetchHistoryPage replaces messages (to re-inject any not in history yet)
//   2. When a new bgTaskQueue entry arrives while agent.vue is mounted
//
// For items already in messages (history fetched them): skip injection.
// For items not yet in messages: inject placeholder user bubble (and Thinking…
//   for running items) and attach the CSR poll so the response resolves without
//   needing another fetchHistoryPage round-trip.
// Inject "Indexing: <file>" bubbles for queued bg tasks + attach their polls.
// Delegated to the shared engine (reads identity/mounted/label via chatHost,
// drives the shared bgTaskQueue + historyItemPolls). See chatHost.
const drainBgTaskQueue = () => chatSession.drainBgTaskQueue();

// When the background-indexing queue drains for a service — its last task
// finished by success, error, OR cancel — the records produced by indexing now
// exist, so tell records.vue to refresh. Watching the projectId list (rather
// than bumping at each individual splice point) means no drain path is missed;
// an all-cancelled batch just causes a harmless extra refresh.
const servicesWithBgTasks = new Set<string>();
// The scan is O(queue), so it is keyed on the queue LENGTH (O(1)) and coalesced
// rather than run per mutation. The old key joined EVERY entry's projectId, which
// rebuilt an n-element string on every push and every removal: a bulk upload
// pushes one entry per uploaded file, making this O(n²) over the batch. A drain
// only nudges records.vue to refresh, so a short coalescing window costs nothing.
let bgServiceScanTimer: ReturnType<typeof setTimeout> | null = null;
const scanServicesWithBgTasks = () => {
    bgServiceScanTimer = null;
    const present = new Set<string>();
    for (const e of bgTaskQueue) present.add(e.projectId);
    for (const sid of servicesWithBgTasks) {
        if (!present.has(sid)) {
            recordsRefreshTrigger[sid] = (recordsRefreshTrigger[sid] || 0) + 1;
        }
    }
    servicesWithBgTasks.clear();
    present.forEach((sid) => servicesWithBgTasks.add(sid));
};
watch(
    () => bgTaskQueue.length,
    () => {
        if (bgServiceScanTimer !== null) return; // a scan is already pending
        bgServiceScanTimer = setTimeout(scanServicesWithBgTasks, 250);
    },
    { immediate: true }
);

// When the currently-running message resolves, upgrade the next queued user
// bubble to "running" (+ a "Thinking..." placeholder). Delegated to the shared
// engine. See chatHost.
const promoteNextQueuedToRunning = () => chatSession.promoteNextQueuedToRunning();

// (Queued-send response/error handling lives entirely in the engine now — its
// dispatchComposedMessage queued branch calls chatSession.onQueuedSend* directly,
// so agent.vue no longer needs local wrappers.)

// Cancel a queued/running message via cancelClientSecretRequest, then revert the
// bubble + advance the queue. Delegated to the shared engine (cancel goes through
// chatHost.cancelRequest → skapi.cancelClientSecretRequest). See chatHost.
const cancelQueuedMessage = (msg: ChatMessage, idx: number) => chatSession.cancelQueuedMessage(msg, idx);

// THE READER ASKING FOR AN ANSWER NOBODY WENT BACK FOR. A streamed turn that was
// never finalized keeps its bytes in the chunk store; automatic recovery is capped
// per history load (newest first) and gives up on a read that fails, so the rest
// are marked, unattended, and only reachable through this. Delegated, not
// reimplemented: the engine's own path pins the finalize to the right chat and
// refuses a second read while one is in flight.
//
// Fire-and-forget by design. Every outcome is already handled inside the engine
// and lands on the bubble (the answer, or the failure this button will offer to
// retry), so there is nothing here to await and nothing to catch: the one thing
// this must not do is throw out of a template click handler.
const recoverStreamedAnswer = (itemId?: string) => {
    if (!itemId) return;
    void chatSession.recoverStreamedAnswer(itemId);
};

const fetchHistoryPage = async (
    fetchMore = false,
    token = chatSession.state.gateRefreshToken,
    // refreshGate's SPECULATIVE history load runs while the API-key lookup is
    // still in flight, so chatEnabled (hasAiPlatform && hasAiApiKey) is not yet
    // meaningful — without this the speculative call silently bailed here, its
    // promise resolved empty, and the gate cleared onto an EMPTY chat on first
    // visit. Platform presence is known synchronously and stays required.
    assumeChatEnabled = false,
) => {
    if (
        (loadingHistory.value && chatSession.state.historyRequestToken === token) ||
        (assumeChatEnabled ? !hasAiPlatform.value : !chatEnabled.value) ||
        selectedAiPlatform.value === "none" ||
        !currentService.value
    ) {
        return;
    }

    chatSession.state.historyRequestToken = token;
    loadingHistory.value = true;
    if (fetchMore) {
        loadingOlderHistory.value = true;
    }
    const platform = selectedAiPlatform.value as "claude" | "openai";
    let projectId = currentService.value.id;
    let owner = currentService.value.owner;

    // Re-resolve the active project before dispatch to guard against stale
    // in-memory context (e.g. account switched in another tab). Only do this
    // on the *initial* first-page load — `fetchMore` (scroll-up pagination)
    // reuses the project context the first-page fetch already validated.
    if (!fetchMore) {
        const clearLoadingFlags = () => {
            if (chatSession.state.historyRequestToken === token) {
                loadingHistory.value = false;
                loadingOlderHistory.value = false;
            }
        };
        try {
            const resolved = await pullServices({ service_id_to_fetch: projectId });
            if (!resolved) {
                messages.value.push({
                    role: "assistant",
                    content: "Project is no longer reachable in your current session. Please re-open the project from My Projects and try again.",
                    isError: true,
                });
                updateHistoryCache();
                sending.value = false;
                clearLoadingFlags();
                return;
            }
            currentService.value = resolved;
            projectId = currentService.value.id;
            owner = currentService.value.owner;
            if (!projectId || !owner) {
                messages.value.push({
                    role: "assistant",
                    content: "Project context is invalid in this session. Please reopen the project and try again.",
                    isError: true,
                });
                updateHistoryCache();
                sending.value = false;
                clearLoadingFlags();
                return;
            }
        } catch (_resolveErr) {
            messages.value.push({
                role: "assistant",
                content: "Failed to validate current project access before querying data. Please refresh and try again.",
                isError: true,
            });
            updateHistoryCache();
            sending.value = false;
            clearLoadingFlags();
            return;
        }
    }

    try {
        // Identity of the CHAT this fetch belongs to, resolved once from the
        // fetch's own projectId/platform and never from a live currentService
        // read (which has already flipped when a switch races the response).
        // Declared here, at the top of the whole try, because BOTH the merge
        // branches and the deferred-batch handler below need it — it used to
        // live inside the replace branch, so the batch handler's reference
        // threw ReferenceError and killed the only path that releases a kept
        // screen (leaving the loading flag pinned and the greeting suppressed).
        // THE ONE SPELLING OF THIS CHAT'S IDENTITY. There used to be two - this
        // fork's own `projectId#platform` and the engine's `projectId#platform#userId`
        // (chatCacheKey) - and every bubble in `messages.value` is stamped with one or
        // the other depending on who created it: this fork's mapper stamped the short
        // one onto all of server history, while every bubble the engine makes (an
        // immediate send, a queued send, a staged turn, a recovered answer) carries
        // the long one. Comparing a mixed list against either spelling therefore
        // answers "does this bubble belong to this chat?" with NO for half of it:
        //
        //   * adoptLocalAnswers refused the fork's own bubbles as donors, so an
        //     answer this fork had already recovered onto the screen was dropped
        //     again on the next refetch of a row that is still unfinalized - and
        //     dropped into a bubble the automatic recovery would not re-read, because
        //     it had already read that row once. A permanently empty bubble over an
        //     answer that is still sitting in the chunk store.
        //   * shouldRescueInFlightMessage was handed the short key while every
        //     in-flight bubble carries the long one, so the ownership guard - which
        //     exists to stop ANOTHER project's turn crossing over - disqualified this
        //     project's own staged and in-flight turns from being rescued across a
        //     first-page refetch.
        //   * refreshGate's cache restore already compared against getHistoryCacheKey
        //     (long), so every server-history bubble this fork had stamped was
        //     filtered out of the restored transcript.
        //
        // The identity segment is the finer of the two and the leak guard still holds
        // (the project is its first segment), so the fork's spelling is simply gone.
        // Built from the load's OWN snapshot, and from the same expression chatHost's
        // getIdentity uses, so a project switch racing this response cannot make this
        // and getHistoryCacheKey() disagree.
        const engineLoadKey = chatCacheKey(projectId, platform, (skapi as any).user?.user_id || projectId || "");
        const options: Record<string, any> = { fetchMore };
        // First page: paint the CONVERSATION immediately; the indexing stubs
        // arrive as a deferred batch and merge in below when they land.
        if (!fetchMore) options.deferBg = true;

        if (fetchMore && historyStartKeyHistory.value.length) {
            options.startKeyHistory = [...historyStartKeyHistory.value];
        }

        debugAgentLoad("getChatHistory start", { token, fetchMore, projectId, platform });

        // Split fetch v2: surface (full bodies, bg queue excluded) + bg compact
        // stubs, tiled to the surface page's window with engine-side state
        // (buffering + retry safety). Single-fetch shape; startKeyHistory is
        // bookkeeping only.
        const fetchHistory = () => getSplitChatHistory(
            {
                service: projectId,
                owner,
                platform,
                userId: user?.user_id,
            },
            options
        );

        let history: any;
        try {
            history = await fetchHistory();
        } catch (err: any) {
            // A malformed-request error (the `_skapi_extract`/unknown-parameter 400
            // class) re-fails identically, so never refresh+retry on it — guards the
            // auth heuristic from misfiring on a param name containing "token".
            if (isAuthExpiredError(err) && !isNonRetryableRequestError(err)) {
                await refreshSkapiSession();
                history = await fetchHistory();
            } else {
                throw err;
            }
        }

        if (token !== chatSession.state.gateRefreshToken) {
            return;
        }

        const chatList: any[] = Array.isArray(history?.list) ? history.list : [];

        // Tag bg-indexing items so mapHistoryListToMessages can mark them isBackgroundTask.
        // queue_name arrives in two shapes depending on which endpoint produced it: history
        // listings give the plain queue name, while a single-item poll used to hand back the
        // raw internal qid ("<service>:<queue>|<seq>"). isBgIndexingQueue parses the queue
        // segment out of either, so this keeps working against older backends that still
        // return the qid form.
        for (const item of chatList) {
            if (isBgIndexingQueue(item.queue_name)) {
                // Compact stubs carry the label as request_text — the body never
                // left the server — so classify from whichever form arrived.
                const userText = item.compact ? item.request_text : extractLastUserTextFromRequest(item.request_body);
                // A first pass ("A new file...") AND every CONTINUE pass (PDF page loop or
                // text/grid window loop) are indexing tasks and must render as the
                // "Indexing: filename" bubble. Matching only the first-pass header left
                // every continuation pass falling to _isOnBgQueue, which renders the raw
                // prompt (the "CONTINUE indexing ... {{SKAPI_WINDOW::...}}" text) as a chat
                // bubble. Mirror of the engine classifier in session.ts.
                if (typeof userText === 'string'
                    && (userText.startsWith('A new file has just been uploaded')
                        || userText.startsWith('CONTINUE indexing'))) {
                    // Background file-indexing task — display as "Indexing: filename".
                    item._isBgTask = true;
                } else {
                    // Regular chat deferred onto the bg queue — display as normal
                    // chat but cancel on the bg queue (not the main userId queue).
                    item._isOnBgQueue = true;
                }
            }
        }

        const list = chatList.sort((a: any, b: any) => {
            const ai = typeof a.id === "string" ? a.id : "";
            const bi = typeof b.id === "string" ? b.id : "";
            if (ai > bi) return -1;
            if (ai < bi) return 1;
            return 0;
        });

        const { messages: mapped } = mapHistoryListToMessages(list, platform, engineLoadKey);
        // Re-apply previously-hydrated compact bodies (engine memo): a refresh
        // maps fresh stubs, and an expanded row's replies must never revert to
        // their 200-char heads.
        chatSession.applyHydratedBodies(mapped as any);
        // AN UNKNOWN ANSWER NEVER OVERWRITES A KNOWN ONE. Before either merge below,
        // any page bubble whose row is terminal-but-empty takes the answer the local
        // list already holds for that turn (and its live state, if the turn is still
        // running). Without this, a refetch landing in the window between a streamed
        // row resolving and its finalize storing the body erases the answer from the
        // screen, on every streamed turn, since a refetch fires on visibilitychange.
        //
        // Delegated rather than reimplemented here even though everything else in
        // this block is a fork: the rule needs the session's own record of which
        // streams ended WITHOUT a terminal event, which no pure helper can see, and a
        // fork that adopted a truncated answer over the row would clear the very
        // marker that would have gone back for the rest. Same position as the
        // engine's loadHistory: after applyHydratedBodies, before the merge.
        //
        // THE DONOR LIST HAS TO BE THE WHOLE LIST. The ownership guard inside compares
        // against the LOCAL bubbles' _ownerKey, and the note this replaces claimed a
        // bubble stamped by this fork could be skipped harmlessly because "it either
        // has no text to donate, or it has text, which means its row had a body, which
        // means the fresh page's copy is not marked at all". That last step is false:
        // a bubble can hold text because it ADOPTED it, or because a recovery wrote a
        // recovered answer into it, while its row is still unfinalized and still maps
        // to a marked-and-empty copy. Skipping that donor threw the answer off the
        // screen on the next refetch, into a bubble the automatic recovery would not
        // re-read (it had already read that row once) - empty forever, over bytes
        // still sitting in the chunk store. The fork's second spelling is gone, so
        // every local bubble is now stamped in the one shape this compares against.
        chatSession.adoptLocalAnswers(mapped as any, engineLoadKey);

        // Set when a first-page refresh re-prepended already-loaded older pages,
        // so the pager reset below knows not to rewind to page 1's boundary.
        let keptOlderPages = false;
        // See the empty-page-1 keep below: held only on the deferred batch's
        // word, and the batch handler lets it go when the chat is proven empty.
        let keptScreenAwaitingBg = false;

        if (fetchMore) {
            // Prepend-dedup by server item id: a first-page refresh rewinds the
            // SDK's cursor chain while retainedOlder keeps deeper pages on
            // screen, so an older-page fetch can return a page that is ALREADY
            // displayed — prepending it raw duplicated (and misordered) the
            // transcript. Id-less bubbles (staged/local) pass through untouched.
            // Mirror of the engine's loadHistory guard.
            // STRIP-THEN-MERGE keyed on SERVER ITEM ID (mirror of the engine's
            // loadHistory): incoming bubbles are the fresher server state, so
            // existing copies of incoming id|role bubbles are removed first
            // (cancel flags carried), then the page merges by item id — ids
            // are creation-ordered and both lists are id-sorted, unlike _ts
            // (assistant _ts is `updated`; long-running turns break it).
            const incomingKeys = new Map<string, any>();
            for (const m of mapped as any[]) { if (m._serverItemId) incomingKeys.set(m._serverItemId + "|" + m.role, m); }
            const existing = (messages.value as any[]).filter((m) => {
                if (!m._serverItemId) return true;
                const inc = incomingKeys.get(m._serverItemId + "|" + m.role);
                if (!inc) return true;
                if (m._cancelling) inc._cancelling = m._cancelling;
                if (m._cancelError) inc._cancelError = m._cancelError;
                return false;
            });
            const mergedList: any[] = [];
            let pi = 0, ei = 0;
            while (pi < mapped.length && ei < existing.length) {
                const pm: any = mapped[pi], em: any = existing[ei];
                const eid = em._serverItemId;
                if (typeof eid !== "string") break; // staged/local tail
                const pid = pm._serverItemId;
                if (typeof pid !== "string" || pid <= eid) { mergedList.push(pm); pi++; }
                else { mergedList.push(em); ei++; }
            }
            while (pi < mapped.length) mergedList.push(mapped[pi++]);
            while (ei < existing.length) mergedList.push(existing[ei++]);
            messages.value = mergedList;
        } else if (!mapped.length && (history?.endOfList === false || (history as any)?.bgPending)
            && messages.value.some((m: any) => m._ownerKey === undefined || m._ownerKey === engineLoadKey)) {
            // Empty first page that cannot judge the chat empty: either
            // empty-but-not-end (a pure-bg band past every hop cap), or an
            // empty DEFERRED page whose bg batch is still flying — a chat whose
            // whole conversation rides the bg queue has a genuinely empty
            // surface (endOfList true on a head refresh), and the batch carries
            // everything. Replacing would blank the chat for the batch's flight
            // time — the greeting flashed over real messages on every tab
            // return. Keep the screen. Mirror of the engine's loadHistory guard.
            // Kept ONLY on the batch's word: the batch handler revisits.
            if (history?.endOfList !== false) keptScreenAwaitingBg = true;
        } else {
            // Abort any running typewriter before replacing messages.value.
            // The typewriter captures an index at start-time; if the new array
            // inserts bg-task bubbles before the regular-chat bubble the old
            // index points to a different message and would corrupt it.
            // The finally block in typewriteIntoIndex is a no-op on abort, so
            // the new array's content (already correct from the server) is safe.
            if (typing.value) chatSession.state.typingAbort = true;

            // Rescue locally-pushed in-flight messages that the server doesn't
            // know about yet — they won't appear in `mapped` and would be lost.
            // Cases:
            //   a) isSendingToServer/isPendingQueued/isPendingInProcess with no
            //      _serverItemId: queued send whose CSR hasn't been acknowledged yet
            //   b) isPending with no _serverItemId: Thinking bubble for a promoted
            //      queued send (inserted by promoteNextQueuedToRunning)
            //   c) sending.value + no-flag user bubble right before case (b):
            //      the immediate-send user message (no distinguishing flags)
            // We re-append them after the replacement so in-flight sends survive
            // repeated fetchHistoryPage calls from the queue-detect polling loop.
            const serverIds = new Set(mapped.map((m) => m._serverItemId).filter(Boolean));

            // Snapshot locally-cancelled IDs before replacement.
            // cancelClientSecretRequest may have returned removed:true while a
            // concurrent getChatHistory call was already in-flight with stale state.
            // The mapped result would show the message as pending instead of cancelled.
            // We re-apply isCancelled after the replacement so it doesn't flicker back.
            const locallyCancelledIds = new Set(
                messages.value
                    .filter((m) => m.isCancelled && m._serverItemId)
                    .map((m) => m._serverItemId as string)
            );

            // A cancel the server has not acknowledged yet. Without carrying these
            // across the replace, a collapsed row mid-cancel drops its "Stopping..."
            // state and re-offers Stop; the second click cancels an id that is
            // already gone and writes a bogus "Could not remove from queue." onto a
            // run that WAS stopped successfully.
            const inFlightCancel = new Map<string, ChatMessage>();
            for (const m of messages.value) {
                if (m._serverItemId && (m._cancelling || m._cancelError)) {
                    inFlightCancel.set(m._serverItemId as string, m);
                }
            }

            // If the freshly-mapped server list ALREADY shows this in-flight turn as
            // a pending placeholder (a non-bg pending assistant, which carries a real
            // _serverItemId), re-pushing the local no-_serverItemId user+Thinking
            // would DUPLICATE it. Each resolve path only clears the first pending
            // bubble, so the duplicate strands a "Thinking…" beside the reply/error.
            // There is at most one in-flight regular turn, so a mapped pending
            // assistant IS this turn — skip the redundant local copy. (Mirrors the
            // engine ChatSession.loadHistory rescue.)
            const mappedHasPendingAssistant = mapped.some(
                (m: any) => m.isPending && m.role === "assistant" && !m.isBackgroundTask
            );
            // Key this fetch is FOR. The chat state lives on a module-level
            // ChatSession singleton shared by every project, so messages.value can
            // still hold an in-flight turn dispatched from ANOTHER project that the
            // user navigated away from. Rescuing those carried the previous
            // project's user bubble + "Thinking..." onto this project's history
            // (and updateHistoryCache below then persisted them under THIS key).
            // Compare against the fetch's own identity, never a live currentService
            // read, which has already flipped to this project. (engineLoadKey is
            // declared at the top of this try, see the note there for why it is the
            // engine's spelling and why a second one cost this guard its meaning: the
            // bubbles a rescue is FOR are the engine's own in-flight ones, and the
            // short key disqualified every single one of them.)
            const rescued: ChatMessage[] = [];
            for (let ri = 0; ri < messages.value.length; ri++) {
                const m = messages.value[ri];
                // One rule, shared with the engine's loadHistory — see
                // shouldRescueInFlightMessage for what it decides and why. It used
                // to be written out twice, and the two had to be edited in lockstep
                // to stay honest; the failure mode when they drift is a turn
                // rendered twice and then PERSISTED into the history cache.
                if (shouldRescueInFlightMessage(m as any, {
                    hasServerId: (sid) => serverIds.has(sid),
                    pageHasPendingAssistant: mappedHasPendingAssistant,
                    sending: sending.value,
                    next: messages.value[ri + 1] as any,
                    // The engine's spelling, which is what the bubbles this rescue is
                    // FOR are stamped with: an in-flight or staged turn is created by
                    // the engine, so the short key this fork used to pass disqualified
                    // every one of them at the cross-project guard.
                    loadKey: engineLoadKey,
                })) rescued.push(m);
            }

            // PRESERVE already-loaded older pages. `mapped` is only page 1, so a
            // blind `messages.value = mapped` threw away every page the user had
            // scrolled in (and every page the cache had just restored) — the
            // "poof, my fetched history is gone" bug. It fired from EVERY
            // first-page refresh: the cache-restore refetch, the queue-detect
            // tick, and the engine's loadHistory via resumePolling.
            //
            // History is DESCENDING by id, so page 1 covers the newest items and
            // anything already in the list whose _serverItemId sorts BELOW page
            // 1's oldest id belongs to an older page. Those are disjoint from
            // `mapped` (strict <), so re-prepending them cannot duplicate.
            // String compare matches the sort used to build the list above.
            let oldestInPage1: string | undefined;
            for (const m of mapped) {
                const sid = m._serverItemId;
                if (typeof sid !== "string") continue;
                if (oldestInPage1 === undefined || sid < oldestInPage1) oldestInPage1 = sid;
            }
            // The retention boundary must be the SURFACE frontier: page 1 also
            // emits bg stubs reaching arbitrarily deep, and keying retention on
            // a weeks-old stub id would wipe (or gap) the scrolled-in pages.
            let surfaceOldestId: string | undefined;
            for (const m of mapped as any[]) {
                if (typeof m._serverItemId !== "string" || m._fromBgChain) continue;
                if (surfaceOldestId === undefined || m._serverItemId < surfaceOldestId) surfaceOldestId = m._serverItemId;
            }
            const retainBoundary: string | undefined = surfaceOldestId !== undefined ? surfaceOldestId : oldestInPage1;
            // Only merge when the current list is genuinely a continuation of THIS
            // page 1 (shares at least one id with it). A project switch, a cleared
            // horizon, or a wholly-new page means the head is unrelated history and
            // must be replaced, not spliced.
            const sharesPage1 = messages.value.some(
                (m) => typeof m._serverItemId === "string" && serverIds.has(m._serverItemId),
            );
            const deferredBg = !!(history as any)?.bgPending;
            const retainedOlder =
                !sharesPage1 || retainBoundary === undefined
                    ? []
                    : messages.value.filter((m) => {
                          // Settled server history only; in-flight bubbles are the
                          // rescue loop's job and would otherwise be kept twice.
                          if (typeof m._serverItemId !== "string") return false;
                          // Same ownership predicate as the rescue loop: never carry
                          // another project's bubbles onto this chat (cross-project
                          // leak fix). Compare against the snapshotted engineLoadKey.
                          if (m._ownerKey !== undefined && m._ownerKey !== engineLoadKey) return false;
                          // While a deferred stub batch is in flight, KEEP loaded
                          // indexing bubbles wherever they sit — the surface-only
                          // page has none, and dropping them would flicker every
                          // row until the batch lands (its apply strips+re-merges
                          // them by id). Mirror of the engine's loadHistory.
                          if (deferredBg && m.isBackgroundTask) return true;
                          // Bg-chain bubbles stay put regardless of the boundary;
                          // later merges strip+replace them with fresher copies.
                          if ((m as any)._fromBgChain) return true;
                          return (m._serverItemId as string) < (retainBoundary as string);
                      });

            // Prepend only the STRICTLY-older bubbles; retained bg bubbles whose
            // ids sit inside page 1's own range are ID-MERGED into it instead —
            // blanket prepending hoisted every indexing row above the whole
            // conversation, and after a head refresh the single-page batch never
            // re-placed the deep ones (engine mirror).
            const prependOlder: any[] = [];
            const interleave: any[] = [];
            for (const m of retainedOlder as any[]) {
                const sid = m._serverItemId as string;
                if (serverIds.has(sid)) continue; // page 1 carries a fresher copy
                if (retainBoundary !== undefined && sid < retainBoundary) prependOlder.push(m);
                else interleave.push(m);
            }
            let page1: any[] = mapped as any[];
            if (interleave.length) {
                const mergedP: any[] = [];
                let ii2 = 0, mi2 = 0;
                while (ii2 < interleave.length && mi2 < page1.length) {
                    const iv = interleave[ii2], mv: any = page1[mi2];
                    const mid2 = typeof mv._serverItemId === "string" ? mv._serverItemId : undefined;
                    if (mid2 === undefined) break; // defensive: id-less tail
                    if ((iv._serverItemId as string) <= mid2) { mergedP.push(iv); ii2++; }
                    else { mergedP.push(mv); mi2++; }
                }
                while (ii2 < interleave.length) mergedP.push(interleave[ii2++]);
                while (mi2 < page1.length) mergedP.push(page1[mi2++]);
                page1 = mergedP;
            }
            messages.value = prependOlder.length ? [...prependOlder, ...page1] : page1;
            // Older pages survived, so the pager must NOT rewind to page 1's
            // boundary — keep the deeper cursor/end-of-list the fetchMore walk
            // already reached, or the next scroll-up would re-fetch page 2.
            keptOlderPages = prependOlder.length > 0 || interleave.length > 0;

            for (const m of rescued) {
                // Rescue can now carry a bubble that has a server id, and the
                // retained-older merge above carries ids too. One turn must not
                // arrive down both routes.
                if (m._serverItemId && messages.value.some(
                    (x) => x._serverItemId === m._serverItemId && x.role === m.role,
                )) continue;
                messages.value.push(m);
            }

            // Re-apply isCancelled for items the server hasn't caught up on yet.
            if (locallyCancelledIds.size > 0) {
                for (let ci = 0; ci < messages.value.length; ci++) {
                    const m = messages.value[ci];
                    if (!m._serverItemId || !locallyCancelledIds.has(m._serverItemId as string) || m.isCancelled) continue;
                    // Server still shows this as pending — restore cancelled state.
                    // Rebuild FROM the mapped bubble rather than as a bare literal:
                    // dropping isBackgroundTask / _indexFile here ejected a stopped
                    // indexing pass from its file's collapsed row, where it then
                    // rendered as a standalone "Indexing: <file>" chat bubble and left
                    // the row recomputing its passes as if that one never happened.
                    messages.value[ci] = {
                        role: "user",
                        content: m.content,
                        isCancelled: true,
                        _serverItemId: m._serverItemId,
                        ...(m.isBackgroundTask ? { isBackgroundTask: true } : {}),
                        ...(m._indexFile ? { _indexFile: m._indexFile } : {}),
                        ...(m._useBgQueue ? { _useBgQueue: true } : {}),
                        ...(m._ownerKey !== undefined ? { _ownerKey: m._ownerKey } : {}),
                    };
                    // Remove associated Thinking bubble if server returned it as running.
                    if (ci + 1 < messages.value.length && messages.value[ci + 1].isPending &&
                        messages.value[ci + 1]._serverItemId === m._serverItemId) {
                        messages.value.splice(ci + 1, 1);
                    }
                }
            }

            // Re-stamp a cancel that is still in flight (see inFlightCancel above).
            // Only onto a bubble the server still reports as pending — once it has
            // settled the flags describe nothing the user can act on.
            if (inFlightCancel.size > 0) {
                for (const m of messages.value) {
                    const was = m._serverItemId ? inFlightCancel.get(m._serverItemId as string) : undefined;
                    if (!was || m.isCancelled) continue;
                    if (!(m.isPendingQueued || m.isPendingInProcess || m.isPending)) continue;
                    if (was._cancelling) m._cancelling = true;
                    if (was._cancelError) m._cancelError = was._cancelError;
                }
            }
            // First-page fetch consumed the latest external refresh signal
            // for this service. Sync the last-seen counter so subsequent
            // watcher callbacks won't re-fire for a bump we already covered.
            const sid = currentService.value?.id;
            if (sid) {
                const cur = agentHistoryRefreshTrigger[sid];
                if (typeof cur === "number") {
                    agentHistoryRefreshLastSeen[sid] = cur;
                }
            }
        }

        // Only adopt page 1's pager position when page 1 IS the whole list. If we
        // just re-prepended older pages, rewinding the cursor here would make the
        // next scroll-up re-fetch page 2 (already on screen) and strand the user.
        if (!keptOlderPages) {
            historyEndOfList.value = !!history?.endOfList;
            historyStartKeyHistory.value = Array.isArray(history?.startKeyHistory)
                ? history.startKeyHistory
                : [];
        }

        // If the cleared-history horizon dropped any item from this page, every
        // older page is also pre-horizon (history is descending), so we can stop
        // fetching older pages.
        const clearedAt = getClearedAt();
        if (clearedAt) {
            // SURFACE items only (mirror of the engine): page 1 also carries bg
            // stubs reaching far past the horizon, and reading their age as
            // "the frontier is pre-horizon" falsely ended the pager with
            // post-horizon surface pages still unfetched.
            const surfaceItems = chatList.filter((it: any) => !it?._fromBgChain);
            if (surfaceItems.length > 0) {
                const oldest = surfaceItems[surfaceItems.length - 1] as any;
                const oldestUpdated = Number(oldest?.updated);
                if (Number.isFinite(oldestUpdated) && oldestUpdated <= clearedAt) {
                    historyEndOfList.value = true;
                }
            }
        }

        updateHistoryCache();

        // For each running/pending item on first-page loads, attach item.poll()
        // so the callback updates the bubble when the server resolves it.
        if (!fetchMore) {
            const cacheKey = `${projectId}#${platform}`;
            // Ration BACKGROUND polls exactly as the engine does (see
            // MAX_CONCURRENT_BG_POLLS); a foreground reply the user is waiting on is
            // never rationed. Taken from the OLDEST bg items: ids sort with newest
            // largest and the server settles a queue FIFO, so the oldest are the only
            // ones that can resolve next. Taking the newest instead would wedge a bulk
            // batch, since those cannot settle until the ones ahead of them do and
            // nothing ahead would hold a poll.
            let bgPollBudget = MAX_CONCURRENT_BG_POLLS;
            chatSession.historyItemPolls.forEach((handle: any) => {
                if (handle && handle.kind === "bg") bgPollBudget--;
            });
            const bgAllow = new Set<string>();
            if (bgPollBudget > 0) {
                const bgIds = chatList
                    .filter((it: any) =>
                        (it.status === 'running' || it.status === 'pending') &&
                        it.poll && it.id &&
                        (it._isBgTask || it._isOnBgQueue) &&
                        !chatSession.historyItemPolls.has(it.id))
                    .map((it: any) => it.id as string)
                    .sort();
                for (let bi = 0; bi < bgIds.length && bi < bgPollBudget; bi++) bgAllow.add(bgIds[bi]);
            }
            for (const item of chatList) {
                if (item.status !== 'running' && item.status !== 'pending') continue;
                if (!item.poll || !item.id) continue;
                // Every live poll registers its item id in historyItemPolls — an
                // immediate dispatch, a queued send, a bg task, or an earlier
                // history poll — so the has() check already skips exactly the items a
                // live poll covers (including the one the current dispatch is polling;
                // its entry survives a normal navigate-away/back because clearChatState
                // only runs on a genuine reset). Do NOT additionally skip every OTHER
                // running/pending item just because some immediate dispatch is in
                // flight: that dispatch polls only ITS item, so blanket-skipping
                // stranded a concurrent "-bg" indexing item (a separate server queue) —
                // or any item whose own poll had died — leaving a "Thinking..." that
                // never clears after the server settled everything.
                if (chatSession.historyItemPolls.has(item.id)) continue;
                // An in-flight immediate dispatch polls its own MAIN-QUEUE item but
                // registers that id in historyItemPolls only AFTER its provider POST
                // returns; in the sliver before that, a remount can surface the item
                // here still unregistered. Skip re-polling MAIN-QUEUE items while a
                // dispatch is in flight so we don't stack a 2nd poll on the item the
                // dispatch is about to poll. Bg-queue items (_isBgTask indexing tasks
                // and _isOnBgQueue chats) run on a SEPARATE queue the immediate
                // dispatch never polls, so they MUST still get their poll — skipping
                // them was the stranded-"Thinking" bug this guard must not revive.
                if (pendingAgentRequests[cacheKey] && !item._isBgTask && !item._isOnBgQueue) continue;
                // Background indexing polls stay off while paused; a foreground reply the
                // user is waiting on keeps polling so it can never strand a "Thinking...".
                if ((item._isBgTask || item._isOnBgQueue) && chatSession.isPollingPaused()) continue;
                // Over the bg poll budget: leave it unpolled. The engine's drain picks
                // it up once an attached poll settles and frees a slot.
                if ((item._isBgTask || item._isOnBgQueue) && !bgAllow.has(item.id)) continue;

                const capturedId = item.id;
                // Named to avoid shadowing the inner `isBg` the onError handler declares below.
                const isBgItem = !!(item._isBgTask || item._isOnBgQueue);
                const pollOpts = {
                    // `meta.executed` is when the worker BEGAN running this request. It
                    // rides on the poll's RUNNING ticks -- the terminal read hands back
                    // the destination's own body, which is no place for our fields -- so
                    // the SDK remembers the last one it saw and hands it over here at
                    // settle. Taking it means an indexing pass shows how long it took the
                    // moment it finishes, with no extra round trip.
                    //
                    // This fork is the one that matters: a pass started from the files
                    // page is discovered by THIS history poll, not by the engine's drain,
                    // so dropping the second argument here is what kept the duration from
                    // appearing until something else refetched history much later.
                    onResponse: (response: any, meta?: { executed?: number }) => {
                        // A stopped poll is not a result; leave the bubble untouched so
                        // resumePolling can re-attach to it.
                        if (response && response.status === "stopped") return;
                        void handleHistoryItemResolution(capturedId, response, platform, cacheKey, meta?.executed);
                    },
                    onError: (err: any) => {
                        chatSession.historyItemPolls.delete(capturedId);
                        const isNotExists = err?.code === 'NOT_EXISTS' || err?.body?.code === 'NOT_EXISTS';
                        const assistantIdx = messages.value.findIndex(
                            (m) => m.isPending && m._serverItemId === capturedId
                        );
                        if (isNotExists) {
                            // Item was cancelled/removed — remove the Thinking... bubble and
                            // mark the user bubble as cancelled instead of showing an error.
                            const userIdx = messages.value.findIndex(
                                (m) => m.role === "user" && m._serverItemId === capturedId && !m.isCancelled
                            );
                            // Decide bg-ness from whichever bubble actually exists. A QUEUED
                            // pass has no assistant placeholder, so reading it off the
                            // placeholder alone classified every queued indexing pass as
                            // foreground: the rebuild below then dropped isBackgroundTask /
                            // _indexFile, ejecting the pass from its collapsed row as a raw
                            // "Indexing: <file>" chat bubble, and promoteNextQueuedToRunning
                            // span up a "Thinking..." on an unrelated foreground message.
                            const isBg = (assistantIdx !== -1 && !!messages.value[assistantIdx].isBackgroundTask)
                                || (userIdx !== -1 && !!messages.value[userIdx].isBackgroundTask);
                            if (assistantIdx !== -1) messages.value.splice(assistantIdx, 1);
                            if (!isBg) {
                                if (userIdx !== -1) {
                                    const existing = messages.value[userIdx];
                                    messages.value[userIdx] = { role: "user", content: existing.content, isCancelled: true, _serverItemId: existing._serverItemId };
                                }
                                chatSession.cancelledServerIds.delete(capturedId);
                                promoteNextQueuedToRunning();
                            } else if (userIdx !== -1) {
                                // Same carry-over every other rebuild does, so the pass stays
                                // a member of its file's row while reading as cancelled.
                                const existing = messages.value[userIdx];
                                messages.value[userIdx] = {
                                    role: "user",
                                    content: existing.content,
                                    isCancelled: true,
                                    isBackgroundTask: true,
                                    _serverItemId: existing._serverItemId,
                                    ...(existing._indexFile ? { _indexFile: existing._indexFile } : {}),
                                    ...(existing._useBgQueue ? { _useBgQueue: true } : {}),
                                };
                                chatSession.promoteNextBgQueuedToRunning();
                            }
                            return;
                        }
                        if (assistantIdx !== -1) {
                            const wasBg = messages.value[assistantIdx].isBackgroundTask;
                            messages.value[assistantIdx] = {
                                role: "assistant",
                                content: getErrorMessage(err),
                                isError: true,
                                ...(wasBg ? { isBackgroundTask: true } : {}),
                            };
                        }
                    },
                };
                // Foreground = a reply the user is watching, so race the flat POLL_INTERVAL with
                // the engine's early probe (skapi's poll never checks at t=0, so a short reply
                // would otherwise idle a full 3s). Background indexing keeps the flat cadence.
                // Shared with the widget through the engine so the two clients cannot drift.
                const pollPromise = isBgItem
                    ? item.poll(Object.assign({ latency: POLL_INTERVAL }, pollOpts))
                    // Pinned to the chat this LOAD is for, snapshotted at its start
                    // (engineLoadKey/platform/projectId/owner), never a live read: a
                    // load can still be attaching polls after the user has moved on,
                    // and a stream stamped with where they moved TO would be finalized
                    // against that project, parsed with that platform's extractor, and
                    // painted into that conversation. _callProviderFor documents the
                    // same hazard and threads its own values for the same reason.
                    // (Engine mirror: its loadHistory passes exactly this.)
                    : chatSession.attachForegroundPoll(item, capturedId, pollOpts, {
                        platform, projectId, owner, ownerKey: engineLoadKey,
                    });
                // Register the handle so (a) a remount dedupes against this poll instead
                // of stacking a second, and (b) pausePolling can stop it. Must read .stop
                // off the poll's OWN promise — chaining .catch() first would hand us a
                // derived promise with no handle on it.
                const pollStop = typeof (pollPromise as any)?.stop === "function"
                    ? (pollPromise as any).stop.bind(pollPromise)
                    : undefined;
                chatSession.historyItemPolls.set(capturedId, {
                    // Everything on the background queue is pausable. _isBgTask vs
                    // _isOnBgQueue only decides how the bubble RENDERS; keying the pause
                    // off _isBgTask alone left bg-queue items we could not text-match
                    // polling forever (observed: stopped 0, remainingBg 0).
                    kind: (item._isBgTask || item._isOnBgQueue) ? "bg" : "fg",
                    stop: pollStop,
                });
                // The SDK always rejects the poll Promise even when onError handles
                // the error. Swallow it here so the global unhandledrejection handler
                // doesn't show an alert box for an error we already dealt with.
                pollPromise?.catch(() => {});
            }

            // Re-inject bg tasks not yet in server history so their Thinking…
            // bubbles survive the messages.value = mapped replacement above.
            drainBgTaskQueue();
            // Learn which files the queue is still working on. The engine seeds this
            // from ITS loadHistory, which this fork never calls — so without this
            // liveIndexChecked stayed false for the whole session here, and "not
            // known" renders as still-working: every already-indexed file showed a
            // spinning row and, if expanded, a loader that never stopped. First page
            // only; the answer describes the whole queue, not this page.
            chatSession.refreshLiveIndexState();
        }

        // Clear the loading flags BEFORE the render that the prepend triggers, so
        // the "Fetching history..." bar is gone by the time the viewport is
        // re-anchored, rather than sitting over the page that just landed.
        //
        // This used to be the fix for a JUMP as well: the bar was an ordinary
        // in-flow sticky element, so mounting it shifted everything below by its
        // own height and unmounting it shifted everything back, once per page,
        // and the fill loop pages several in a row. That is no longer what makes
        // it safe - the bar now takes no height in flow at all (see
        // .bq-history-loading) and cannot move the list whenever it mounts. The
        // early clear stays because it is still the right ORDER. (The finally
        // stays as the error-path safety net.)
        if (chatSession.state.historyRequestToken === token) {
            loadingHistory.value = false;
            loadingOlderHistory.value = false;
        }

        // AND AN UNKNOWN ANSWER LEFT OVER IS RESOLVED BY READING THE CHUNKS BACK.
        // Fired AFTER the page is rendered and the flags are cleared, never before:
        // a streamed turn nobody finalized is a turn whose answer is sitting in the
        // chunk store, and going to fetch it must not hold up the conversation it
        // belongs to. Bounded (2 per load, newest first) and serial inside the
        // engine, and it never throws.
        //
        // Handed the load's SNAPSHOTTED identity, not a live read, for the same
        // reason every other step in this function is: a project switch racing the
        // response would otherwise finalize this turn against the project the user
        // switched TO. Same position as the engine's loadHistory. (This fork is the
        // MOUNT path and never calls loadHistory, so without this line an unfinalized
        // turn would be marked here and then never read back - CRITICAL 1 left open
        // on the path the user actually arrives through, with the marker making it
        // look handled.)
        //
        // engineLoadKey is the chat's identity in the ENGINE's spelling - see the note
        // where it is declared. This is the call where the wrong one costs the answer:
        // the engine's "is this chat still the one on screen?" test would fail on
        // every call, and a recovered answer would be written into a cache key nothing
        // reads instead of onto the bubble the reader is looking at.
        chatSession.scheduleStreamRecovery(engineLoadKey, platform, projectId, owner);

        // ── deferred indexing-stub batch (first-paint split) ─────────────────
        // The conversation is on screen; when the stub batch lands, classify/
        // map it like any page and STRIP-THEN-MERGE by timestamp (existing
        // copies of incoming ids removed first — retained rows may sit at
        // provisional positions). Mirror of the engine's loadHistory handler.
        const bgPending = !fetchMore && (history as any)?.bgPending;
        if (bgPending) {
            // Ownership id for the shared flag: a same-token refetch can mint a
            // SECOND bgPending, and the older handler settling later must neither
            // clear nor keep the flag the newer batch owns (engine mirror).
            const batchId = ++bgHistoryBatchSeq;
            // SILENT head refresh: an ended chain's immediate return already says
            // endOfList true — its batch is a quiet top-up, not "loading indexing
            // history"; the hint is for a genuine first paint (engine mirror).
            // SILENT unless this is the chat's genuine FIRST PAINT: an ended
            // chain's head refresh and a mid-walk tab return both merge quietly
            // (flashing the hint on every return was the reported bug).
            if ((history as any)?.endOfList !== true && (history as any)?.firstLoad === true) chatSession.state.bgHistoryLoading = true;
            const releaseBgFlag = () => {
                if (bgHistoryBatchSeq === batchId) chatSession.state.bgHistoryLoading = false;
            };
            (bgPending as Promise<any>).then(async (batch: any) => {
                if (token !== chatSession.state.gateRefreshToken) { releaseBgFlag(); return; }
                const bList: any[] = Array.isArray(batch?.list) ? batch.list : [];
                for (const item of bList) {
                    if (isBgIndexingQueue(item.queue_name)) {
                        const t = item.compact ? item.request_text : extractLastUserTextFromRequest(item.request_body);
                        if (typeof t === "string"
                            && (t.startsWith("A new file has just been uploaded") || t.startsWith("CONTINUE indexing"))) {
                            item._isBgTask = true;
                        } else {
                            item._isOnBgQueue = true;
                        }
                    }
                }
                const sorted = bList.sort((a: any, b: any) => {
                    const ai = typeof a.id === "string" ? a.id : "", bi = typeof b.id === "string" ? b.id : "";
                    return ai > bi ? -1 : ai < bi ? 1 : 0;
                });
                const { messages: m2 } = mapHistoryListToMessages(sorted, platform, engineLoadKey);
                chatSession.applyHydratedBodies(m2 as any);
                if (keptScreenAwaitingBg && !m2.length && batch?.endOfList === true) {
                    // The empty page 1 was kept ONLY on this batch's word — and it
                    // just proved the chat empty server-side. Let the kept screen
                    // go: drop this chat's settled server bubbles, keep staged and
                    // in-flight local work, remember the end (engine mirror).
                    messages.value = (messages.value as any[]).filter((m) => {
                        if (typeof m._serverItemId !== "string") return true;
                        if (m._ownerKey !== undefined && m._ownerKey !== engineLoadKey) return true;
                        return false;
                    });
                    historyEndOfList.value = true;
                    releaseBgFlag();
                    updateHistoryCache();
                    return;
                }
                const incoming = new Set<string>();
                for (const m of m2 as any[]) { if (m._serverItemId) incoming.add(m._serverItemId + "|" + m.role); }
                const baseList = (messages.value as any[]).filter(
                    (m) => !(m._serverItemId && incoming.has(m._serverItemId + "|" + m.role)),
                );
                const mergedList: any[] = [];
                let pi2 = 0, ei2 = 0;
                while (pi2 < m2.length && ei2 < baseList.length) {
                    const pm2: any = m2[pi2], em2: any = baseList[ei2];
                    const eid2 = em2._serverItemId;
                    if (typeof eid2 !== "string") break; // staged/local tail
                    const pid2 = pm2._serverItemId;
                    if (typeof pid2 !== "string" || pid2 <= eid2) { mergedList.push(pm2); pi2++; }
                    else { mergedList.push(em2); ei2++; }
                }
                while (pi2 < m2.length) mergedList.push(m2[pi2++]);
                while (ei2 < baseList.length) mergedList.push(baseList[ei2++]);
                messages.value = mergedList;
                if (batch?.endOfList === true) historyEndOfList.value = true;
                releaseBgFlag();
                updateHistoryCache();
                // The other half of the refresh. Everything this batch brought in sits
                // ABOVE or AMONG the newest turn, so a reader pinned to the bottom was
                // left stranded above it by exactly the batch's height, with nothing to
                // put them back (every scroll-anchor method no-ops while pinned, and
                // this handler ended here).
                settleScrollAfterRefresh();
                // Rows may have added height (or not) — re-run the fill so the
                // scroll-to-top pager trigger exists either way.
                void ensureHistoryFillsViewport(token);
            }, () => {
                // Stubs missing until the next refresh re-asks; never fatal.
                releaseBgFlag();
            });
        }

        if (fetchMore && messagesBox.value) {
            await nextTick();
            if (token !== chatSession.state.gateRefreshToken || !messagesBox.value) {
                return;
            }
            // The viewport itself is re-anchored by the chatDisplayRows watchers
            // (captureRowScrollAnchor / restoreRowScrollAnchor), which hold a
            // specific ROW in place. A scrollHeight delta cannot do that job here:
            // an older page whose messages all join a collapsed indexing row adds
            // no row of its own — they fold into the row that is already there —
            // so the height it adds is not height above them.
            // Only the pinned-to-bottom case is left, which the anchor deliberately
            // skips.
            if (stickToBottom) {
                chatScrollAnchor.pinBottom();
            }
        }

        if (!fetchMore) {
            // Sticky, NOT forcing. This runs after EVERY first-page fetch —
            // including background ones (cache-restore refetch, queue-detect tick,
            // resumePolling) — and the forcing variant both yanked a reader who had
            // scrolled up into history back to the bottom AND unconditionally set
            // stickToBottom = true, which silently re-armed the detect loop's
            // !stickToBottom bail. On a genuine mount the user is still pinned, so
            // this behaves identically there.
            //
            // Through settleScrollAfterRefresh, which is also what the deferred bg
            // batch below calls: one decision, made the same way at both moments.
            settleScrollAfterRefresh();
        }
    } catch (err) {
        if (token === chatSession.state.gateRefreshToken) {
            debugAgentLoad("getChatHistory failed", {
                token,
                fetchMore,
                projectId,
                platform,
                error: getErrorMessage(err),
            });
        }
        // history is optional; ignore if unavailable
    } finally {
        if (chatSession.state.historyRequestToken === token) {
            loadingHistory.value = false;
            loadingOlderHistory.value = false;
        }
        // A first page that renders shorter than the box leaves the user with no
        // way to reach page 2 (scroll-to-top is the only pager trigger, and a box
        // that cannot scroll never fires it). Trivially reached once a page of
        // indexing turns collapses into one row. Fire-and-forget, and only from
        // the first-page path — the fill loop itself drives the fetchMore path.
        // Deliberately after the flags are cleared: the loop bails while a
        // history request is still in flight.
        if (!fetchMore && chatSession.state.historyRequestToken === token) {
            void ensureHistoryFillsViewport(token);
        }
    }
};

const refreshGate = async () => {
    const token = ++chatSession.state.gateRefreshToken;
    const parsedAiAgent = parseAiAgentValue(currentService.value?.service?.ai_agent);

    // The sweep-derived refs describe ONE project, and a switch re-runs this
    // gate without remounting — refs from the old project must not survive into
    // the new one's stale window (they painted A's stub rows into B's chat and
    // filed A's verdicts under B). A same-project re-gate keeps them:
    // last-known-good through an invalidation, no flash.
    const gateSvc = currentService.value?.id || "";
    if (markerRefsSvc !== gateSvc) {
        markerRefsSvc = gateSvc;
        runStubRecords.value = {};
        doneMarkerKeys.value = {};
        markerSweepSettled.value = false;
        stubRecheckSig = "";
        stubRecheckRounds = 0;
        // Paths are project-relative and collide across projects; the once-per-
        // path guard must not suppress a legitimate flip in the next project.
        mintObservedPaths.clear();
    }

    selectedAiPlatform.value = parsedAiAgent.platform;
    selectedAiModel.value = parsedAiAgent.model;
    hasAiPlatform.value = parsedAiAgent.hasPlatform;
    hasAiApiKey.value = false;
    initialGateLoading.value = parsedAiAgent.hasPlatform;
    let shouldLoadHistory = false;
    let shouldClearHistory = false;

    // Kick the done:: marker sweep alongside everything else (fire-and-forget,
    // shared+cached with the files page): rows resolve green the moment both
    // the history and this one answer, without waiting on the queue probe.
    if (parsedAiAgent.hasPlatform) refreshDoneMarkers();

    // Speculative history load: the history request never needs the KEY, only
    // the final boolean gates whether its result stays. So the moment key
    // resolution goes async (shared prefetch or a listClientSecrets round
    // trip), start the history fetch in parallel instead of behind it. If the
    // key turns out missing (rare: platform configured but key deleted), the
    // no-key ending below awaits this promise and clearChatState() wipes what
    // it painted — the gate loader hides that window either way.
    let historyPromise: Promise<void> | null = null;
    const speculateHistory = () => {
        if (!historyPromise) {
            historyPromise = loadChatHistory(token, true).catch(() => {});
        }
    };

    if (!currentService.value || !hasAiPlatform.value) {
        shouldClearHistory = true;
    } else {
            const projectId = currentService.value.id;
            const secretId = selectedAiPlatform.value as "claude" | "openai";

            // A visit-time prefetch (main.vue run) may already be fetching this
            // key — await that shared request so the cache check below reuses its
            // result instead of firing a duplicate (common on a direct agent load).
            const inflightKey = aiApiKeyInflight[projectId + "#" + secretId];
            if (inflightKey) {
                speculateHistory();
                try { await inflightKey; } catch { /* prefetch is best-effort */ }
                if (token !== chatSession.state.gateRefreshToken) return;
            }

            // check lightweight cache first
            if (aiApiKeyCache[projectId]?.[secretId]) {
                hasAiApiKey.value = true;
                shouldLoadHistory = true;
            } else {
                // check the shared client-secrets pager if it has this key cached already
                const pager = serviceClientSecrets[projectId];
                if (pager?.list?.[secretId]?.client_secret) {
                    hasAiApiKey.value = true;
                    shouldLoadHistory = true;
                    aiApiKeyCache[projectId] = {
                        ...(aiApiKeyCache[projectId] || {}),
                        [secretId]: pager.list[secretId].client_secret,
                    };
                } else {
                    try {
                        debugAgentLoad("listClientSecrets start", {
                            token,
                            projectId,
                            secretId,
                        });

                        speculateHistory();
                        const result = await currentService.value.listClientSecrets(false, secretId);

                        debugAgentLoad("listClientSecrets resolved", {
                            token,
                            projectId,
                            secretId,
                            count: result?.list?.length || 0,
                        });

                        if (token !== chatSession.state.gateRefreshToken) {
                            return;
                        }

                        const match = result?.list?.find((s) => s.id === secretId);

                        if (match?.key && typeof match.key === "string" && match.key.trim()) {
                            hasAiApiKey.value = true;
                            shouldLoadHistory = true;
                            aiApiKeyCache[projectId] = {
                                ...(aiApiKeyCache[projectId] || {}),
                                [secretId]: match.key,
                            };
                        } else {
                            shouldClearHistory = true;
                        }

                        debugAgentLoad("listClientSecrets cached", {
                            token,
                            projectId,
                            secretId,
                            cached: !!aiApiKeyCache[projectId]?.[secretId],
                        });
                    } catch (err) {
                        if (token === chatSession.state.gateRefreshToken) {
                            debugAgentLoad("listClientSecrets failed", {
                                token,
                                projectId,
                                secretId,
                                error: getErrorMessage(err),
                            });
                        }
                        hasAiApiKey.value = false;
                        shouldClearHistory = true;
                    }
                }
            }
        }

    if (token !== chatSession.state.gateRefreshToken) {
        return;
    }

    if (shouldLoadHistory && hasAiApiKey.value && hasAiPlatform.value) {
        // Reuse the speculative fetch when one was started; it is the same
        // token, so all its internal guards hold.
        await (historyPromise || loadChatHistory(token));
        if (token === chatSession.state.gateRefreshToken) {
            initialGateLoading.value = false;
            await scrollToBottom(false);
            // Now that the gate placeholder is gone, the message box is finally
            // measurable — the fill that ran inside loadChatHistory could only
            // see `.bq-gate-loading` (min-height:100%, so never scrollable).
            void ensureHistoryFillsViewport(token);
            void chatSession.resumePendingRequest(token);
        }
        return;
    }

    if (shouldClearHistory) {
        // A speculative history fetch may still be applying state — let it
        // settle before wiping, or its late writes would survive the clear.
        if (historyPromise) await historyPromise;
        if (token !== chatSession.state.gateRefreshToken) return;
        clearChatState();
    }

    if (token === chatSession.state.gateRefreshToken) {
        initialGateLoading.value = false;
    }
};

const loadChatHistory = async (token = chatSession.state.gateRefreshToken, assumeKey = false) => {
    if (token !== chatSession.state.gateRefreshToken) {
        return;
    }

    // `assumeKey` is refreshGate's speculative start: the key lookup is still
    // in flight, so hasAiApiKey is not yet meaningful. Platform checks stay —
    // they are known synchronously.
    if ((!assumeKey && !hasAiApiKey.value) || !hasAiPlatform.value || selectedAiPlatform.value === "none") {
        clearChatState();
        return;
    }

    const cacheKey = getHistoryCacheKey();
    // Expansion is per-chat, and switching projects re-runs refreshGate rather
    // than remounting this component, so the Set has to be dropped here (see
    // clearChatState, which covers the disabled-chat paths).
    if (cacheKey !== expandedIndexGroupsChatKey) {
        expandedIndexGroupsChatKey = cacheKey;
        expandedIndexGroups.value = new Set();
        // Same reason, DIFFERENT scope: the live-index snapshot describes ONE
        // project's queue. The engine drops it inside its own loadHistory, which
        // this fork never calls, so carrying it across a switch let a row in the
        // new project read "Indexed" on the previous project's evidence —
        // including for a file being indexed right now. But this branch also
        // fires on EVERY mount (expandedIndexGroupsChatKey is setup-scoped), and
        // resetLiveIndexState is the engine's call to make: it wipes only when
        // the snapshot belongs to a DIFFERENT chat, so re-entering the page keeps
        // the rows' last known green/yellow while the first-page fetch below
        // re-asks the queue quietly. Only a genuine project/platform switch goes
        // back to grey "not known yet".
        chatSession.resetLiveIndexState();
    }
    const cached = aiChatHistoryCache[cacheKey];

    if (cached) {
        if (token !== chatSession.state.gateRefreshToken) {
            return;
        }
        // Drop any bubble stamped for a different chat. Entries written before
        // this guard existed (or by an older tab) can still hold another
        // project's in-flight user + "Thinking..." bubbles, and this branch is
        // the replay vector that would restore them on every visit.
        // A STAGED turn is resolved by an in-page upload, never by a server request,
        // so a restored copy with no chain left behind it would read
        // "(Uploading files...)" forever. Today the cache and the chains die together
        // and this changes nothing; it keeps that from being a silent assumption.
        const restored = chatSession.settleDeadStagedMessages(cached.messages as any) as ChatMessage[];
        const sanitizedCachedMessages = restored
            .filter((m) => m._ownerKey === undefined || m._ownerKey === cacheKey)
            .map((m) =>
                m.role === "user"
                    ? { ...m, content: sanitizeAttachmentLinksForHistory(m.content) }
                    : m,
            );
        // This branch replaces the list wholesale BEFORE the refetch that would
        // otherwise rescue a live staged turn from state.messages, so carry one across
        // directly. Normally the cached copy above already has it and this finds
        // nothing; it covers the window between Send and the first updateHistoryCache,
        // during which the bubble exists only in state.messages. Deduped by stage id —
        // restoring both copies would show the question twice.
        const cachedStageIds = new Set(
            sanitizedCachedMessages.map((m) => m._stageId).filter(Boolean) as string[],
        );
        const stagedLive = messages.value.filter(
            (m: ChatMessage) =>
                !!m._stageId &&
                !cachedStageIds.has(m._stageId) &&
                (m._ownerKey === undefined || m._ownerKey === cacheKey),
        );
        messages.value = stagedLive.length
            ? [...sanitizedCachedMessages, ...stagedLive]
            : sanitizedCachedMessages;
        historyEndOfList.value = !!cached.endOfList;
        historyStartKeyHistory.value = Array.isArray(cached.startKeyHistory)
            ? [...cached.startKeyHistory]
            : [];
        aiChatHistoryCache[cacheKey] = {
            ...cached,
            messages: sanitizedCachedMessages,
        };
        // Show any bg-task bubbles that arrived since the cache was written
        // (e.g. user uploaded while on another page) without waiting for the
        // network round-trip below.
        drainBgTaskQueue();
        await scrollToBottom();
        // Always do a fresh fetch on top of cache so item.poll() is attached
        // to any currently running/pending items.
        void fetchHistoryPage(false, token, assumeKey);

        // If the refresh trigger was bumped while unmounted, also kick the
        // queue-detect loop to surface newly-registered bg-indexing entries.
        const projectId = currentService.value?.id;
        if (projectId) {
            const cur = agentHistoryRefreshTrigger[projectId];
            const last = agentHistoryRefreshLastSeen[projectId];
            if (typeof cur === "number" && cur !== last) {
                agentHistoryRefreshLastSeen[projectId] = cur;
                startQueueDetectPoll();
            }
        }
        return;
    }

    // No cache for this chat. messages.value is the shared singleton array and
    // may still hold the previous project's turn; drop those now so they are
    // never the base the fresh fetch builds on (the render gate usually hides
    // this window, but nothing guarantees it stays that way).
    if (messages.value.some((m) => m._ownerKey !== undefined && m._ownerKey !== cacheKey)) {
        messages.value = messages.value.filter(
            (m) => m._ownerKey === undefined || m._ownerKey === cacheKey,
        );
    }
    historyEndOfList.value = false;
    historyStartKeyHistory.value = [];
    await fetchHistoryPage(false, token, assumeKey);

    // No-cache path: a fresh fetch may have happened too early to include
    // the queued indexing requests (the proxy registers them
    // asynchronously). If the refresh trigger has been bumped since we
    // last consumed it, kick the queue-detect loop so we keep polling
    // until the new pending entries surface.
    const sidNoCache = currentService.value?.id;
    if (sidNoCache) {
        const cur = agentHistoryRefreshTrigger[sidNoCache];
        const last = agentHistoryRefreshLastSeen[sidNoCache];
        if (typeof cur === "number" && cur !== last) {
            agentHistoryRefreshLastSeen[sidNoCache] = cur;
            startQueueDetectPoll();
        }
    }
};

// Can the user reach older history at all? Paging is triggered ONLY by scrolling
// to the top of this box, so a box with nothing to scroll has no trigger.
const messagesBoxCanScroll = (): boolean => {
    const el = messagesBox.value;
    // The box is not showing messages — not mounted, or the gate placeholder is
    // still up (`.bq-gate-loading` is min-height:100%, so it measures as exactly
    // unscrollable no matter how tall the real page is). Report "scrollable" so
    // the fill does not page history it cannot see; refreshGate re-runs it once
    // the placeholder clears.
    if (!el || initialGateLoading.value) return true;
    return el.scrollHeight - el.clientHeight > HISTORY_FILL_SLACK_PX;
};

// The row currently at the top of the viewport, and how much scrollable content
// sits above it. GROWTH in that number — not growth in the box's total height —
// is what makes a scroll-up worthwhile: an older page whose messages all join a
// collapsed indexing row adds no row of its own (they fold into the row that is
// already there), so the box can get taller while nothing new comes within reach.
const topVisibleRowKey = (): string | null => {
    const box = messagesBox.value;
    if (!box) return null;
    const boxTop = box.getBoundingClientRect().top;
    for (const el of Array.from(box.children) as HTMLElement[]) {
        const key = el.dataset?.rowKey;
        if (!key) continue;
        if (el.getBoundingClientRect().top - boxTop + el.offsetHeight > 0) return key;
    }
    return null;
};
const contentAboveRow = (key: string): number | null => {
    const box = messagesBox.value;
    if (!box) return null;
    const boxTop = box.getBoundingClientRect().top;
    for (const el of Array.from(box.children) as HTMLElement[]) {
        if (el.dataset?.rowKey !== key) continue;
        return el.getBoundingClientRect().top - boxTop + box.scrollTop;
    }
    return null;
};

// Shared plumbing for the two "keep paging" loops below. The loop itself is the
// engine's (createHistoryFiller), so the BunnyQuery widget behaves identically;
// only the DOM measurement is view-side. One loop at a time, with a request that
// arrives mid-loop ANDed into it rather than dropped — see createHistoryFiller
// for why dropping picks the wrong winner.
//
// The token is captured per call, so the filler's own guards read the LIVE chat
// rather than whichever chat happened to start the loop.
let historyFillToken = 0;
// The fill LOOP is running. Spans the gaps between its pages, where the
// per-request loadingHistory keeps dropping back to false; a collapsed indexing
// row still waiting on its own earlier passes renders off this rather than
// flickering between "loading" and a status once per page.
const historyFilling = ref(false);
const historyFiller = createHistoryFiller({
    isEndOfList: () => historyEndOfList.value,
    isLoading: () => loadingHistory.value,
    isStale: () =>
        historyFillToken !== chatSession.state.gateRefreshToken ||
        !chatEnabled.value ||
        selectedAiPlatform.value === "none",
    messageCount: () => messages.value.length,
    fetchOlder: async () => {
        // Report a swallowed request rather than letting the unchanged message
        // count read as an exhausted pager.
        if (loadingHistory.value) return false;
        await fetchHistoryPage(true, historyFillToken);
        return true;
    },
    // A collapsed indexing row whose run begins above the loaded window says
    // "loading" for as long as the pages that would complete it keep coming, and
    // that span is the LOOP, not a page (see createHistoryFiller).
    onRunningChange: (running) => { historyFilling.value = running; },
});
const pageOlderHistoryUntil = async (
    isSatisfied: () => boolean,
    token = chatSession.state.gateRefreshToken,
) => {
    if (!chatEnabled.value || selectedAiPlatform.value === "none") return;
    if (token !== chatSession.state.gateRefreshToken) return;
    historyFillToken = token;
    await historyFiller.fill(async () => {
        // Measure only after the just-fetched page has actually rendered.
        await nextTick();
        return isSatisfied();
    });
};

// Keep paging older history until this box can actually scroll. Without it a
// chat whose newest page collapses to a single indexing row (a file's every pass
// folds into ONE row — twenty-plus messages, one line) never overflows, never
// fires a scroll event, and so never loads page 2: the user's own conversation
// underneath the upload is unreachable.
const ensureHistoryFillsViewport = (token = chatSession.state.gateRefreshToken) =>
    pageOlderHistoryUntil(messagesBoxCanScroll, token);

// Keep paging until the scroll-up the user just made actually put something
// ABOVE them. One page is not enough: a page that is entirely one file's earlier
// passes joins the collapsed row already on screen and adds no row of its own, so
// the reader stays pinned at scrollTop 0, where scrolling up again fires no
// further event. (If that page carried the run's FIRST pass, the row is
// re-identified — contentAboveRow finds no such row, and the loop stops rather
// than paging on a measurement it can no longer make.)
const pageOlderHistoryUntilTaller = async (token = chatSession.state.gateRefreshToken) => {
    const anchorKey = topVisibleRowKey();
    const before = anchorKey ? contentAboveRow(anchorKey) : null;
    await pageOlderHistoryUntil(
        () => {
            // Nothing to measure against (empty list, anchor row gone): let the
            // single page that was just fetched stand.
            if (!messagesBox.value || !anchorKey || before === null) return true;
            const now = contentAboveRow(anchorKey);
            return now === null || now > before + HISTORY_FILL_SLACK_PX;
        },
        token,
    );
};

// Where the box was at the previous scroll event. Only used to tell a scroll DOWN
// from the browser clamping scrollTop after the content above the reader shrank.
let lastHistoryScrollTop = 0;
// The next scroll event was caused by our own compensation, not by the reader.
let anchorWroteSinceScroll = false;
const onHistoryScroll = async () => {
    if (!messagesBox.value) {
        return;
    }

    const el = messagesBox.value;
    // A scroll event dispatched while the tab is hidden is not the reader: it is a
    // shrink-then-grow patch firing one event against the grown scrollHeight, which
    // reads as "not at the bottom" and silently un-sticks someone who left pinned.
    // It could also fire the pager at scrollTop <= 60 with rAF frozen, where it
    // cannot finish.
    if (typeof document !== "undefined" && document.hidden) { lastHistoryScrollTop = el.scrollTop; return; }
    // Track whether the user is pinned to the bottom so a later viewport
    // height change can re-stick if they never scrolled away.
    //
    // Re-sticking additionally requires having moved DOWN. Content shrinking above
    // the reader — a group collapsing, a re-render, an image failing back to
    // nothing, a page replaced — makes the browser CLAMP scrollTop to the new
    // maximum, which is "at the bottom" by definition and fires a scroll event
    // indistinguishable from the reader arriving there. Read as sticky, that
    // pinned someone who was mid-history to the newest message and then re-pinned
    // them on every poll that followed. A clamp can only ever LOWER scrollTop, so
    // this separates the two exactly, and costs nothing in the real case: a reader
    // who scrolls back down to the bottom is moving down while they do it.
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 16;
    const ours = anchorWroteSinceScroll;
    anchorWroteSinceScroll = false;
    if (!atBottom) stickToBottom = false;
    // `ours`: the box's own compensation write can land exactly AT the bottom (content
    // above grew while content below shrank in the same update), and the scroll event
    // that follows is indistinguishable from the reader flicking down there. Read as
    // the reader, it silently converts them into a bottom-pinned one, and from then on
    // every keyboard open and every merge drags them to the newest message.
    // Flagged rather than value-matched: a clamp reports the POST-clamp position, never
    // the value that was written, and scroll events coalesce, so the number cannot say
    // who caused it. The host knows, because the host made the call.
    else if (!ours && el.scrollTop >= lastHistoryScrollTop) stickToBottom = true;
    lastHistoryScrollTop = el.scrollTop;

    // This is where the reader's place comes from. Every scroll — theirs, and
    // every programmatic one — lands here, so the anchor is always the position
    // the box is actually at, and chatScrollAnchor.hold() can put it back after
    // anything that resolves in the background. Cheap: one pass over the rows,
    // and it stops at the first one on screen.
    chatScrollAnchor.remember();

    if (el.scrollTop <= 60) {
        // Not a single page: keep going until the scroll-up revealed something,
        // or a page of pure indexing passes (which collapses into a row already
        // on screen) leaves the user pinned at the top with nothing new.
        await pageOlderHistoryUntilTaller();
    }
};

// An <img> (or anything else that loads) inside the list just settled and changed
// its own box. Hold the reader's place; scrollToBottomIfSticky covers the reader
// who is pinned to the bottom instead, and the two cases are exclusive.
//
// Bound imperatively and in the CAPTURE phase because `load` does not bubble, so
// there is no template listener that can see it. On the box rather than per
// element because this has to cover the images the preview machinery never
// touches: a markdown `![alt](url)` the model wrote is a plain <img> with a live
// src, with no state attribute, no hydration and no hook of its own.
// A late web font re-wraps every bubble at once, with no DOM mutation and no
// event any other hook is bound to — and with native scroll anchoring off (see
// the .bq-messages rule) nothing else would absorb it. `loadingdone`, not
// document.fonts.ready: ready resolves once and misses every later arrival.
const onFontsSettled = () => chatScrollAnchor.hold();

const onMessagesImageSettled = (e: Event) => {
    const img = e.target as HTMLElement | null;
    if (img?.tagName !== "IMG") return;
    // absorb, not hold: an image inside the anchored row (a reply taller than the
    // viewport, a picture higher up in it) moves every line the reader is on
    // WITHOUT moving the row's own top, which is the one thing hold() measures.
    // absorb compensates by the element's own delta instead, and only while the
    // element's top is above the fold, so the two can never both pay for the same
    // pixel.
    //
    // previewLayoutBox, so this and the engine's own onLayoutChange hook key
    // absorb's per-element memo on the SAME node: the box that resizes is the
    // anchor (picture + dot-trail loader + caption chip), not the <img> alone.
    anchorWroteSinceScroll = true;
    chatScrollAnchor.absorb(previewLayoutBox(img as any) as any);
};

// Explicit user scroll-UP intent. wheel/touch fire synchronously on the user's
// action (and never for programmatic scrolls), so setting stickToBottom = false
// here beats the streaming typewriter's per-tick auto-scroll — letting the user
// scroll up to read earlier messages while a response is still generating.
// (Re-sticking when they scroll back to the bottom is handled by onHistoryScroll.)
const onMessagesWheel = (e: WheelEvent) => {
    if (e.deltaY < 0) stickToBottom = false;
};
let touchStartY = 0;
const onMessagesTouchStart = (e: TouchEvent) => {
    touchStartY = e.touches[0]?.clientY ?? 0;
};
const onMessagesTouchMove = (e: TouchEvent) => {
    // Finger dragging DOWN the screen scrolls content UP (toward earlier
    // messages), so release stickiness.
    if ((e.touches[0]?.clientY ?? 0) > touchStartY + 4) stickToBottom = false;
};

const applyAgentViewportHeight = () => {
    if (!agentRoot.value) {
        return;
    }

    const topOffset = Math.max(0, agentRoot.value.getBoundingClientRect().top);
    const isDesktop = window.matchMedia("(min-width: 801px)").matches;
    const navHeight = isDesktop ? (document.getElementById("navBar") as HTMLElement | null)?.offsetHeight || 0 : 32;
    const footerHeight = isDesktop
        ? parseFloat(getComputedStyle(document.body).getPropertyValue("--footer-height")) || 0
        : 0;

    const available = Math.max(240, window.innerHeight - topOffset - navHeight - footerHeight);
    agentRoot.value.style.setProperty("--agent-available-height", `${available}px`);

    // Changing the container height reflows the messages box. If the user
    // is pinned to the bottom (e.g. just mounted, or just sent a message),
    // re-scroll so the late height update on mobile doesn't leave us short
    // of the bottom.
    if (stickToBottom && messagesBox.value) {
        requestAnimationFrame(() => {
            if (stickToBottom && messagesBox.value) {
                chatScrollAnchor.pinBottom();
            }
        });
    }

    // A taller box (rotation, keyboard dismissed, window resized) can stop
    // overflowing, which takes the scroll-to-top pager trigger away with it.
    void ensureHistoryFillsViewport();
};

const scheduleAgentViewportHeight = (remainingChecks = 6) => {
    window.cancelAnimationFrame(viewportHeightFrame);

    const run = async () => {
        await nextTick();
        viewportHeightFrame = window.requestAnimationFrame(() => {
            const root = agentRoot.value;

            if (!root) {
                return;
            }

            const isVisible = root.getClientRects().length > 0 && root.getBoundingClientRect().height > 0;

            if (!isVisible && remainingChecks > 0) {
                scheduleAgentViewportHeight(remainingChecks - 1);
                return;
            }

            applyAgentViewportHeight();
        });
    };

    void run();
};

const onWindowResize = () => {
    scheduleAgentViewportHeight();
};

type MsgPart =
    | { type: 'text'; text: string }
    | { type: 'html'; html: string }
    | { type: 'file'; filename: string; href: string }
    | {
          type: 'link';
          label: string;
          fullLabel?: string;
          href: string;
          expired: boolean;
          expiredHref?: string;
          remotePath?: string;
          /** Set by classifyInlineLink for a hosted file a browser can paint. */
          image?: { ext: string; contentType: string };
      };

const refreshingLinkMap = ref<Record<string, boolean>>({});
const refreshedExpiredLinkMap = ref<Record<string, string>>({});
const refreshingLinkPromises = new Map<string, Promise<string>>();
// Files we asked for a url for and did not get one: a failed mint (click or
// image preview) or a preview whose minted url would not load. Keyed by the
// engine's linkUnavailableKeyFor* so a failure reported with a storage path and
// one reported with a placeholder href both find their chip. Reactive because
// the chip's rendering depends on it (see the parse-cache watcher below).
const unavailableLinkMap = ref<Record<string, boolean>>({});

// Memoize parsed bubble HTML keyed by the raw message content. During the
// typewriter the whole message list re-renders once per revealed frame, and
// Vue re-invokes parseMsgParts for EVERY message each time — re-running
// marked.parse over the entire conversation on every frame. That per-frame
// re-parse of already-settled bubbles was the dominant cost that dragged the
// chat out on slow machines. Settled messages have stable content, so we parse
// them once and reuse; only the actively-typing bubble (new content each frame)
// and typing-state-dependent output (the "[generating …]" fence placeholder,
// see below) are recomputed.
//
// LRU eviction: parseCacheGet promotes a hit to most-recently-used, so the
// settled bubbles re-read every frame stay at the hot end, while the
// typewriter's transient intermediate strings (each read once) sink to the cold
// front and are evicted first. The cap is sized to the live message count so a
// long, scrolled-back history (which re-scans >cap distinct keys per frame)
// can't self-evict its own still-visible bubbles and thrash back to a full
// re-parse every frame — the exact O(n)-per-frame cost this cache removes. The
// three link maps above are the only external inputs to the parse, so drop the
// cache whenever they change (rare user-driven link refreshes and the equally
// rare mint failure; never typing).
const PARSE_CACHE_MIN = 400;
const parseMsgPartsCache = new Map<string, MsgPart[]>();
const parseCacheGet = (key: string): MsgPart[] | undefined => {
    const v = parseMsgPartsCache.get(key);
    if (v) { parseMsgPartsCache.delete(key); parseMsgPartsCache.set(key, v); }  // LRU: mark as most-recently-used
    return v;
};
const parseCacheSet = (key: string, value: MsgPart[]) => {
    parseMsgPartsCache.set(key, value);
    // Keep room for every currently-rendered bubble plus a margin for the
    // typewriter's in-flight transient keys, so settled bubbles are never
    // evicted mid-frame by a longer conversation or the streaming bubble.
    const cap = Math.max(PARSE_CACHE_MIN, messages.value.length + 64);
    while (parseMsgPartsCache.size > cap) {
        const oldest = parseMsgPartsCache.keys().next().value;  // least-recently-used
        if (oldest === undefined) break;
        parseMsgPartsCache.delete(oldest);
    }
};
watch([refreshingLinkMap, refreshedExpiredLinkMap, unavailableLinkMap], () => parseMsgPartsCache.clear());

// We drop the fresh-link cache on wall-clock LINK_REFRESH_WINDOW_MS boundaries
// 30, 45) so any visible link periodically reverts to its `_expired_.url`
// placeholder and the next click re-mints a new short-lived URL. This bounds how
// long a stored href is trusted. It MUST stay shorter than the minted URL's
// lifetime (EXPIRED_LINK_REFRESH_EXPIRES_SECONDS) or a cached href could be
// served after its URL already expired — 15min window < 20min TTL, with margin.
// (LINK_REFRESH_WINDOW_MS and EXPIRED_LINK_REFRESH_EXPIRES_SECONDS are imported
// from the engine: the widget needs the same two numbers, and the window is
// derived from the TTL there so the "cache must expire first" invariant holds by
// construction rather than by comment.)
let refreshedLinkExpiryTimer: ReturnType<typeof setTimeout> | null = null;

const msUntilNextLinkExpiryBoundary = () => {
    const now = Date.now();
    const nextBoundary = Math.ceil(now / LINK_REFRESH_WINDOW_MS) * LINK_REFRESH_WINDOW_MS;
    // If we're already exactly on a boundary, schedule for the next one so
    // we don't fire a zero-delay timeout that races the URL we just stored.
    return Math.max(1, nextBoundary - now);
};

const expireAllRefreshedLinks = () => {
    if (Object.keys(refreshedExpiredLinkMap.value).length) {
        refreshedExpiredLinkMap.value = {};
    }
    // Failures expire on the same boundary. A mint can fail because the file is
    // gone, but it can also fail because the network blinked, and a chip greyed
    // out by a five-second outage that stays grey for the life of the tab is its
    // own bug. The boundary is already a re-render, so recovery costs nothing.
    if (Object.keys(unavailableLinkMap.value).length) {
        unavailableLinkMap.value = {};
    }
};

const scheduleNextLinkExpiryBoundary = () => {
    if (refreshedLinkExpiryTimer) {
        clearTimeout(refreshedLinkExpiryTimer);
        refreshedLinkExpiryTimer = null;
    }
    refreshedLinkExpiryTimer = setTimeout(() => {
        expireAllRefreshedLinks();
        scheduleNextLinkExpiryBoundary();
    }, msUntilNextLinkExpiryBoundary());
};

const ensureLinkExpiryBoundaryScheduled = () => {
    if (!refreshedLinkExpiryTimer) {
        scheduleNextLinkExpiryBoundary();
    }
};

const isLinkRefreshing = (part: MsgPart) =>
    part.type === "link" && !!refreshingLinkMap.value[part.expiredHref || part.href];

// A url for this file could not be produced, so the chip stops pretending it is
// a link: ✕ instead of ↗, greyed, no href and no click. The failure is recorded
// under ONE key (whichever identifier the failing call had) and matched against
// all of a link's identifiers by the engine's isLinkUnavailable.
// Batched, because the trigger is per-IMAGE: a reply listing a folder of deleted
// pictures reports one failure per preview, and every write to this map clears
// the whole parse cache — one full re-run of marked over the conversation per
// dead image. One write per tick instead.
let pendingUnavailableKeys: string[] = [];
let pendingClearKeys: string[] = [];
let unavailableFlushQueued = false;

// One map write per tick for every mark AND every clear queued in it. Marks and
// clears share the flush so a tick that does both cannot cost two full re-parses,
// and so their order is decided here rather than by which callback ran first.
const queueUnavailableFlush = () => {
    if (unavailableFlushQueued) return;
    unavailableFlushQueued = true;
    setTimeout(() => {
        unavailableFlushQueued = false;
        const marks = pendingUnavailableKeys;
        const clears = pendingClearKeys;
        pendingUnavailableKeys = [];
        pendingClearKeys = [];
        if (!marks.length && !clears.length) return;
        const next = { ...unavailableLinkMap.value };
        marks.forEach((k) => { next[k] = true; });
        // Clears last: a key that both failed and painted in the same tick is
        // reachable, and a chip that stays alive is the recoverable mistake.
        clears.forEach((k) => { delete next[k]; });
        unavailableLinkMap.value = next;
    }, 0);
};

const markLinkUnavailable = (key: string) => {
    if (!key || unavailableLinkMap.value[key] || pendingUnavailableKeys.includes(key)) return;
    pendingUnavailableKeys.push(key);
    // Nothing has necessarily minted successfully yet, so the boundary timer that
    // clears this map may not be running.
    ensureLinkExpiryBoundaryScheduled();
    queueUnavailableFlush();
};

// A preview for this file just painted, so it is reachable and no chip for it
// should still read (unavailable).
//
// This is a RACE GUARD, not the general recovery: once a key is marked, the chip
// renders with no <img> at all (renderInlineLinkHtml drops the preview so a
// re-render cannot re-mint and re-fail forever), so nothing for that file can
// paint again and reach this. What it does catch is the window before the mark
// lands: failures are flushed on a timer, so one image can fail while a second
// element for the same file is still loading, and without this the survivor's
// success would be overwritten by the queued failure a tick later.
// Recovery for a settled mark stays where it was: the LINK_REFRESH_WINDOW_MS
// boundary that clears the whole map.
//
// Clears through the SAME batch as the mark. Every write to this map drops the
// entire parse cache, so a per-image write would re-run marked over the whole
// conversation once per repaired image, which is exactly the cost markLinkUnavailable
// batches to avoid.
const clearLinkUnavailable = (keys: string[]) => {
    const wanted = keys.filter(Boolean);
    if (!wanted.length) return;
    // Anything still queued to be marked is dropped outright: the file answered.
    if (pendingUnavailableKeys.length) {
        pendingUnavailableKeys = pendingUnavailableKeys.filter((k) => !wanted.includes(k));
    }
    const live = wanted.filter((k) => unavailableLinkMap.value[k] && !pendingClearKeys.includes(k));
    if (!live.length) return;
    pendingClearKeys.push(...live);
    queueUnavailableFlush();
};

const isLinkPartUnavailable = (part: MsgPart) =>
    part.type === "link" && isLinkUnavailable(part, unavailableLinkMap.value);

// The chip's leading glyph and trailing state, for the two template-rendered
// link paths. The v-html path gets the identical string from the engine's
// renderInlineLinkHtml.
const linkGlyph = (part: MsgPart) =>
    isLinkPartUnavailable(part) ? INLINE_LINK_UNAVAILABLE_GLYPH : INLINE_LINK_GLYPH;

const linkStateSuffix = (part: MsgPart) => {
    if (isLinkPartUnavailable(part)) return INLINE_LINK_UNAVAILABLE_SUFFIX;
    return isLinkRefreshing(part) ? " (fetching...)" : "";
};

// Label truncation (LINK_LABEL_MAX_DISPLAY_CHARS = 32, leading ellipsis so the
// filename stays visible) is the engine's truncateLabelForDisplay, imported above.


const clearRefreshingLink = (href: string) => {
    const updated = { ...refreshingLinkMap.value };
    delete updated[href];
    refreshingLinkMap.value = updated;
};

const fetchFreshHrefForExpiredLink = async (
    expiredHref: string,
    remotePath?: string,
    // Decides whether the opened tab DISPLAYS the file or downloads it.
    // get_signed_url only sets ResponseContentType when we pass one and otherwise
    // falls back to application/octet-stream, which always downloads. Image
    // previews pass the type the engine classified so a click shows the picture.
    contentType?: string,
): Promise<string> => {
    const cachedFresh = refreshedExpiredLinkMap.value[expiredHref];
    if (cachedFresh) {
        return cachedFresh;
    }

    const inFlight = refreshingLinkPromises.get(expiredHref);
    if (inFlight) {
        return inFlight;
    }

    const run = async () => {
        refreshingLinkMap.value = {
            ...refreshingLinkMap.value,
            [expiredHref]: true,
        };

        const resolvedRemotePath = remotePath || extractRemotePathFromAttachmentHref(expiredHref);
        if (!resolvedRemotePath) {
            throw new Error("Unable to refresh this expired attachment link.");
        }

        // Plain get-db presigned URL (NO generate_temporary_cdn_url) so `expires`
        // is honored and the click link is genuinely short-lived. TTL is kept above
        // the 15-min cache-drop window so a cached href never outlives its URL.
        const tempUrl = await currentService.value.getTemporaryUrl({
            request: "get-db",
            path: resolvedRemotePath,
            expires: EXPIRED_LINK_REFRESH_EXPIRES_SECONDS,
            ...(contentType ? { contentType } : {}),
        });
        const freshHref = typeof tempUrl === "string" ? tempUrl : tempUrl.url;
        refreshedExpiredLinkMap.value = {
            ...refreshedExpiredLinkMap.value,
            [expiredHref]: freshHref,
        };
        ensureLinkExpiryBoundaryScheduled();
        return freshHref;
    };

    const promise = run()
        .finally(() => {
            refreshingLinkPromises.delete(expiredHref);
            clearRefreshingLink(expiredHref);
        });

    refreshingLinkPromises.set(expiredHref, promise);
    return promise;
};

// Image previews are hydrated AFTER render, never during the parse. Every stored
// file classifies as expired with the _expired_.url placeholder as its href, so
// the url an <img> needs does not exist until something mints it, and minting
// through refreshedExpiredLinkMap would trip the watcher that clears the entire
// parse cache: one mint per visible image would re-run marked over the whole
// conversation, once per image. The engine holds the minted urls in a plain
// non-reactive cache instead, and the src is written straight onto the element.
//
// The mint goes through a CACHEABLE GET (browser_cache). A presigned url is a
// fresh SigV4 query string every time, so without it each reload mints new urls,
// every one a new cache key, and every visible image downloads again. `refresh`
// is how the error path escapes that cache once the url it cached has expired.
const imagePreviewCtx = () => ({
    scope: currentServiceId(),
    mint: async (remotePath: string, contentType: string, refresh?: boolean): Promise<string> => {
        const tempUrl = await currentService.value.getTemporaryUrl({
            request: "get-db",
            path: remotePath,
            // PREVIEW ttl, not the click ttl: this url never leaves the page, and
            // its length is what decides how long the picture stays locally
            // reusable (the server will not cache a mint past the life of the
            // credential in it).
            expires: PREVIEW_URL_EXPIRES_SECONDS,
            browser_cache: PREVIEW_BROWSER_CACHE_SECONDS,
            ...(refresh ? { refresh: true } : {}),
            ...(contentType ? { contentType } : {}),
        });
        return typeof tempUrl === "string" ? tempUrl : tempUrl.url;
    },
    // An image arriving late pushes the conversation down under the viewport.
    // Re-pin only if the user was already at the bottom. A paint is also proof
    // the file is reachable, so it lifts any mark an earlier failure left.
    onLoad: (path: string) => {
        clearLinkUnavailable(linkUnavailableKeysForPath(path));
        void scrollToBottomIfSticky(false);
    },
    // The resizes an <img> makes with no event of its own to announce them: the
    // src landing, and the src being dropped for a retry (which collapses an
    // already-painted picture to nothing). `load` and `error` are covered by the
    // listener on the message box, which also catches the markdown images this
    // module never sees.
    onLayoutChange: (img: unknown) => { chatScrollAnchor.absorb(img as any); },
    // Both failures the engine reports here mean the same thing to the user: the
    // mint was refused, or the url it minted would not load. Either way there is
    // no url for this file, so the caption chip left behind must not keep
    // offering a click that opens a dead tab.
    onError: (path: string, err: unknown) => {
        console.warn("Image preview failed", path, err);
        markLinkUnavailable(linkUnavailableKeyForPath(path));
    },
});

const hydrateMessageImagePreviews = () => {
    if (!anyImagePreviewEmitted) return;
    const box = messagesBox.value;
    if (!box) return;
    const nodes = box.querySelectorAll<HTMLImageElement>(
        "img.bq-img-preview:not([data-bq-img-state])",
    );
    if (!nodes.length) return;
    // A collapsed indexing row is one line and its label is the indexed file's
    // own path, which is often an image. Never mint for those.
    const list = Array.from(nodes).filter((n) => !n.closest(".bq-index-label"));
    if (list.length) hydrateImagePreviews(list, imagePreviewCtx());
};
// Synchronous rather than rAF-deferred: a warm-cache preview must get its src in
// the same block as the patch, or every re-render blanks an already-loaded image
// for a frame. The querySelectorAll is an attribute selector over the bubble list
// and anyImagePreviewEmitted short-circuits every conversation with no images.
onUpdated(hydrateMessageImagePreviews);

// The catch-all for everything the row watchers below cannot see.
//
// chatDisplayRowsShape only changes when the row SET changes (or one of a few
// group fields), which is the right trigger for a row appearing, disappearing or
// relocating — and blind to every height change INSIDE a row that is already
// there. A link chip re-parsed grey with a ✕, a compact bubble hydrating to its
// full text, an error line landing on a settled turn, the "Fetching history..."
// bar (which is sticky, therefore still in flow, therefore still takes height at
// the very top of the list) appearing and going away again: none of those touch
// a row key, all of them move everything below, and every one of them resolves
// on a promise while the reader is somewhere else in the conversation.
//
// This is cheap enough to run on every update — the reason the row watchers were
// kept narrow. hold() re-uses the anchored ELEMENT (Vue patches keyed rows in
// place, so the node survives), which makes it one getBoundingClientRect rather
// than a scan, and it returns immediately while the reader is pinned to the
// bottom — which is the whole of the streaming-typewriter case.
onUpdated(() => {
    // Same ordering rule as restoreRowScrollAnchor: measure only once the previews
    // in this patch have their src back, or hold() pays a delta computed against
    // collapsed pictures.
    hydrateMessageImagePreviews();
    anchorWroteSinceScroll = true;
    chatScrollAnchor.hold();
});

// Cache blob URLs keyed by (filename + content) so that re-renders (and the
// per-tick re-renders during typewriter) don't create a fresh Blob/ObjectURL
// every time. Without this, long streaming messages leak hundreds of URLs.
const fileBlobCache = new Map<string, string>();

const getOrCreateFileHref = (filename: string, body: string) => {
    const key = `${filename}\u0000${body}`;
    const existing = fileBlobCache.get(key);
    if (existing) return existing;
    // The engine decides how this format declares UTF-8: a BOM for a spreadsheet or
    // a text file, <meta charset> for HTML, \u escapes for RTF, nothing at all for
    // the formats that are UTF-8 by spec and that a BOM would break. Without it a
    // Korean CSV opens as CP949 mojibake in Excel, and so does every other
    // non-Latin script. Mirrored server-side in skapi-mcp/download-encoding.js.
    const prepared = prepareDownloadText(filename, body);
    const ext = extOf(filename);
    // mime covers the long tail the engine map does not name (images, archives).
    const type = EXT_CONTENT_TYPES[ext]
        || mime.getType(filename)
        || 'text/plain; charset=utf-8';
    const blob = new Blob([prepared.text], { type });
    const href = URL.createObjectURL(blob);
    fileBlobCache.set(key, href);
    return href;
};

const revokeFileBlobCache = () => {
    for (const href of fileBlobCache.values()) {
        try {
            URL.revokeObjectURL(href);
        } catch {
            // ignore
        }
    }
    fileBlobCache.clear();
};

// HTML escape used when assembling anchor strings that get spliced into
// marked-rendered HTML. Both element text and attribute values use this.
const escapeAttr = (s: string): string => s.replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[ch] as string));

type LinkMsgPart = Extract<MsgPart, { type: 'link' }>;

// Build the anchor HTML for an extracted file fence. Embedded into the
// markdown source via placeholder; marked passes raw HTML through.
const fileToAnchorHtml = (filename: string, href: string): string => {
    const text = `\u2197 ${filename}`;
    return `<a class="bq-file-download" href="${escapeAttr(href)}" download="${escapeAttr(filename)}" target="_blank" rel="noopener noreferrer">${escapeAttr(text)}</a>`;
};

// Build the anchor HTML for an extracted inline link/source. Carries
// data-* attributes that the click delegation handler reads to dispatch
// expired-link refresh through onMessageLinkClick.
// The markup is the ENGINE's (renderInlineLinkHtml). This was a byte for byte
// copy of the widget's emitter, and the image preview would have had to be
// written into both. Classification already lives in the engine for the same
// reason; the emitter follows it.
//
// `anyImagePreviewEmitted` is monotonic and is only a guard for the onUpdated
// hydration below. It is never read by the parse, so it cannot make a cached
// parse stale.
let anyImagePreviewEmitted = false;

const linkToAnchorHtml = (link: LinkMsgPart, allowImagePreview: boolean): string => {
    const unavailable = isLinkUnavailable(link, unavailableLinkMap.value);
    // An unavailable link renders no preview, so it must not claim one either:
    // the flag only gates the post-render hydration pass.
    if (allowImagePreview && link.image && !unavailable) anyImagePreviewEmitted = true;
    return renderInlineLinkHtml(link, {
        refreshing: !!refreshingLinkMap.value[link.expiredHref || link.href],
        allowImagePreview,
        unavailable,
    });
};

// Resolve a regex match from createInlineLinkRegex into a LinkMsgPart, or
// null if the match doesn't translate (e.g. unparseable storage path). For
// `src::<token>` matches the `tail` is any trailing text the token had
// trimmed off (punctuation/wrappers) — emitted as plain text after the
// link so the surrounding sentence reads naturally.
const buildLinkPartFromGroups = (
    full: string,
    g1: string | undefined,
    g2: string | undefined,
    g3: string | undefined,
    g4: string | undefined,
    g5: string | undefined,
    g6: string | undefined,
): { part: LinkMsgPart; tail?: string } | null =>
    // Classification is the ENGINE's (classifyInlineLink): what a link IS must
    // not be decided twice, once here and once in the widget. Those two copies
    // are where every link bug in this file's history lived, and each one had to
    // be found and fixed in both. This view now supplies only what is local to
    // it (the project, the db host, its own cache of already-minted hrefs) and
    // renders whatever comes back.
    classifyInlineLink(full, [g1, g2, g3, g4, g5, g6], {
        projectId: currentServiceId(),
        dbHostPrefix: `https://db.${hostDomain}`,
        resolveFreshHref: (expiredHref) => refreshedExpiredLinkMap.value[expiredHref],
    }) as { part: LinkMsgPart; tail?: string } | null;

const parseMsgParts = (content: string, opts?: { imagePreviews?: boolean }): MsgPart[] => {
    // Replace files/links/sources in the content with unique placeholder
    // tokens, run the result through `marked` for full markdown support
    // (bold, italic, headings, lists, tables, blockquotes, code blocks,
    // etc.), then splice the placeholders back as anchor HTML. The whole
    // message bubble is then rendered with a single v-html, and click
    // delegation on the bubble dispatches expired-link refreshes through
    // the existing onMessageLinkClick logic.
    //
    // imagePreviews: false — used by the collapsed indexing-row HEADER, whose
    // file chip must stay a plain inline chip: an is-image-preview block there
    // breaks the header's horizontal alignment. Cache key carries the mode so
    // the same text parsed both ways never collides.
    const noPreviews = opts?.imagePreviews === false;
    const cacheKey = (noPreviews ? "np" : "") + content;
    const cached = parseCacheGet(cacheKey);
    if (cached) return cached;
    // Set true if the output depends on transient typing state (the unclosed-
    // fence "[generating \u2026]" placeholder below, which also reads dotTick). Such
    // results are NOT cached \u2014 they must recompute while the reveal is in flight.
    let typingDependent = false;

    const placeholderHtml: string[] = [];
    // Use private-use Unicode codepoints as placeholder fences so they
    // never collide with normal text and survive marked unchanged.
    const PH = (idx: number) => `\uE000BQ${idx}\uE001`;
    const pushPlaceholder = (anchorHtml: string) => {
        const idx = placeholderHtml.length;
        placeholderHtml.push(anchorHtml);
        return PH(idx);
    };

    // 1. Replace closed file fences ```filename.ext\n...\n``` with
    //    placeholders pointing at a `<a class="bq-file-download">` anchor.
    let working = content.replace(
        /```([^\n`]+?\.[^\s.`]+)\n([\s\S]*?)```/g,
        (_full, filename: string, body: string) =>
            pushPlaceholder(fileToAnchorHtml(filename, getOrCreateFileHref(filename, body))),
    );

    // 2. Mid-typewriter unclosed fence: hide the streaming source until
    //    the closing ``` arrives so the user doesn't watch literal code
    //    scroll past character by character.
    if (typing.value) {
        const openFence = working.match(/```([^\n`]+?\.[^\s.`]+)\n?/);
        if (openFence && typeof openFence.index === 'number') {
            typingDependent = true;  // dots + placeholder change per frame — do not cache
            const dots = '.'.repeat((dotTick.value % 3) + 1);
            working = working.slice(0, openFence.index) + `\n[generating ${openFence[1]}${dots}]`;
        }
    }

    // 3. Mask inline backtick code spans so that markdown links / src::
    //    tokens *inside* code spans are not extracted (matches existing
    //    behavior — anything inside backticks is shown literally).
    const codeMasks: string[] = [];
    working = working.replace(/`[^`\n]+`/g, (match) => {
        const idx = codeMasks.length;
        codeMasks.push(match);
        return `\uE002C${idx}\uE003`;
    });

    // 4. Replace inline link tokens with placeholders. Reuse the existing
    //    branch logic via buildLinkPartFromGroups so behavior is identical
    //    to the pre-markdown renderer.
    // Each preview costs a presign call and an image download once hydrated, and
    // a reply listing a folder can name dozens. Past the budget a link renders as
    // the ordinary text chip. `previewsLeft` is a local of this call, so the
    // output stays a function of `content` plus the watched link maps and the
    // memoization contract above is unchanged.
    let previewsLeft = noPreviews ? 0 : IMAGE_PREVIEWS_PER_MESSAGE;
    const linkRe = createInlineLinkRegex();
    working = working.replace(linkRe, (full, ...args) => {
        const [g1, g2, g3, g4, g5, g6] = args as (string | undefined)[];
        const built = buildLinkPartFromGroups(full, g1, g2, g3, g4, g5, g6);
        if (!built) return full;
        const allow = previewsLeft > 0;
        const placeholder = pushPlaceholder(linkToAnchorHtml(built.part, allow));
        if (allow && built.part.image) previewsLeft--;
        return placeholder + (built.tail || '');
    });

    // 5. Restore the masked inline-code spans so marked formats them.
    working = working.replace(/\uE002C(\d+)\uE003/g, (_m, idx) => codeMasks[Number(idx)] || '');

    // 6. Render markdown. GFM tables, fenced code, etc. `breaks: true`
    //    keeps single newlines as <br>, matching plain-text chat feel.
    const html = marked.parse(working, {
        gfm: true,
        breaks: true,
        async: false,
    }) as string;

    // 7. Splice placeholders back in.
    const finalHtml = html.replace(
        /\uE000BQ(\d+)\uE001/g,
        (_m, idx) => placeholderHtml[Number(idx)] || '',
    );

    const result: MsgPart[] = [{ type: 'html', html: finalHtml }];
    // Cache only typing-independent results. The actively-typing bubble's
    // intermediate strings (each a distinct `content`) get cached too, but the
    // LRU bound evicts them as new frames arrive while re-read settled bubbles
    // stay hot — so the map converges to ~one entry per settled message.
    if (!typingDependent) parseCacheSet(cacheKey, result);
    return result;
};

/* ---- collapsed background-indexing rows ---------------------------------
 * One file's indexing turns (the first pass plus every CONTINUE pass, each
 * with a request AND a response bubble) render as a SINGLE status row, however
 * far apart they sit and whatever the user asked in between. The grouping
 * itself is the shared engine's (buildChatDisplayList), so the BunnyQuery
 * widget collapses identically; only this markup is view-side.
 */
// Status glyphs are inlined SVG, never Material Symbols: index.html gates icon
// font glyphs behind an `icon_names=` allowlist, and the widget has no icon
// font at all. The active one is spun by CSS (.bq-index-group.is-active).
const INDEX_ICON_ACTIVE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.5 12a8.5 8.5 0 0 1-8.5 8.5 8.5 8.5 0 0 1-7.6-4.7"/><path d="M3.5 12A8.5 8.5 0 0 1 12 3.5a8.5 8.5 0 0 1 7.6 4.7"/><path d="M20 3.6v4.8h-4.8"/><path d="M4 20.4v-4.8h4.8"/></svg>';
const INDEX_ICON_DONE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5 5L20 6.5"/></svg>';
const INDEX_ICON_ERROR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5"/><path d="M12 16.6h.01"/></svg>';
const INDEX_ICON_CANCELLED = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8.2 8.2l7.6 7.6"/></svg>';
// "Not known yet" — a clock, deliberately NOT the circular arrow: a spinning sync
// glyph is how this row says WORK IS HAPPENING, and the whole point of this state
// is that it is not claiming that. CSS fades it instead of spinning.
const INDEX_ICON_PENDING = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.4V12l3.3 2"/></svg>';

// Identifies the turn a collapsed row is currently anchored at, so the scroll
// anchor can tell that the row RELOCATED (an older page moved the run's start)
// rather than merely gaining a pass. Straight from the engine: WHICH member the
// row renders at is buildChatDisplayList's decision, and re-deriving it here as
// "the last member" made this disagree with the engine the moment the anchor
// moved to the run's FIRST pass. An id, not an array index: a prepend renumbers
// indices without moving anything. "" when that turn has no id yet, which reads
// as "cannot tell" and simply skips the moved-row check.
const indexGroupAnchorId = (group: IndexingGroup): string => group.anchorId || "";

// Head and tail must never contradict each other, so both read `finished` BEFORE
// status: a run between two passes has status "done" and is not finished, and a
// check-mark reading "Indexed" over a still-spinning tail is the exact claim this
// is meant to stop making. Cancelled is tested first because a stop is final
// whatever else is true, then `resolving` — which outranks `finished` for the same
// reason `finished` outranks status: it is the coarsest thing known, and both of
// the answers below it are derived from data still arriving. Mirrored verbatim in
// the widget's indexGroupIcon/Verb.
const indexGroupIcon = (group: IndexingGroup): string =>
    group.status === "cancelled" ? INDEX_ICON_CANCELLED
        : group.resolving ? INDEX_ICON_PENDING
            : !group.finished ? INDEX_ICON_ACTIVE
                : group.status === "error" ? INDEX_ICON_ERROR
                    : INDEX_ICON_DONE;

const indexGroupVerb = (group: IndexingGroup): string => {
    if (group.status === "cancelled") return "Indexing cancelled:";
    // Name the wait. "Loading history" is a statement about the CHAT being paged in,
    // not about the file — the row deliberately says nothing about the file until it
    // has the run's beginning to say it from.
    //
    // Short on purpose. The label is one nowrap ellipsized line shared with the file
    // name, and at the widget's narrow end (~360px) the row has roughly 196px for
    // both: a 26-character verb ate the name down to a few characters for the whole
    // wait, which trades one confusing row for another.
    if (group.resolving) {
        return group.resolvingReason === "history" ? "Loading history:" : "Checking status:";
    }
    if (!group.finished) return group.isReindex ? "Reindexing" : "Indexing";
    if (group.status === "error") return "Indexing failed:";
    return group.isReindex ? "Reindexed" : "Indexed";
};

// The file renders as a BARE storage-path markdown link, the same form the
// uncollapsed bubble used, so parseMsgParts marks it expired and a click mints
// a fresh temporary URL (see formatIndexingLabel).
const indexGroupLabel = (group: IndexingGroup): string => {
    const nameLabel = group.path ? `[${group.name}](${group.path})` : group.name;
    return `${indexGroupVerb(group)} ${nameLabel}`;
};

// Passes LOADED, never a server-side total: history pages newest-first, so a
// scroll-up can always reveal more. `+` marks a run whose start is still unpaged.
// A run:: stub has NO loaded passes at all — "0+ passes" is noise, so it shows
// none until real passes arrive and the real group replaces it.
const indexGroupCount = (group: IndexingGroup): string => {
    if (group.stub) return "";
    if (group.passCount <= 1 && !group.mayHaveOlder) return "";
    return `${group.passCount}${group.mayHaveOlder ? "+" : ""} passes`;
};

// Stop indexing this file: cancels every queued/running pass of it and keeps the
// client from dispatching the next one. Passes that already ran keep the records
// they wrote — this stops the remaining work, it does not undo what was indexed.
//
// The row's Stop only ASKS. Cancelling is not resumable from the row (the queued
// passes are dropped, not paused, so finishing the file means reindexing it from
// the start), and the button sits inside a one-line row the user is often just
// trying to expand — one stray click should not throw away an hour of indexing.
// confirmStopIndexGroup does the actual cancelling.
//
// Is there still work to stop? NOT "does the row have a cancellable pass right
// now", which is what this used to ask. A worker-driven file (any PDF, and every
// windowed read) has no pass in this client between two passes: the worker mints
// the next one itself and the client only learns about it a beat later. That gap
// is seconds long, happens once per pass, and is the most likely moment for a user
// to be looking at the row — so keying the button and the dialog off a live pass
// made both blink out mid-run, and a confirm that landed in the gap cancelled
// nothing at all while the worker carried on. `finished` is the question actually
// being asked, and for a worker run it is answered by the queue itself; `resolving`
// keeps it from being asked before anything is known.
// A run:: STUB is never stoppable from here: its passes are not loaded (there
// is nothing proven to cancel), and the record may describe ANOTHER user's run,
// whose queue this client's cancel cannot reach — the real group carries the
// real Stop the moment bg history lands.
const indexGroupStoppable = (group: IndexingGroup): boolean =>
    !!group && !group.stub && !group.finished && !group.resolving && !group.stopped && !group.cancelling;
const stopIndexRunKey = ref("");
const stopIndexFileKey = ref("");
const cancelIndexGroup = (group: IndexingGroup) => {
    if (!indexGroupStoppable(group)) return;
    stopIndexRunKey.value = group.runKey;
    stopIndexFileKey.value = group.key;
};

// "Jul 24, 2026, 3:42 PM" under a settled bubble; empty (so the row hides) while
// a turn is still pending or for a bubble with no timestamp yet. Shared formatter
// so the widget renders identically.
// NEVER on a bubble that is still running, and streaming is what makes that worth
// stating: a live turn now takes the markdown branch WHILE it runs, and a
// placeholder mapped from history carries the REQUEST time in _ts, so it would
// date the answer before it exists (the settle re-stamps it with the response
// time). Before streaming, the spinner branch made this implicit. Mirrored in the
// widget's buildMessageEl.
const bubbleTime = (msg: ChatMessage): string => {
    let ts = msg?.isPending ? "" : formatChatTimestamp(msg?._ts);
    // How long an indexing pass took, appended to its own end time. `_tsStart` is
    // stamped only on an indexing pass's reply, so this needs no second test to
    // stay off ordinary answers. Empty for anything under a second, which is what
    // a one-sided server timestamp collapses to. Mirrored in the widget.
    if (ts && typeof msg?._ts === "number" && typeof msg?._tsStart === "number") {
        const dur = formatDuration(msg._ts - msg._tsStart);
        if (dur) ts += " (" + dur + ")";
    }
    return ts;
};

// Which collapsed rows the user opened. Keyed by FILE (group.key), and reset
// whenever the chat changes — the key is a project-relative storage path, and
// this one component instance serves every project.
//
// By file, NOT by run (group.runKey): a run is named after its first LOADED pass,
// so it is renamed the moment an earlier pass arrives — routine while a
// worker-driven chain runs, because an adopted pass reaches the client before the
// ones before it are paged in. Keyed by run, a row the user had opened closed
// itself every time that happened. See IndexingGroup.key.
const expandedIndexGroups = ref<Set<string>>(new Set());
let expandedIndexGroupsChatKey = "";
const toggleIndexGroup = (key: string) => {
    // Replace rather than mutate: a Set's contents are not reactive in Vue 3.
    const next = new Set(expandedIndexGroups.value);
    const opening = !next.has(key);
    if (opening) next.add(key);
    else next.delete(key);
    // COMMIT FIRST: the lazy fetch below guards every step on "is this row
    // still open" — kicked before the assignment it read the OLD set, decided
    // the row was closed, and silently did nothing. That was the dead expand.
    expandedIndexGroups.value = next;
    if (opening) {
        // The row's settled replies may be compact stubs (split fetch) — fetch
        // their real bodies now that someone actually wants to read them.
        void hydrateCompactGroup(key);
        // And an INCOMPLETE row (a run:: stub with no passes loaded, or a run
        // whose start is above the loaded window) starts paging its history in
        // now — opening the row is the demand signal the lazy split fetch was
        // waiting for.
        void loadIndexGroupHistory(key);
    }
    // Collapsing a row can drop the list below one screen, which removes the
    // scroll-to-top pager trigger along with the height.
    void ensureHistoryFillsViewport();
};

// ── expand-triggered lazy history fetch ─────────────────────────────────────
// An opened row whose passes are not (fully) loaded pages older history until
// its run is complete on screen, the history genuinely ends, or a cap trips.
// The row shows a spinner in its head and a loading note in its body the whole
// time — whatever its status verdict is (a green row can be fetching detail).
const INDEX_GROUP_FETCH_MAX_PAGES = 40;
const indexGroupFetching = reactive(new Set<string>());
const groupNeedsHistory = (key: string): boolean => {
    if (historyEndOfList.value) return false;
    const row = chatDisplayRows.value.find(
        (r) => r.kind === "group" && r.group.key === key,
    ) as Extract<ChatDisplayRow, { kind: "group" }> | undefined;
    if (!row) return false;
    const g: any = row.group;
    return !!(g.stub || g.mayHaveOlder);
};
const loadIndexGroupHistory = async (key: string) => {
    if (indexGroupFetching.has(key)) return;
    if (!groupNeedsHistory(key)) return;
    const svc = currentService.value?.id;
    indexGroupFetching.add(key);
    try {
        let pages = 0, waits = 0;
        while (pages < INDEX_GROUP_FETCH_MAX_PAGES) {
            if (currentService.value?.id !== svc) return;      // project switched
            if (!expandedIndexGroups.value.has(key)) return;   // closed: stop paying
            if (!groupNeedsHistory(key)) break;
            if (loadingOlderHistory.value || historyFilling.value || bgHistoryLoading.value) {
                // Another pager (or the deferred stub batch) owns the wire; its
                // result may already contain what this row needs. Bounded: a
                // stuck flag must not pin the spinner forever.
                if (++waits > 240) return;
                await new Promise((r) => setTimeout(r, 250));
                continue;
            }
            pages++;
            await fetchHistoryPage(true);
        }
        // Newly-paged passes may be compact stubs — fetch their real bodies.
        void hydrateCompactGroup(key);
    } finally {
        indexGroupFetching.delete(key);
    }
};

// Fetch the full response bodies for an expanded row's compact passes. The
// mechanics (point lookups, per-chat memo so refreshes can't revert hydrated
// bubbles, dedup, cache update) live in the ENGINE (hydrateCompactItems) so
// the widget shares them; this wrapper only maps the group to its item ids.
const hydrateCompactGroup = (groupKey: string) => {
    const row = chatDisplayRows.value.find(
        (r) => r.kind === "group" && r.group.key === groupKey,
    ) as Extract<ChatDisplayRow, { kind: "group" }> | undefined;
    if (!row) return;
    const ids = row.group.members
        .map((m: any) => m.msg)
        .filter((m: any) => m && m._compact && m.role === "assistant" && m._serverItemId && !m.isError && !m.isPending)
        .map((m: any) => m._serverItemId as string);
    if (ids.length) void chatSession.hydrateCompactItems(ids);
};

type ChatDisplayRow =
    | { kind: "message"; msg: ChatMessage; index: number; inGroup: boolean; key: string }
    | { kind: "group"; group: IndexingGroup; index: number; key: string }
    // The trailing "still working" loader of an OPEN group. Carries no message and
    // no index: it is the group's own mark, not a turn.
    | { kind: "tail"; key: string };

// Flat render list: the group's own turns are emitted as ordinary message rows
// right AFTER its header when expanded (rather than nested inside it) so the
// bubble markup below stays single-sourced.
// Record every settled indexing verdict where the FILES page can see it
// (chatIndexVerdicts in file.ts): a first visit to the files page then paints
// its badges from what this page already established instead of re-proving it
// from grey. Rows arrive in conversation order, so a later (newer) run of the
// same file overwrites the earlier one's verdict — the newest run's state wins.
const recordChatIndexVerdicts = (rows: ChatDisplayRow[]) => {
    const svc = currentService.value?.id;
    if (!svc) return;
    // The engine session is a module singleton: at mount (and right after a
    // project switch) messages can still hold the PREVIOUS chat's rows while
    // currentService already names the new one — recording then would file the
    // old project's verdicts under the new service id (the cross-project leak
    // class). The gate is up exactly until THIS chat's history has landed.
    if (initialGateLoading.value) return;
    // The gate alone is not enough: chatDisplayRows recomputes the moment
    // currentService flips (its clear-horizon read depends on it) and its
    // watcher is registered BEFORE the gate's, so this used to run with the
    // OLD project's rows while `svc` already named the NEW one. These two are
    // written only after THIS project's own sweep resolved, so they cannot be
    // true while the rows still belong to the previous chat.
    if (markerRefsSvc !== svc || !markerSweepSettled.value) return;
    if (!chatIndexVerdicts[svc]) chatIndexVerdicts[svc] = new Map();
    const verdicts = chatIndexVerdicts[svc];
    for (const row of rows) {
        if (row.kind !== "group") continue;
        const g: any = row.group;
        if (!g?.key) continue;
        // run:: stubs are DERIVED from the same sweep the files page already
        // reads directly — filing verdicts from them adds nothing and lets a
        // stale stub overwrite (or delete) evidence from real rows. Loaded
        // passes only.
        if (g.stub) continue;
        // Unknown yet: keep whatever we had rather than guessing either way.
        if (g.resolving) continue;
        // Stopped / cancelled / errored runs are NOT indexed; a RUNNING newest
        // run supersedes any earlier green.
        if (g.stopped || g.cancelling || g.status === "cancelled" || g.status === "error" || !g.finished || g.status !== "done") {
            verdicts.delete(g.key);
            continue;
        }
        // 'single' driver: one settled pass IS the whole job (deterministic).
        // A done:: marker is durable authority wherever it came from. Everything
        // else is a queue-idle inference — display-grade only.
        const durable = g.driver === "single" || !!doneMarkerKeys.value[g.key];
        verdicts.set(g.key, { state: durable ? "confirmed" : "probable", at: Date.now() });
        // Mint-on-observe: loaded rows just PROVED a run over that the run::
        // records don't know about — either a 'working' record whose settle no
        // client ever observed (tab closed mid-run), or NO record at all (a
        // LEGACY run from before run:: existed). Same write for both: the
        // deterministic bar ('confirmed') is the same authority that mints
        // done::, so creating the record here is what moves a legacy run onto
        // the fast path — every later visit paints it from the sweep instead
        // of re-proving it. Real storage path only, once per path per visit.
        if (durable && g.path && !mintObservedPaths.has(g.path)) {
            const runRec = runStubRecords.value[g.path];
            if (!runRec || runRec.status === "working") {
                mintObservedPaths.add(g.path);
                const firstTs = g.members?.[0]?.msg?._ts;
                const lastTs = g.members?.[g.members.length - 1]?.msg?._ts;
                // `platform` is not optional here: a record minted without it is shown
                // in BOTH chats, so a run finished under one model painted a green
                // "Indexed" row into the other model's conversation.
                const mintPlatform = selectedAiPlatform.value === "claude" || selectedAiPlatform.value === "openai"
                    ? selectedAiPlatform.value : undefined;
                void upsertIndexRunRecord(svc, g.path, {
                    status: "done",
                    ...(typeof firstTs === "number" ? { started: firstTs } : {}),
                    finished: typeof lastTs === "number" ? lastTs : Date.now(),
                    ...(g.name ? { filename: g.name } : {}),
                    ...(mintPlatform ? { platform: mintPlatform } : {}),
                });
                // Patch the LOCAL views of the record too: the shared sweep
                // cache serves 'working' for up to its TTL, and a remount
                // re-reading it re-fired this whole block (visible as a
                // per-refresh "already taken" 400 before the lookup-first
                // upsert; now it would be a pointless read).
                noteRunRecordStatus(svc, g.path, "done", {
                    ...(g.name ? { filename: g.name } : {}),
                    ...(typeof firstTs === "number" ? { started: firstTs } : {}),
                    ...(mintPlatform ? { platform: mintPlatform } : {}),
                });
                if (runRec) {
                    runStubRecords.value = { ...runStubRecords.value, [g.path]: { ...runRec, status: "done" } };
                }
            }
        }
    }
};

const chatDisplayRows = computed<ChatDisplayRow[]>(() => {
    const rows: ChatDisplayRow[] = [];
    const entries = buildChatDisplayList(messages.value as any, {
        hasMoreHistory: !historyEndOfList.value,
        // Older pages coming in RIGHT NOW, which is what lets a row whose run
        // begins above the loaded window say "still loading" instead of guessing.
        // historyFilling, not just the per-request flag: the viewport fill is many
        // pages and that flag drops to false between every one of them.
        loadingOlderHistory: loadingOlderHistory.value || historyFilling.value,
        // What the server still has indexing work for. It is the only thing that can
        // tell a worker-driven run's "between passes" from its "finished"; until it
        // has been answered, `checked` is false and such a run reads as still working.
        liveIndexKeys: liveIndexKeys.value,
        liveIndexChecked: liveIndexChecked.value,
        // Durable completion markers: settle worker-run greens without waiting
        // for the queue answer (see the doneKeys contract in the engine).
        doneKeys: doneMarkerKeys.value,
        // Runs the user stopped. A stop that landed on a RUNNING pass leaves no
        // cancelled bubble behind (that pass finishes and answers normally), so
        // without this the row reports the stop as a finished "Indexed".
        stoppedIndexIds: stoppedIndexIds.value,
        // Durable run:: records: rows for runs whose passes are not loaded (bg
        // history deferred, or older than the paging cap). Real groups win.
        runStubs: runStubRecords.value,
        // Records are service-wide and horizon-blind; without this every
        // "Clear chat history" resurrected one row per indexed file.
        // clearHorizonTick makes the localStorage read reactive.
        stubClearedAt: (clearHorizonTick.value, getClearedAt()),
        // A run:: record is per FILE; a chat is per (project, PLATFORM). Without
        // this the Claude chat's runs rendered as rows in the same project's
        // ChatGPT chat, where their passes can never load and the queue probe
        // can never see them (unresolvable by construction).
        stubPlatform: selectedAiPlatform.value === "claude" || selectedAiPlatform.value === "openai"
            ? selectedAiPlatform.value : undefined,
    });
    // Row keys must be stable across an older-page PREPEND. Keying by array
    // index was not: a prepend renumbers every row, so Vue tore down and rebuilt
    // the entire message list on each page — losing DOM state and any chance of
    // holding the scroll position on content that had not actually changed. The
    // server item id survives paging; the role disambiguates the request and
    // response bubbles that share one id. In-flight local bubbles have neither
    // and fall back to the index, which is safe: they only ever sit at the very
    // bottom, where a prepend never reaches.
    //
    // A STAGED turn is the exception that made _localId mandatory rather than
    // incidental: its indexing rows are inserted ABOVE it as each file starts, so
    // its index moves several times before it is ever sent. Keyed by index it was
    // torn down and remounted on every file. stageOutgoingMessage mints a _localId
    // and dispatchComposedMessage carries it onto the replacement, so the bubble
    // keeps one key from Send to answer.
    const seen = new Set<string>();
    const keyFor = (msg: ChatMessage, index: number): string => {
        const id = msg._serverItemId || msg._localId;
        let key = id ? `s${id}:${msg.role}` : `i${index}`;
        // Duplicate keys silently corrupt a keyed diff, so never emit one even if
        // a merge upstream ever produced two bubbles with the same id and role.
        if (seen.has(key)) key = `${key}#${index}`;
        seen.add(key);
        return key;
    };
    for (const entry of entries) {
        if (entry.kind === "message") {
            rows.push({ kind: "message", msg: entry.msg as ChatMessage, index: entry.index, inGroup: false, key: keyFor(entry.msg as ChatMessage, entry.index) });
            continue;
        }
        rows.push({ kind: "group", group: entry.group, index: entry.index, key: `g${entry.group.runKey}` });
        if (!expandedIndexGroups.value.has(entry.group.key)) continue;
        // visibleMembers, not members: a CONTINUE pass's request bubble only repeats
        // the row's own header, and the running placeholder is superseded by the
        // group's own loader below. members stays the full list for every
        // count/status/cancel/anchor decision.
        for (const member of entry.group.visibleMembers) {
            rows.push({ kind: "message", msg: member.msg as ChatMessage, index: member.index, inGroup: true, key: keyFor(member.msg as ChatMessage, member.index) });
        }
        // Still working, or not confirmed otherwise. Keyed off runKey ALONE — never
        // off passCount or status, which change every pass and would remount the
        // element and restart its animation each time.
        //
        // NOT while resolving, for the same reason the head refuses to spin there:
        // an animated rail under an open row is the "work is happening here" signal,
        // and this state exists to withhold exactly that claim. It also has nothing
        // to point at — the passes a 'history' wait is missing are EARLIER ones,
        // which arrive above the row, not below it.
        //
        // Cancelled is tested here for the same reason indexGroupIcon tests it
        // first: a stop is final whatever else is true. The engine does already
        // force `finished` on a cancelled run, so this can never fire — but the head
        // does not lean on that and neither should the tail, which is the whole
        // point of the two being one claim made twice.
        if (!entry.group.finished && !entry.group.resolving && entry.group.status !== "cancelled") {
            rows.push({ kind: "tail", key: `t${entry.group.runKey}` });
        }
    }
    return rows;
});

// Feed the files page's badge cache from whatever this page just resolved.
// Watching the computed (not duplicating its inputs) keeps the recorder in
// lockstep with what the user actually sees. NOT immediate: at registration the
// singleton session may still hold the previous project's rows (the recorder
// also guards on the gate); the gate clearing after history lands recomputes
// the rows and fires this watcher anyway.
watch(chatDisplayRows, (rows) => {
    recordChatIndexVerdicts(rows);
    // A fresh 'working' stub has no settle path of its own: no poll, no queue
    // entry, no worker adoption — only a fresh sweep can flip it. While one is
    // on screen (and the queue does not already prove the file live), re-sweep
    // on a cadence that matches the sweep TTL so the row settles in-session.
    // Any non-terminal stub qualifies: 'active' (queue-confirmed) AND the grey
    // resolving 'status' shape a working-claim record now renders as — both
    // settle only through a fresh sweep. BOUNDED: after several sweeps in which
    // the same stub set came back unchanged, the records are dead and the loop
    // stops (it re-arms the moment the set changes); ticks landing on a hidden
    // tab defer instead of sweeping.
    // Signature from the RECORDS, not the row shape: a row that is correctly
    // yellow (queue-confirmed live) used to count, so a genuinely running file
    // burned every round in its first minutes and the loop was already
    // exhausted at the exact moment the run ended and new information arrived.
    const openStubKeys = Object.keys(runStubRecords.value)
        .filter((p) => {
            const rec = runStubRecords.value[p];
            if (!rec || rec.status !== "working") return false;
            if (liveIndexKeys.value[p]) return false;
            if (doneMarkerKeys.value[p]) return false;
            return true;
        })
        .sort();
    if (openStubKeys.length) {
        const sig = openStubKeys.join("|");
        if (sig !== stubRecheckSig) { stubRecheckSig = sig; stubRecheckRounds = 0; }
        if (stubRecheckRounds < STUB_RECHECK_MAX_ROUNDS && stubRecheckTimer === null) {
            stubRecheckTimer = setTimeout(() => {
                stubRecheckTimer = null;
                if (agentUnmounted) return;
                if (document.hidden) return; // re-armed by the next visible render
                stubRecheckRounds++;
                refreshDoneMarkers(true);
            }, STUB_RECHECK_MS);
        }
    } else {
        stubRecheckSig = "";
        stubRecheckRounds = 0;
        if (stubRecheckTimer !== null) {
            clearTimeout(stubRecheckTimer);
            stubRecheckTimer = null;
        }
    }
});

/* ---- stop-indexing confirmation ----------------------------------------
 * The dialog holds the run KEY, not the group object: chatDisplayRows is rebuilt
 * on every notify, so a group captured at click time goes stale within seconds
 * and a pass queued while the dialog sat open would be missing from the
 * cancellableIds we would then cancel. Re-resolving also closes the dialog on its
 * own when the file finishes (or fails, or is already being cancelled) while the
 * user is reading it — the row's Stop is gone by then, so the confirm must go
 * too rather than sit there offering to cancel nothing. */
const stopIndexTarget = computed<IndexingGroup | null>(() => {
    if (!stopIndexRunKey.value) return null;
    // Matched by runKey; the FILE key is the fallback for the one thing that
    // renames a run under an open dialog — an older history page arriving with
    // the run's true first pass, which the run is named after. Without it the
    // confirm would silently cancel nothing.
    let byFile: IndexingGroup | null = null;
    for (const row of chatDisplayRows.value) {
        if (row.kind !== "group") continue;
        const live = indexGroupStoppable(row.group) ? row.group : null;
        if (row.group.runKey === stopIndexRunKey.value) return live;
        if (!byFile && live && stopIndexFileKey.value && row.group.key === stopIndexFileKey.value) byFile = row.group;
    }
    return byFile;
});

const closeStopIndexModal = () => {
    stopIndexRunKey.value = "";
    stopIndexFileKey.value = "";
};

const confirmStopIndexGroup = () => {
    const group = stopIndexTarget.value;
    closeStopIndexModal();
    if (group) chatSession.cancelIndexingGroup(group);
};

/* ---- holding the reader's place ----------------------------------------
 * The chat mutates without the reader asking it to: an older page prepends, a
 * poll resolves, an indexing row splices in or grows a "N passes" label, a link
 * chip is re-parsed grey, an image preview finishes decoding, the "Fetching
 * history..." bar comes and goes. Each of those changes the height of something
 * that may sit ABOVE the viewport, and the browser answers by keeping scrollTop,
 * which slides the sentence being read out from under the reader.
 *
 * The implementation is the engine's (engine/scroll_anchor.ts), so the widget
 * behaves identically. It is used in two shapes here:
 *
 *   capture/restore, from the pre and post watchers below — for the row SET
 *   changing, which is the one mutation this view can bracket exactly.
 *
 *   remember/hold — remember() from onHistoryScroll (so the anchor is always the
 *   reader's own last position), hold() from onUpdated and from an image
 *   preview's layout hook, for everything that resolves on a promise and can
 *   therefore not be bracketed at all.
 *
 * Both skip while the reader is pinned to the bottom; there the scrollToBottom*
 * paths own the position.
 *
 * A collapsed indexing row is a WEAK anchor because it can still relocate (an
 * older page moving the run's start); pinning it while it moves is what would
 * drag the reader along with it. So an ordinary message row is always preferred,
 * and a group row is used only when nothing else is on screen — and then only if
 * it did not move (data-row-pos names the turn it is anchored at).
 */
// One implementation, shared with the widget (engine/scroll_anchor.ts). No
// rawFallback here: Vue patches the list in place rather than tearing it down,
// so when the anchor row is gone the browser has already kept a sane position
// and re-imposing a stale offset would be worse than leaving it alone.
const chatScrollAnchor = createScrollAnchor({
    getBox: () => messagesBox.value as any,
    isStuck: () => stickToBottom,
});
let rowScrollAnchor: ReturnType<typeof chatScrollAnchor.capture> = null;
const captureRowScrollAnchor = () => { rowScrollAnchor = chatScrollAnchor.capture(); };
const restoreRowScrollAnchor = () => {
    // Hydrate BEFORE measuring. Vue queues onUpdated hooks and flush:'post'
    // watchers in the same post-flush pass, so nothing guarantees hydration runs
    // first — and a preview that has not been given its src back yet is hidden by
    // the sheet and measures 0px, which makes the anchor math run against a list
    // short by the full height of every picture in it. Idempotent: an element is
    // claimed by its own data-bq-img-state, so this cannot mint twice.
    // (The widget hydrates before its restore for the same reason.)
    hydrateMessageImagePreviews();
    anchorWroteSinceScroll = true;
    chatScrollAnchor.restore(rowScrollAnchor);
    rowScrollAnchor = null;
};
// Anchoring reads getBoundingClientRect, which forces a synchronous reflow, so
// it must NOT run on every message mutation: the streaming typewriter rewrites a
// bubble's content each frame. What moves the reader is the row SET changing —
// a row appearing, disappearing, or relocating — plus the few group-row fields
// that alter its height. Watch exactly that, so a streaming reply (which changes
// no keys) costs nothing.
const chatDisplayRowsShape = computed(() =>
    chatDisplayRows.value
        .map((row) => (row.kind === "group"
            // anchorId too: a row can RELOCATE without changing height (an older
            // page moving the run's start), and a relocation the watchers never
            // see is a scroll jump nothing captured a position for.
            // `finished` too: it is what adds and removes the trailing loader row,
            // which changes the group's height without changing any other field here.
            // `resolving` is deliberately NOT here: all it changes is the head's verb
            // and glyph, and the shared chat.css pins that head to a single nowrap
            // line, so it cannot move anything. Anchoring reads
            // getBoundingClientRect, which forces a synchronous reflow of the whole
            // list — twice, pre and post — so a field that cannot change a height
            // must not be in this key.
            ? `${row.key}:${row.group.anchorId}:${row.group.status}:${row.group.finished ? 1 : 0}:${row.group.passCount}:${row.group.mayHaveOlder ? 1 : 0}:${row.group.cancellableIds.length}`
            : row.key))
        .join("|"));
// "pre" runs before the DOM is patched, "post" after — same source, so the two
// always pair up.
watch(chatDisplayRowsShape, captureRowScrollAnchor, { flush: "pre" });
watch(chatDisplayRowsShape, restoreRowScrollAnchor, { flush: "post" });

const onMessageLinkClick = async (e: MouseEvent, part: MsgPart, anchorOverride?: HTMLAnchorElement | null) => {
    // Already known dead. The template drops the href so there is nothing to
    // navigate to, but the handler is still bound: swallow the click rather than
    // firing another mint that is only going to fail the same way.
    if (isLinkPartUnavailable(part)) {
        e.preventDefault();
        return;
    }

    if (part.type !== "link" || !part.expired) {
        return;
    }

    const originalHref = part.expiredHref || part.href;

    // A previous click is already re-resolving this link (the chip shows
    // "(fetching...)"). Swallow further clicks so a rapid repeat doesn't each
    // await the shared in-flight fetch and then fire anchor.click() — which
    // would open the file in several tabs at once when it resolves.
    if (refreshingLinkMap.value[originalHref]) {
        e.preventDefault();
        return;
    }

    const anchor = anchorOverride
        ?? (e.currentTarget instanceof HTMLAnchorElement ? e.currentTarget : null);
    const cachedFresh = refreshedExpiredLinkMap.value[originalHref];

    if (cachedFresh) {
        part.expired = false;
        part.href = cachedFresh;
        if (anchor) {
            anchor.href = cachedFresh;
        }
        return;
    }

    e.preventDefault();

    try {
        const freshHref = await fetchFreshHrefForExpiredLink(
            originalHref,
            part.remotePath,
            part.image?.contentType,
        );

        part.expired = false;
        part.href = freshHref;
        if (anchor) {
            anchor.href = freshHref;
            anchor.click();
            return;
        }

        const replayAnchor = document.createElement("a");
        replayAnchor.href = freshHref;
        replayAnchor.target = "_blank";
        replayAnchor.rel = "noopener noreferrer";
        replayAnchor.style.display = "none";
        document.body.appendChild(replayAnchor);
        replayAnchor.click();
        replayAnchor.remove();
    } catch (err) {
        console.error("Failed to refresh expired attachment URL", err);
        // The chip itself is the message now: it turns grey and takes a ✕, which
        // survives past the moment an alert would have been dismissed and does
        // not interrupt whatever else the user was doing.
        markLinkUnavailable(linkUnavailableKeyForHref(originalHref));
        if (part.remotePath) markLinkUnavailable(linkUnavailableKeyForPath(part.remotePath));
    }
};

// Click delegation for the rendered-markdown bubble. Inline link/source
// anchors live inside the v-html string, so the bubble's `@click` is the
// only handler that fires. We reconstruct a synthetic MsgPart from the
// anchor's data attributes and forward to onMessageLinkClick. Plain links
// (no `data-bq-expired`) just navigate normally.
const onBubbleLinkClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const anchor = target.closest('a[data-bq-link]') as HTMLAnchorElement | null;
    if (!anchor) return;
    // A chip we already failed to mint a url for carries no href and no
    // `data-bq-expired`, so it cannot navigate and cannot ask for another mint.
    // Swallowing the click keeps a stray listener from ever reviving it.
    if (anchor.dataset.bqUnavailable === '1') { e.preventDefault(); return; }
    if (anchor.dataset.bqExpired !== '1') return;
    const part: MsgPart = {
        type: 'link',
        label: (anchor.textContent || '').replace(/^\u2197\s?/, '').replace(/\s*\(fetching\.{3}\)\s*$/, ''),
        fullLabel: anchor.dataset.bqFullLabel,
        href: anchor.href,
        expired: true,
        expiredHref: anchor.dataset.bqExpiredHref,
        remotePath: anchor.dataset.bqRemotePath,
    };
    onMessageLinkClick(e, part, anchor);
};

// The immediate-send request/dispatch + its cache+resume rendering
// (dispatchAgentRequest, typewriteLatestReply, resumePendingRequest) now live in
// the shared engine's ChatSession — cache+resume is the engine default, so both
// agent.vue and the BunnyQuery widget share it. agent.vue reaches them via
// chatSession.dispatchComposedMessage (sendMessage) and
// chatSession.resumePendingRequest (refreshGate, after a remount).

// Build the system prompt fresh on every send. The project name/description
// can change at any time (settings page edits, another tab, etc.) so we
// can't precompute or memoize this string.
// The prompt text itself lives in src/code/ai_prompts/chat_system_prompt.ts
// so it can be tweaked without touching this component.
const buildSystemPrompt = (): string => {
    const svc = currentService.value?.service;
    const serviceId = currentService.value?.id || "";
    return buildChatSystemPrompt({
        projectId: svc?.projectId ?? "",
        serviceName: svc?.name,
        serviceDescription: svc?.description,
        // The opening bubble is client-side chrome and never enters the message
        // history, so the model is told what it opened with. Same computed the
        // template paints, so the two can never disagree.
        greeting: greetingParts.value.text,
        canUpload: !uploadsFrozenForUser.value,
        // Where THIS project's indexer writes. The MCP's auto-fill assumes
        // "authorized"; on a project set to public or private that would search
        // the wrong group and answer "nothing found".
        //
        // A CACHE READ, and it has to be: buildSystemPrompt is the ChatHost's
        // sync hook, called by the engine from paths (a resume after a remount)
        // that have nowhere to put an await. It answers with the default until
        // the settings record has been fetched, so the fetch is started at mount
        // and awaited again in sendMessage right before the prompt is pinned,
        // which is the call whose answer actually reaches the model.
        indexAccessGroup: projectUploadAccessGroup(serviceId),
        client: "console",
    });
};

let attachmentBatchSeq = 0;
const newBatchId = () => `batch_${++attachmentBatchSeq}_${Date.now()}`;

type AttachmentSendJob = {
    text: string;
    batchId: string;
    stageId?: string;
    pinned: PinnedDispatchContext;
    /** The Service this send belongs to, captured at Send time. Stage 1 runs
     *  minutes later and this component does NOT remount on a project switch,
     *  so every `currentService.value` read inside the upload resolved to
     *  whatever project the user had wandered to: another project's storage
     *  received the bytes and its __INDEXING__ table received the run::/src::
     *  records. The dispatch half was already pinned; this pins the upload. */
    pinnedService: any;
};

// A send with attachments is two serialized stages, chained SEPARATELY.
//
// Uploads run one at a time because a batch owns shared, single-instance
// machinery: the "this file exists" prompt is one modal, and the per-batch
// overwrite choice ("apply to all") is reset at the start of each run. Keeping
// them in send order also puts each turn's indexing requests on the server
// queue in that order.
//
// The dispatch stage is chained on its own so a turn waiting for indexing to
// finish does not hold up the NEXT send's uploads — that wait can run for
// minutes, and the point of all this is that the user is never blocked. Both
// chains are appended at Send time, so the turns still reach the server in the
// order they were typed.
let attachmentUploadChain: Promise<unknown> = Promise.resolve();
let attachmentDispatchChain: Promise<unknown> = Promise.resolve();
const enqueueAttachmentSend = (job: AttachmentSendJob) => {
    const uploaded = attachmentUploadChain
        .catch(() => { /* a failed batch must not stall the ones behind it */ })
        .then(() => runAttachmentUpload(job));
    attachmentUploadChain = uploaded;
    attachmentDispatchChain = attachmentDispatchChain
        .catch(() => { /* likewise */ })
        .then(() => uploaded)
        .then((urls) => runAttachmentDispatch(job, urls));
};

// Stage 1: upload this send's own batch of chips and enqueue their indexing.
// Resolves to the attachment urls, or null when the upload failed outright (the
// failure has been reported and nothing will be dispatched). Everything here
// happens AFTER the user has moved on, so it touches only what it captured at
// Send time.
const runAttachmentUpload = async (
    job: AttachmentSendJob,
): Promise<{ name: string; url: string }[] | null> => {
    // The chat this send was made in. It can take a while, and the user may be
    // looking at another project by the time it lands — the dispatch itself is
    // pinned, but the VIEW-side reports below (an error bubble, the failure
    // dialog) would otherwise be shown against, and cached into, whatever chat
    // happens to be open.
    const pinnedKey = `${job.pinned.identity.projectId}#${job.pinned.identity.platform}`;
    const stillViewingSendersChat = () => chatSession.getHistoryCacheKey() === pinnedKey;

    let attachmentUrls: { name: string; url: string }[] = [];
    try {
        attachmentUrls = await uploadPendingAttachments(job.batchId, job.stageId, job.pinnedService, job.pinned.identity);
    } catch (err: any) {
        const msg =
            err && err.message ? String(err.message) : "Failed to upload attachments";
        console.error("Attachment upload failed", err);
        // Nothing will be dispatched, so settle the staged bubble rather than
        // leaving it uploading forever — the user's text stays on screen above
        // the failure instead of disappearing with it.
        if (job.stageId) chatSession.settleStagedMessage(job.stageId);
        if (stillViewingSendersChat()) {
            messages.value.push({
                role: "assistant",
                content: `Attachment upload failed: ${msg}`,
                isError: true,
            });
            await nextTick();
            // Sticky, not forcing. An upload can fail minutes after the user hit
            // Send, long after they scrolled away, and yanking them to the bottom
            // for it is the same unrequested move as the delayed dispatch. The
            // failure is already reported persistently in the bubble above and in
            // the error report, so nothing is lost by leaving them where they are.
            await scrollToBottomIfSticky(false);
        }
        return null;
    }
    // After the uploads + indexing-queue requests are all made, surface a
    // single report of everything that failed, grouped by error code +
    // description. The failed chips return to the composer to retry.
    const failures = groupAttachmentFailures(
        attachments.value.filter((a) => a._batchId === job.batchId) as any[]
    );
    if (failures.length && stillViewingSendersChat()) uploadErrorReport.value = failures;
    clearSuccessfulAttachments(job.batchId);
    return attachmentUrls;
};

// Stage 2: send the chat turn those files belong to LAST — after every indexing
// pass on the queue has finished, not merely after the uploads.
const runAttachmentDispatch = async (
    job: AttachmentSendJob,
    attachmentUrls: { name: string; url: string }[] | null,
) => {
    // Upload failed (already reported), or an attachment-only turn: the files
    // are indexing and there is no chat message to send.
    if (!attachmentUrls || !job.text) return;

    // The files are up; what the turn is waiting on now is their INDEXING.
    if (job.stageId) chatSession.markStagedMessageIndexing(job.stageId);
    // Indexing a file is a CHAIN — each pass is only enqueued once the previous
    // one lands — so a turn sent when the uploads finish is answered from a
    // half-read file, with the remaining passes queued up behind it. Wait for
    // the background queue to actually drain. (Times out rather than stranding
    // the message if a chain wedges server-side.)
    await chatSession.awaitIndexingDrained(job.pinned.identity);
    // The files are indexed: the turn is genuinely just queued now, so it reads
    // solid and "(In queue)" from this instant — not once the request lands, which
    // is another round trip after a wait measured in minutes. Runs on a timeout or
    // a skip too: the turn is being sent either way.
    if (job.stageId) chatSession.markStagedMessageReady(job.stageId);

    // Compose the user message (attachment-link block + office-extraction
    // placeholders) via the shared engine helper, then dispatch through the
    // shared ChatSession — it owns the queued-vs-immediate decision, the
    // cache+resume immediate-send model, the office `extractContent`, and the
    // "-bg" queue routing. A turn with attachments always goes on the "-bg"
    // queue: that is the queue its files were indexed on, so it is the only one
    // where being enqueued last means running last.
    // inlineExtractedContent: false — these files were just indexed (the drain
    // above waited for it), so their content is already in the database. Inlining
    // it would make the worker download and re-parse every attachment a SECOND
    // time, which reads exactly like the file being indexed again, and would
    // re-send the whole file text as prompt tokens. The agent reads the indexed
    // records instead (see the system prompt), or readFileContent for raw text.
    const { composed, composedForLlm, extractContent, fileUrls } = composeUserMessage(job.text, attachmentUrls, { inlineExtractedContent: false });
    chatSession.dispatchComposedMessage(composed, true, composedForLlm, extractContent, fileUrls, job.pinned);
};

const sendMessage = async () => {
    const text = prompt.value.trim();
    // The chips THIS send takes. Chips already handed to an earlier send stay
    // out of it (see composerAttachments).
    const batchAttachments = composerAttachments.value.slice();
    const hasAttachments = batchAttachments.length > 0;
    if ((!text && !hasAttachments) || !chatEnabled.value) {
        return;
    }
    // A chat message bundles all the attachment URLs into one prompt, so block
    // the send when that would exceed the file-count cap (#6) or the per-request
    // token budget. (The Enter key bypasses the disabled Send button, so guard
    // here too.) Attachment-only sends index files one-by-one and are unaffected.
    if (text && (countFilesInAttachments(batchAttachments) > MAX_CHATBOX_FILE_COUNT || attachmentsExceedBudget.value)) {
        return;
    }

    const aiPlatform = selectedAiPlatform.value;
    if (aiPlatform === "none") {
        messages.value.push({ role: "user", content: text || "(attachments only)" });
        messages.value.push({
            role: "assistant",
            content: "AI platform is not configured.",
            isError: true,
        });
        updateHistoryCache();
        prompt.value = "";
        clearAttachments();
        await nextTick();
        autoGrowInput();
        await scrollToBottom(true);
        return;
    }

    // Settle the project's settings before the prompt is built. indexAccessGroup
    // is a cache read (buildSystemPrompt is sync), and on the very first send
    // after a page load that cache can still be empty: the model would then be
    // told the indexer writes at "authorized" on a project that publishes at
    // group 0, and every search it ran would answer "nothing found". Normally a
    // no-op, since the mount primed this; never rejects.
    await readyProjectSettings(currentService.value?.id || "");

    // Pin the project context NOW, before the attachment uploads run. Uploading
    // a large file takes a while, and the user can switch projects mid-upload -
    // the engine's identity/system-prompt reads are live, so without this the
    // question typed for THIS project would be dispatched to whichever project
    // is open by the time the upload finishes.
    const pinnedDispatch: PinnedDispatchContext = {
        identity: chatHost.getIdentity(),
        systemPrompt: buildSystemPrompt(),
    };

    // Hand the composer back to the user before a single byte moves. The turn
    // takes its chips with it (stamped with a batch id) and, when it has text,
    // leaves a staged bubble behind so it holds the position it was sent in —
    // the uploads and the dispatch then finish on their own, behind whatever
    // the user does next.
    prompt.value = "";
    if (hasAttachments) {
        const batchId = newBatchId();
        for (const a of batchAttachments) a._batchId = batchId;
        const stageId = text ? chatSession.stageOutgoingMessage(text) : undefined;
        if (stageId) pinnedDispatch.stageId = stageId;
        enqueueAttachmentSend({ text, batchId, stageId, pinned: pinnedDispatch, pinnedService: currentService.value });
    } else {
        const { composed, composedForLlm, extractContent, fileUrls } = composeUserMessage(text, []);
        chatSession.dispatchComposedMessage(composed, false, composedForLlm, extractContent, fileUrls, pinnedDispatch);
    }

    await nextTick();
    autoGrowInput();
};

watch(
    () => [currentService.value?.id, currentService.value?.service?.ai_agent],
    (now, old) => {
        // A model-only change (same service, same platform) doesn't affect the
        // chat history — getHistoryCacheKey() is keyed by platform, not model —
        // nor the gate. Skip the full refreshGate: it would restore the cache and
        // rebuild messages.value via fetchHistoryPage, orphaning the "Thinking..."
        // placeholder of any in-flight request. Just sync the selected model so
        // new sends use it. (Platform changes are handled by onPlatformSelect's
        // own refreshGate; service changes fall through to refreshGate below.)
        const serviceUnchanged = !!now[0] && now[0] === old?.[0];
        const parsed = parseAiAgentValue(currentService.value?.service?.ai_agent);
        applyProjectContextWindow();
        if (serviceUnchanged && parsed.platform === selectedAiPlatform.value) {
            selectedAiModel.value = parsed.model;
            return;
        }
        void refreshGate();
    }
);

// External surfaces (e.g. dbfile.vue after a batch upload completes) bump
// `agentHistoryRefreshTrigger[projectId]` to ask us to refetch chat history
// so background-indexing requests show up as pending bubbles. The proxy
// enqueues the request asynchronously \u2014 the queue entry may not be
// observable in clientSecretRequestHistory for a few seconds after the
// upload finishes \u2014 so we don't fire a single-shot refetch. Instead we
// snapshot the set of currently-known pending ids, then poll the first page
// at INTERVAL_MS until we see a NEW pending id (or hit MAX_ATTEMPTS, to
// avoid runaway polling on misconfigured services). Once a new pending id
// is observed, the existing `schedulePendingHistoryRefetch` loop fired by
// fetchHistoryPage takes over and keeps polling until the request resolves.
//
// Watched as a *deep* watch on the whole trigger object so newly-added
// service-id keys are picked up even if the slot didn't exist when the
// watcher was first installed.
const QUEUE_DETECT_INITIAL_DELAY_MS = 500;
const QUEUE_DETECT_INTERVAL_MS = 2500;
const QUEUE_DETECT_MAX_ATTEMPTS = 8; // ~20s of polling after the initial delay
let agentHistoryRefreshDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let queueDetectAttempts = 0;
let queueDetectBaselineIds: Set<string> = new Set();

const cancelQueueDetectPoll = () => {
    if (agentHistoryRefreshDebounceTimer) {
        clearTimeout(agentHistoryRefreshDebounceTimer);
        agentHistoryRefreshDebounceTimer = null;
    }
    queueDetectAttempts = 0;
};

const startQueueDetectPoll = () => {
    cancelQueueDetectPoll();
    if (agentUnmounted) return;
    if (!chatEnabled.value || selectedAiPlatform.value === "none") return;

    // Snapshot the pending server-item ids we already know about. The poll
    // loop completes the moment the refreshed history contains a pending
    // id NOT in this set, indicating the new queue entry has been
    // registered server-side.
    queueDetectBaselineIds = new Set(
        messages.value
            .filter((m) => m.isPending && typeof m._serverItemId === "string")
            .map((m) => m._serverItemId as string)
    );
    queueDetectAttempts = 0;

    const tick = async () => {
        agentHistoryRefreshDebounceTimer = null;
        if (agentUnmounted) return;
        if (!chatEnabled.value || selectedAiPlatform.value === "none") return;

        // The user has scrolled up to read older history (stickToBottom flips
        // false on any upward wheel/touch). A first-page refetch here REPLACES
        // messages.value with just the first page, discarding every scrolled-in
        // older page and yanking the view back to the bottom. This detect loop
        // exists only to surface a freshly-registered indexing bubble at the
        // bottom — irrelevant while they read history — so stop rather than
        // destroy their pagination. drainBgTaskQueue + the per-item polls still
        // surface the bubble when they scroll back down / on the next refresh.
        if (!stickToBottom) {
            cancelQueueDetectPoll();
            return;
        }

        queueDetectAttempts++;

        await fetchHistoryPage(false, chatSession.state.gateRefreshToken);

        if (agentUnmounted) return;
        // Re-check AFTER the await: the fetch is a network round-trip, and the user
        // can scroll up into history while it is in flight. The pre-fetch bail
        // alone would keep the loop running for the rest of its budget.
        if (!stickToBottom) {
            cancelQueueDetectPoll();
            return;
        }

        // Did a brand-new pending id appear? If so, the upload's queue
        // entry is now observable and item.poll() has been attached; stop
        // our detect loop.
        const sawNewPending = messages.value.some(
            (m) =>
                m.isPending &&
                typeof m._serverItemId === "string" &&
                !queueDetectBaselineIds.has(m._serverItemId as string)
        );
        if (sawNewPending) {
            queueDetectAttempts = 0;
            return;
        }

        // Nothing is pending anywhere in the freshly-fetched first page. The
        // entry we were waiting on either already resolved (nothing left to
        // surface) or was never registered. Give async registration a couple of
        // attempts, then stop — otherwise a COMPLETED index leaves this loop
        // re-fetching (and resetting the user's pagination) for the full attempt
        // budget with nothing to find, which reads as endless phantom polling.
        // Is anything pending that we are actually still waiting on? Ids that
        // drainBgTaskQueue just re-seeded from bgTaskQueue do NOT count: the drain
        // runs at the end of every first-page fetch and re-injects a pending bubble
        // for any queue entry missing from the page, so counting those made
        // `anyPending` permanently true and this early-stop dead code — the loop
        // then always burned its full budget re-fetching with nothing to find.
        const selfSeededIds = new Set(
            bgTaskQueue
                .filter(
                    (e) =>
                        e.projectId === currentService.value?.id &&
                        e.platform === selectedAiPlatform.value,
                )
                .map((e) => e.id),
        );
        const anyPending = messages.value.some(
            (m) =>
                m.isPending &&
                typeof m._serverItemId === "string" &&
                !selfSeededIds.has(m._serverItemId as string),
        );
        if (!anyPending && queueDetectAttempts >= 3) {
            queueDetectAttempts = 0;
            return;
        }

        if (queueDetectAttempts >= QUEUE_DETECT_MAX_ATTEMPTS) {
            queueDetectAttempts = 0;
            return;
        }

        agentHistoryRefreshDebounceTimer = setTimeout(() => {
            void tick();
        }, QUEUE_DETECT_INTERVAL_MS);
    };

    agentHistoryRefreshDebounceTimer = setTimeout(() => {
        void tick();
    }, QUEUE_DETECT_INITIAL_DELAY_MS);
};

watch(
    agentHistoryRefreshTrigger,
    () => {
        if (agentUnmounted) return;
        const projectId = currentService.value?.id;
        if (!projectId) return;
        const cur = agentHistoryRefreshTrigger[projectId];
        if (typeof cur !== "number") return;
        const last = agentHistoryRefreshLastSeen[projectId];
        if (last === cur) return;
        agentHistoryRefreshLastSeen[projectId] = cur;
        if (!chatEnabled.value || selectedAiPlatform.value === "none") return;
        startQueueDetectPoll();
    },
    { deep: true }
);

// When dbfile.vue pushes a new bg-task entry (after notifyAgentSaveAttachment
// returns), inject the Thinking... bubble immediately if history is loaded.
// If history is still loading, drainBgTaskQueue() will be called once it finishes.
// Coalesced. A bulk upload pushes one entry per uploaded file, and each drain is
// O(messages + queue), so 10k files meant 10k full drains over a message list
// growing to 10k — quadratic work on the main thread, which is a large part of
// what froze the tab. A short window leaves a single-file push feeling immediate
// while collapsing a burst into one drain.
let drainCoalesceTimer: ReturnType<typeof setTimeout> | null = null;
watch(
    bgTaskQueue,
    () => {
        if (agentUnmounted || loadingHistory.value) return;
        if (drainCoalesceTimer !== null) return; // a drain is already scheduled
        drainCoalesceTimer = setTimeout(() => {
            drainCoalesceTimer = null;
            if (agentUnmounted || loadingHistory.value) return;
            drainBgTaskQueue();
        }, 50);
    },
    { deep: false }
);

watch(
    () => agentRoot.value,
    (root) => {
        if (root) {
            scheduleAgentViewportHeight();
        }
    },
    { flush: "post" }
);

// Animate the dots in [generating file.ext...] placeholders while any
// typewriter is running. Stop the interval when typing ends to avoid
// pointless re-renders.
watch(typing, (isTyping) => {
    if (isTyping) {
        if (dotTickTimer) return;
        dotTick.value = 0;
        dotTickTimer = setInterval(() => {
            dotTick.value = (dotTick.value + 1) % 3;
        }, 400);
    } else if (dotTickTimer) {
        clearInterval(dotTickTimer);
        dotTickTimer = null;
    }
});

// The bg-stub hint (deferred first-paint batch) shares the ticker.
watch(() => showHistoryLoadingIndicator.value || (bgHistoryLoading.value && !initialGateLoading.value), (isVisible) => {
    if (isVisible) {
        if (historyLoadDotTickTimer) return;
        historyLoadDotTick.value = 0;
        historyLoadDotTickTimer = setInterval(() => {
            historyLoadDotTick.value = (historyLoadDotTick.value + 1) % 3;
        }, 400);
    } else if (historyLoadDotTickTimer) {
        clearInterval(historyLoadDotTickTimer);
        historyLoadDotTickTimer = null;
    }
});

// --- ChatSession host adapter (Stage E cluster 2) ---------------------------
// The shared engine (chatSession) reaches the view through these hooks for
// identity, render, scroll, the skapi cancel/refresh surface, and bg-indexing
// display. agent.vue delegates the engine-compatible state-machine primitives
// (promote/drain/queued-send/cancel/typewriter/cache) to chatSession, which
// drives messages.value in place via the cluster-1 reactive binding — so
// notify/refreshMessageBubble are no-ops (Vue re-renders on the mutation).
// The cache+resume immediate-send model, history pagination, and attachment
// upload (admin storage accounting) stay agent.vue-specific; the engine's own
// upload orchestration is exercised only by the BunnyQuery widget, so the
// upload I/O hooks below are unused here (present for ChatHost completeness).
const chatHost: ChatHost = {
    getIdentity: () => ({
        projectId: currentService.value?.id ?? "",
        publicProjectId: currentService.value?.service?.projectId || undefined,
        owner: currentService.value?.owner ?? "",
        userId: (skapi as any).user?.user_id || currentService.value?.id || "",
        platform: selectedAiPlatform.value as "claude" | "openai" | "none",
        model: selectedAiModel.value || undefined,
        serviceName: currentService.value?.service?.name,
        serviceDescription: currentService.value?.service?.description,
    }),
    buildSystemPrompt: () => buildSystemPrompt(),
    notify: () => {},
    refreshMessageBubble: () => {},
    scrollToBottom: (smooth) => scrollToBottom(smooth),
    scrollToBottomIfSticky: (smooth) => scrollToBottomIfSticky(smooth),
    // The engine loads first-page history on its own from resumePolling (tab
    // returns to the foreground), and that path never enters this file's
    // fetchHistoryPage — so without this hook the viewport fill simply would not
    // run for it. That load is exactly the one that leaves a short box: its
    // REPLACE branch keeps only page 1 whenever page 1 shares nothing with what
    // was on screen, and a page of indexing turns collapses to one row.
    onHistoryLoaded: (fetchMore, token) => {
        if (!fetchMore) void ensureHistoryFillsViewport(token);
    },
    settleScroll: () => settleScrollAfterRefresh(),
    cancelRequest: (opts) => (skapi as any).cancelClientSecretRequest(opts),
    refreshSession: () => refreshSkapiSession(),
    // `continued` MUST be forwarded. The engine renders indexing labels through
    // this adapter from two places — its own history mapper (history.ts, via
    // loadHistory) and drainBgTaskQueue — and both pass a 6th `continued` arg.
    // Declaring only 5 params silently dropped it, so every engine-produced
    // bubble fell back to continued=false and a CONTINUE pass rendered as the
    // full "Indexing: <name> · <mime>" instead of "Indexing (continuing) <name>".
    // That is the label-reversion bug; do not shorten this signature again.
    formatIndexingLabel: (name, mime, size, storagePath, reindex, continued) =>
        formatIndexingLabel(name, mime, size ?? undefined, storagePath, reindex, continued),
    isViewMounted: () => chatEnabled.value,
    getClearedAt: () => getClearedAt(),
    uploadFile: () => { throw new Error("[agent.vue] chatHost.uploadFile is unused — agent.vue uploads via uploadPendingAttachments"); },
    getTemporaryUrl: () => { throw new Error("[agent.vue] chatHost.getTemporaryUrl is unused"); },
    storagePathFor: (relPath) => relPath,
    getMimeType: () => null,
    promptOverwrite: () => { throw new Error("[agent.vue] chatHost.promptOverwrite is unused"); },
    resetOverwriteBatch: () => {},
    renderAttachmentChips: () => {},
    updateComposerControls: () => {},
};

// Tab visibility and view attachment are DISTINCT pause reasons: a hidden tab and a
// closed view can overlap, and clearing one must not resume while the other still holds.
// Polling only. Nothing here touches the scroll, and that is the point: the reader
// was never moved while they were away, because compensation runs whether or not
// the tab is visible, so there is nothing to put back on return.
const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
        chatSession.pausePolling("hidden");
    } else if (document.visibilityState === "visible") {
        void chatSession.resumePolling("hidden");
    }
};


onMounted(async () => {
    // While this view is open it owns polling/rendering of bgTaskQueue tasks;
    // the fallback poller in main.vue stands down (see agentViewMounted).
    agentViewMounted.value = true;
    // Install this view's host on the shared ChatSession BEFORE any chat work
    // (refreshGate → loadChatHistory → drainBgTaskQueue all reach engine hooks).
    setChatHost(chatHost);
    // Clear the detached pause now the host exists. refreshGate -> loadChatHistory does
    // the actual re-attach and reconcile, so this only lifts the gate; calling
    // resumePolling's own reconcile here would race it.
    chatSession.clearPauseReason("detached");
    document.addEventListener("visibilitychange", onVisibilityChange);
    void refreshGate();
    removeFullScreenLoader();
    syncMobile();
    await nextTick();
    autoGrowInput();
    if (agentRoot.value) {
        scheduleAgentViewportHeight();
    }
    await nextTick();
    await scrollToBottom(false);
    window.addEventListener("resize", onWindowResize);
    window.addEventListener("resize", syncMobile);
    window.addEventListener("click", closeModelMenu);
    window.addEventListener("click", closePlatformMenu);
    messagesBox.value?.addEventListener("load", onMessagesImageSettled, true);
    messagesBox.value?.addEventListener("error", onMessagesImageSettled, true);
    (document as any).fonts?.addEventListener?.("loadingdone", onFontsSettled);

    // Re-evaluate attachment chip truncation whenever the chat container
    // resizes (e.g. agent viewport height settles, sidebar toggles).
    if (chatBox.value && typeof ResizeObserver !== "undefined") {
        attachmentsResizeObserver = new ResizeObserver(() => {
            scheduleAttachmentRecompute();
        });
        attachmentsResizeObserver.observe(chatBox.value);
    }
});

onUnmounted(() => {
    agentUnmounted = true;
    if (stubRecheckTimer !== null) { clearTimeout(stubRecheckTimer); stubRecheckTimer = null; }
    // Stop background polling for this view. Server-side indexing is unaffected: the
    // worker drives the document loop itself, so this drops traffic, not progress.
    chatSession.pausePolling("detached");
    document.removeEventListener("visibilitychange", onVisibilityChange);
    // Hand bgTaskQueue completion-polling back to main.vue's fallback poller.
    agentViewMounted.value = false;
    chatSession.state.typingAbort = true;
    if (dotTickTimer) {
        clearInterval(dotTickTimer);
        dotTickTimer = null;
    }
    if (historyLoadDotTickTimer) {
        clearInterval(historyLoadDotTickTimer);
        historyLoadDotTickTimer = null;
    }
    cancelQueueDetectPoll();
    revokeFileBlobCache();
    if (refreshedLinkExpiryTimer) {
        clearTimeout(refreshedLinkExpiryTimer);
        refreshedLinkExpiryTimer = null;
    }
    window.cancelAnimationFrame(viewportHeightFrame);
    window.cancelAnimationFrame(attachmentsRecomputeFrame);
    window.removeEventListener("resize", onWindowResize);
    window.removeEventListener("resize", syncMobile);
    window.removeEventListener("click", closeModelMenu);
    window.removeEventListener("click", closePlatformMenu);
    messagesBox.value?.removeEventListener("load", onMessagesImageSettled, true);
    messagesBox.value?.removeEventListener("error", onMessagesImageSettled, true);
    (document as any).fonts?.removeEventListener?.("loadingdone", onFontsSettled);
    chatScrollAnchor.forget();
    if (attachmentsResizeObserver) {
        attachmentsResizeObserver.disconnect();
        attachmentsResizeObserver = null;
    }
});
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-agent {
    height: var(--agent-available-height, 50dvh);
    overflow: hidden;
    font-family: @mono;
    color: @ink;
}

.bq-meta {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    border: 1px solid @line;
    padding: 1rem;
    box-shadow: 2px 2px var(--bq-shadow);
}

.bq-section-title {
    font-size: 1.1rem;
    font-weight: 700;
    // margin: 0 0 1.25rem;
    padding-bottom: 0.75rem;
    // border-bottom: 1px solid @line;
}

.bq-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;

    h2 {
        margin: 0;
        font-size: inherit;
        font-weight: inherit;
    }
}

.bq-title-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.bq-title-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.bq-icon {
    font-size: 1.4rem;
    color: @muted;
    transition: color 0.15s;
    user-select: none;
    vertical-align: middle;
    cursor: pointer;

    &:hover,
    &.active {
        color: @pink;
    }

    &.disabled {
        opacity: 0.3;
        pointer-events: none;
        cursor: default;
    }

    &.bq-icon--danger {
        &:hover {
            color: @danger;
        }
    }
}

.bq-platform-logo {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
}

.bq-model-name {
    font-size: 0.75rem;
    font-weight: 400;
    color: @muted;
}

.bq-ai-select {
    position: relative;
    width: 220px;

    &.bq-ai-select--inline {
        width: auto;
        min-width: 160px;
        max-width: 240px;
    }

    &.bq-ai-select--platform {
        width: auto;
    }
}

.bq-ai-select-native {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    min-height: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 2;
}

.bq-ai-select-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.3rem 0.6rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.78rem;
    cursor: pointer;

    &:not(:disabled):hover,
    &.open {
        border-color: @ink;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.bq-ai-select-value {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.bq-ai-caret {
    color: @muted;
    flex: 0 0 auto;
}

.bq-ai-options {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    display: flex;
    flex-direction: column;
}

.bq-model-options {
    max-height: 240px;
    overflow-y: auto;
}

.bq-platform-options {
    min-width: 160px;
}

.bq-ai-logo {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
}

.bq-ai-option {
    border: 0;
    border-bottom: 1px solid @line;
    background: transparent;
    box-shadow: none;
    width: 100%;
    text-align: left;
    padding: 0.4rem 0.6rem;
    font-family: @mono;
    font-size: 0.78rem;
    color: @ink;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.45rem;
    cursor: pointer;

    &:last-child {
        border-bottom: 0;
    }

    &:hover {
        background: @hover-bg;
    }

    &.active {
        background: @hover-bg;
    }
}

.bq-gate {
    margin-bottom: 1rem;
    padding: 0.85rem;
    border: 1px solid @line;
    background: @hover-bg;
}

.bq-gate-loading {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 0.83rem;
}

.bq-gate-title {
    margin: 0 0 0.35rem;
    font-weight: 600;
}

.bq-gate-desc {
    margin: 0 0 0.65rem;
    color: @muted;
}

.bq-link {
    color: @pink;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.bq-chat {
    border: none;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

.bq-messages {
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    /* Exactly ONE compensator. Blink and Gecko run their own scroll anchoring on
       this scroller, and it fires at the very layout the JS anchor's absorb()
       forces when it reads offsetHeight - so an image growing above the fold was
       corrected by the browser and then paid for a second time in JS. Measured on
       one background re-render: 6224px of jump with both live, 0 with this.
       Deleting absorb() instead is not an option (15000px of drift on WebKit,
       which has no native anchoring at all). See engine/scroll_anchor.ts. */
    overflow-anchor: none;
    flex: 1;
    min-height: 0;
    max-height: none;
    padding: 0;
    border-bottom: none;
    background: @paper;
}

/* ZERO HEIGHT IN FLOW, DELIBERATELY.
   This bar mounts the moment a history fetch starts and unmounts when the page
   lands, and it does that while the reader is sitting at the very top of the
   list, which is the only thing that triggers a fetch. As an ordinary in-flow
   sticky element it added its own height ABOVE them on mount and took it away
   again on unmount: measured in Chrome with overflow-anchor:none (which this
   scroller sets), every row moved by exactly the bar's height, twice per page,
   and the fill loop pages several in a row.

   The scroll anchor could only ever chase that after the fact. So the bar
   contributes nothing to lay out instead: the outer box is height 0 and the
   inner strip overflows it, which keeps the bar pinned at the top of the
   viewport (measured: top 0 at every scroll position) while mounting and
   unmounting it moves nothing at all. It paints over the top of the list while
   it is up, so it is click-through and its own background fades out. */
.bq-history-loading {
    position: sticky;
    top: 0;
    z-index: 1;
    height: 0;
    padding: 0;
    overflow: visible;
    pointer-events: none;
}

.bq-history-loading-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0 1rem;
    text-align: center;
    font-size: 0.8rem;
    color: @muted;
    background: linear-gradient(to bottom, @paper 70%, rgb(from @paper-lit r g b / 0));
}

/* The chat conversation surface — message bubbles, rendered markdown
   (formerly :deep into the v-html), in-bubble file/link anchors, and the
   queue/cancel affordances — is now shared with the BunnyQuery widget via
   bunnyquery/styles/chat.css (imported as the non-scoped <style>
   block at the end of this file). Only the admin-panel container/layout
   rules stay scoped here. */

/* ─── upload access-group chooser ─── */
// Same shape as the db files page: a radio list, because the hint under each
// option is what actually distinguishes the three answers.
.bq-access-options {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    margin: 0.85rem 0 0;
    text-align: left;

    .bq-access-option {
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: baseline;
        column-gap: 0.5rem;
        padding: 0.5rem 0.6rem;
        cursor: pointer;
        user-select: none;

        &:hover {
            background: @hover-bg;
        }

        input {
            cursor: pointer;
            margin: 0;
        }
    }

    .bq-access-option-label {
        font-size: 0.9rem;
    }

    .bq-access-option-hint {
        grid-column: 2;
        margin-top: 0.15rem;
        font-size: 0.78rem;
        line-height: 1.35;
        color: @muted;
    }
}

.bq-overwrite-applyall {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.85rem;
    font-size: 0.85rem;
    cursor: pointer;
    user-select: none;

    input {
        cursor: pointer;
        margin: 0;
    }
}

// upload error report dialog (failed files grouped by error)
.bq-upload-error-list {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    margin-top: 0.85rem;
    max-height: 50vh;
    overflow-y: auto;
}
.bq-upload-error-group {
    border-left: 3px solid @danger;
    padding-left: 0.7rem;
}
.bq-upload-error-heading {
    margin: 0 0 0.3rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: @danger;
    word-break: break-word;
}
.bq-upload-error-files {
    margin: 0;
    padding-left: 1.1rem;
    font-size: 0.8rem;
    color: @ink;
}
.bq-upload-error-files li {
    margin: 0.1rem 0;
    word-break: break-word;
}

.bq-input-row {
    display: flex;
    flex-shrink: 0;
    --chat-control-height: 2.6rem;
    gap: 0.65rem;
    padding: 0.65rem 0 0;
    background: @paper;
    flex-wrap: wrap;
}

.bq-attach-input {
    display: none;
}

.bq-input-wrap {
    position: relative;
    flex: 1;
    display: flex;
    min-width: 0;
}

.bq-attach-btn {
    position: absolute;
    top: 0;
    left: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--chat-control-height);
    height: var(--chat-control-height);
    min-height: 0;
    padding: 0;
    border: none;
    background: transparent;
    color: @muted;
    cursor: pointer;
    z-index: 1;
    box-shadow: none;
    border-radius: 0;

    &:hover:not(:disabled) {
        background: transparent;
        color: @pink;
        transform: none;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.bq-attachments {
    flex-basis: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    padding: 0 0 0.5rem;
}

.bq-attachment-warning {
    flex-basis: 100%;
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    padding: 0.4rem 0.5rem;
    margin-bottom: 0.4rem;
    border: 1px solid @danger;
    background: color-mix(in srgb, @danger-lit 8%, transparent);
    color: @danger;
    font-size: 0.83rem;
    line-height: 1.3;
}

.bq-attachment-warning-icon {
    font-size: 1rem;
    line-height: 1.3;
    flex: 0 0 auto;
}

.bq-attachment {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    max-width: 18rem;
    padding: 0.3rem 0.5rem;
    border: 1px solid @line;
    background: @paper;
    font-size: 0.78rem;
    font-family: @mono;
    position: relative;
    overflow: hidden;
    isolation: isolate;

    &.is-uploading {
        &::before {
            content: "";
            position: absolute;
            inset: 0 auto 0 0;
            width: var(--att-progress, 0%);
            background: color-mix(in srgb, @pink-lit 18%, transparent);
            transition: width 0.15s linear;
            z-index: 0;
            pointer-events: none;
        }

        > * {
            position: relative;
            z-index: 1;
        }
    }

    // The two indeterminate phases, both drawn as a barber pole over the full
    // chip because neither has a percentage to show:
    //   is-preparing:  before the first byte-progress event, while
    //                  get-signed-url is in flight. A bar at 0% reads stalled.
    //   is-finalizing: bytes done, post-upload work (signed url, src:: record,
    //                  indexing dispatch) still running. A bar frozen at a
    //                  solid 100% reads hung.
    // Between them the bar is determinate and driven by --att-progress.
    &.is-preparing,
    &.is-finalizing {
        &::before {
            width: 100%;
            background: repeating-linear-gradient(
                -45deg,
                color-mix(in srgb, @pink-lit 22%, transparent) 0 8px,
                color-mix(in srgb, @pink-lit 10%, transparent) 8px 16px
            );
            background-size: 22.6px 100%;
            animation: bq-att-barber 0.8s linear infinite;
            transition: none;
        }

        .bq-attachment-meta::after {
            // Walking dots: bq-dots is the app-global content animation
            // (base.less); a fixed-width box keeps the chip from jittering.
            content: ".";
            display: inline-block;
            width: 1.1em;
            text-align: left;
            animation: bq-dots 1.2s steps(1) infinite;
        }
    }

    @keyframes bq-att-barber {
        to { background-position: 22.6px 0; }
    }

    &.is-error {
        border-color: @danger;
        background: @danger-bg;

        .bq-attachment-meta, .bq-attachment-name {
            color: @danger;
        }
    }

    &.is-index-error {
        border-color: @warning-border;
        background: @warning-bg;

        .bq-attachment-meta, .bq-attachment-name {
            color: @warning;
        }
    }

    &.is-done {
        border-color: @success-border;
        background: @success-bg;

        .bq-attachment-meta {
            color: @success;
        }
    }

    &.is-clickable {
        cursor: pointer;
        transition: background 0.12s ease, border-color 0.12s ease;

        &:hover {
            background: @hover-bg;
            border-color: @ink;
        }
    }
}

.bq-attachment-icon {
    flex: 0 0 16px;
    color: @muted;
}

.bq-attachment-arrow {
    flex: 0 0 auto;
    font-size: 0.95rem;
    line-height: 1;
    color: @muted;
}

.is-done .bq-attachment-arrow {
    color: @ink;
}

.bq-attachment-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 12rem;
}

.bq-attachment-meta {
    color: @muted;
    flex-shrink: 0;
}

.bq-attachment-more {
    color: @muted;
    font-style: italic;
}

.bq-attachment-remove-all {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.3rem 0.5rem;
    border: 1px dashed @line;
    background: transparent;
    color: @muted;
    font-size: 0.72rem;
    cursor: pointer;
    align-self: center;

    &:hover {
        color: @danger;
        border-color: @danger;
    }
}

.bq-attachment-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.2rem;
    height: 1.2rem;
    margin-left: 0.15rem;
    padding: 0;
    border: none;
    background: transparent;
    color: @muted;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;

    &:hover {
        color: @ink;
    }
}

.bq-chat {
    position: relative;
}

.bq-drop-overlay {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, @paper-lit 85%, transparent);
    border: 2px dashed @pink;
    pointer-events: none;
}

.bq-drop-overlay-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: @pink;
    font-size: 0.9rem;
    font-weight: 600;
}

.bq-apikey-overlay {
    position: absolute;
    inset: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, @paper-lit 85%, transparent);
    padding: 1rem;
}

.bq-apikey-overlay-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.85rem;
    text-align: center;
    max-width: 32rem;

    .btn {
        align-self: center;
        min-width: 11rem;
    }

    // Keep the button in its normal active styling while we're fetching the
    // previous API key — disabled fades it out and on mobile the lingering
    // :hover state flips it to white, which makes the dots animation invisible.
    .btn.is-loading-key,
    .btn.is-loading-key:hover,
    .btn.is-loading-key:disabled,
    .btn.is-loading-key:focus,
    .btn.is-loading-key:active {
        opacity: 1;
        background: @ink;
        color: @paper;
        cursor: progress;
    }
}

.bq-apikey-logo {
    width: 3rem;
    height: 3rem;
    object-fit: contain;
}

.bq-apikey-help {
    margin: 0;
    color: @muted;
    font-size: 0.83rem;
    line-height: 1.4;
}

.bq-apikey-error {
    margin: 0;
    color: @danger;
    font-size: 0.9rem;
    font-weight: 600;
}

.bq-platform-overlay-options {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;
}

.bq-platform-overlay-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 9rem;

    .bq-apikey-logo {
        width: 1.25rem;
        height: 1.25rem;
    }

}

.bq-input {
    flex: 1;
    min-height: var(--chat-control-height);
    box-sizing: border-box;
    overflow-y: hidden;
    resize: vertical;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    padding: 0.7rem 0.7rem 0.7rem var(--chat-control-height);
    font-family: @mono;
    font-size: 0.83rem;

    &::placeholder { color: @placeholder; }
    &:focus { outline: none; border-color: @ink; }
    &:disabled { opacity: 0.5; cursor: default; }
}

// When the attach clip button is hidden (uploadsFrozenForUser) there is no
// absolutely-positioned button occupying the left gutter, so drop the reserved
// left padding back to match the right side.
.bq-input.bq-input--noattach {
    padding-left: 0.7rem;
}

.btn {
    align-self: flex-end;
    min-width: 5.5rem;
    height: var(--chat-control-height);
}

// Local overrides so the API-key modal in this scoped file matches the one
// in service.vue (where `.bq-modal-textarea` and the modal `.btn` rule are
// scoped to that component and don't bleed into us).
.bq-modal-wrap {
    .bq-modal-textarea {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1px solid @line;
        background: @paper;
        color: @ink;
        font-family: @mono;
        font-size: 0.9rem;
        outline: none;
        margin-bottom: 0.75rem;
        border-radius: 0;
        transition: border-color 0.15s;
        resize: vertical;
        min-height: 4rem;
        word-wrap: break-word;
        overflow-x: hidden;
        white-space: pre-wrap;

        &::placeholder { color: @placeholder; }
        &:focus { border-color: @ink; }
        &:disabled { opacity: 0.5; cursor: default; }
    }

    .btn {
        width: 100%;
        align-self: stretch;
        min-width: 0;
        height: auto;
    }
}

@media (max-width: 700px) {
    .bq-agent {
        height: var(--agent-available-height, calc(100dvh - 4rem));
    }

    .bq-meta {
        height: 100%;
        padding: 1rem 0;
        border: none;
        box-shadow: none;
    }

    .bq-input-row {
        flex-direction: column;
    }

    .bq-input-wrap {
        width: 100%;
        flex: 0 0 auto;
    }

    .bq-attachments {
        flex-basis: auto;
        width: 100%;
    }

    .bq-attachment-warning {
        flex-basis: auto;
        width: 100%;
    }

    .btn {
        width: 100%;
    }

    .bq-bubble {
        max-width: 100%;
    }
}
</style>

<!--
  Shared chat conversation surface (message bubbles, rendered markdown, in-bubble
  file/link anchors, queue/cancel affordances) — authored ONCE in
  bunnyquery/styles/chat.css and consumed by the BunnyQuery widget too.
  This block is intentionally NON-scoped: the rules must reach the v-html `.bq-md`
  markdown (which carries no scope attribute). The .bq-* chat classes exist only
  in this view within the admin app, so global application is safe. The required
  --bq-* design tokens are provided app-wide by src/assets/less/bq-tokens.less.
-->
<style src="bunnyquery/styles/chat.css"></style>
